import { useState, useEffect, useMemo, useRef, useCallback, Fragment } from "react";
import { nomesTipos } from "@/features/tipos-cliente";
import { comPulseira, lerPulseira } from "@/features/cliente-sessao";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useCopaTheme, CopaOverlay } from "../features/copa";
import "../features/copa/copa-theme.css";

const COPA_FLAG_BRASIL_URL = "https://upload.wikimedia.org/wikipedia/commons/thumb/0/05/Flag_of_Brazil.svg/330px-Flag_of_Brazil.svg.png";
import { Search, ShoppingCart, ShoppingBag, Plus, Minus, Trash2, X, XCircle, Clock, Star, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, User, ImageOff, MapPin, Store, Truck, CreditCard, Banknote, QrCode, Check, Home, Building, Package, Send, ChefHat, Timer, Bike, CheckCircle, MessageCircle, ExternalLink, Gift, ClipboardList, UtensilsCrossed, Bell, BellRing, RotateCw, Ticket, Phone, Share2, History, Calendar, Info, Coins, Receipt } from "lucide-react";
import { statusPedidoAntigo, mensagemBloqueio, rotuloBloqueio } from "../features/cardapio-reorder/statusPedidoAntigo";
import SeloStatusPedido from "../features/cardapio-reorder/SeloStatusPedido";
import {
  AvisoCupom, FaixaOfertas, SeloOferta, beneficiosDoProduto, itemAceitaCupom, marcarOferta, mesclarPromocoes, nomesSemCupom,
  ofertaDoProduto, propsCardOferta, unidadesPorChave, useOfertasDoDia, useOfertasExpiradasNoCarrinho, useTelemetriaCardapio,
} from "../features/ofertas-do-dia";
import { tokenDaConversa, useLinkConversa } from "../features/recuperador";
import { CombinaCom, ListaSugestoes, ehSimples, registrarAdicionada, useSugestoes } from "../features/sugestoes";
import { abrirComprovante, useMesaQr } from "../features/cardapio-mesa/useMesaQr";
import { useVoltarParaMesa } from "../features/cardapio-mesa/useOutraMesa";
import MesaQrFluxo from "../features/cardapio-mesa/MesaQrFluxo";
import PagamentoMesa from "../features/cardapio-mesa/PagamentoMesa";
import MotoHelmet from "../components/icons/MotoHelmet";
import { FacebookIcon, InstagramIcon, KwaiIcon, TikTokIcon } from "../components/icons/SocialIcons";
import QuantidadePeso, { vendePorPeso } from "../components/peso/QuantidadePeso";
import { usePizzaPopup } from "../components/pizza/usePizzaPopup";
import { sanitizeCurrencyInput } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import LoginModal from "../components/modals/LoginModal";
import ProfileMenu, { EditAccountModal } from "../components/ProfileMenu";
import PixOnlineModal from "../components/modals/PixOnlineModal";
import CartaoOnlineModal from "../components/modals/CartaoOnlineModal";
// trackFunnel do Clarity = o de utils/ + espelho do funil no Microsoft Clarity
import { trackFunnel, iniciarClarityCardapio, clarityEvento } from "../features/clarity-cardapio";
import { saveCartDraft, loadCartDraft, clearCartDraft, reconcileCartWithProducts } from "../utils/cartPersistence";
import { temPersonalizacao } from "../utils/cartGrouping";
// Import pelo caminho direto (e nao pelo barrel da feature) de proposito: o
// barrel tambem exporta a BannersPage do painel, que carrega o cliente HTTP
// autenticado — nada disso tem o que fazer no cardapio publico.
import CardapioBanner from "../features/cardapio-banners/public/CardapioBanner";
import { CardapioHero } from "../features/cardapio-hero";
import { CarteiraCashback, CupomDaSacola, ItemSacola, OfertasParaDesbloquear, SacolaLateral, descontoDoCupom, useTelaLarga } from "../features/cardapio-sacola";
import { AcompanhamentoPedido } from "../features/acompanhamento-pedido";
import { CardapioCarregando } from "../features/cartoes-carregando";
import { PopupAvaliacao } from "../features/avaliacao-popup";
import { CarrosselMidias, VideoDoCartao, useMidiasPublicas } from "../features/produto-midias";
import { CartaoFidelidade, FaixaPremio, MeuCartaoModal, TiraCartao, premioCabeNaSacola, premioComoCupom, useCartaoFidelidade } from "../features/cartao-fidelidade";

import { capturarOrigemAnuncio, obterOrigemAnuncio } from "@/lib/origemAnuncio";
import { trackPurchase } from "@/utils/trackFunnel";
import { capturarSmsRef, obterSmsRef } from "@/lib/smsRef";
import { resumoEtapasDoItem, subitemsDoItem } from "../utils/etapasDoItem";
import { sinalDaEtapa, valorDaEtapa } from "../utils/precoEtapas";
import { rotuloPagamento } from "../features/pedidos/lib/pagamentoRotulo";
import { formatarQtd, medidaDoItem, medidaDoProduto, qtdDoItem } from "../lib/unidadeVenda";
capturarSmsRef();
// Ids do anúncio da Meta (nc_c/nc_as/nc_ad + fbclid) que vêm junto do ?c= —
// dizem QUAL anúncio trouxe o clique; vão no pedido como origem_anuncio.
capturarOrigemAnuncio();

const API = '/api';

// Quilo/litro (30/09/2026): no "N itens" o produto por peso conta 1 item —
// 1,250 kg de picanha não é "1,25 itens". Por unidade soma a quantidade.
// A medida vem de `products` (/public/products, com sale_unit e is_divisible);
// o /public/products/all é compacto e não traz a unidade.
const contarItens = (items, produtos) => (items || []).reduce(
  (s, it) => s + (medidaDoItem(it, produtos) ? 1 : Number(it.quantidade || it.quantity || 1)), 0);
// Passo e mínimo do − / + do carrinho para produto por quilo/litro (0,1 kg/L)
const PASSO_PESO = 0.1;
// Altura da barra de categorias (py-2 + chip py-1.5 + borda). A sacola lateral gruda
// logo abaixo dela; mudou o estilo dos chips, confira este número.
const ALTURA_BARRA_CATEGORIAS = 47; // medida no navegador em 02/10/2026

// Scroller horizontal com setinhas no desktop — usado no layout de Mercearia
function HorizontalScroller({ children, ariaLabel = 'Lista de produtos', darkMode = false }) {
  const ref = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = ref.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [children]);

  const scroll = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.8), behavior: 'smooth' });
  };

  const arrowBase = `hidden md:flex absolute top-1/2 -translate-y-1/2 w-9 h-9 rounded-full items-center justify-center shadow-lg transition-opacity z-10 ${darkMode ? 'bg-zinc-900/95 text-white border border-zinc-700 hover:bg-zinc-800' : 'bg-white/95 text-gray-900 border border-gray-200 hover:bg-gray-50'}`;

  return (
    <div className="relative group">
      <div
        ref={ref}
        aria-label={ariaLabel}
        className="flex gap-3 overflow-x-auto scroll-smooth snap-x pb-2 -mx-3 px-3"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {children}
      </div>
      {canLeft && (
        <button
          type="button"
          aria-label="Rolar para a esquerda"
          onClick={() => scroll(-1)}
          className={`${arrowBase} left-1 opacity-0 group-hover:opacity-100`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {canRight && (
        <button
          type="button"
          aria-label="Rolar para a direita"
          onClick={() => scroll(1)}
          className={`${arrowBase} right-1 opacity-0 group-hover:opacity-100`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}

// Ícone de Moto para a BAG
const MotoIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.44 9.03L15.41 5H11v2h3.59l2 2H5c-2.8 0-5 2.2-5 5s2.2 5 5 5c2.46 0 4.45-1.69 4.9-4h1.65l2.77-2.77c-.21.54-.32 1.14-.32 1.77 0 2.8 2.2 5 5 5s5-2.2 5-5c0-2.65-1.97-4.77-4.56-4.97zM7.82 15C7.4 16.15 6.28 17 5 17c-1.63 0-3-1.37-3-3s1.37-3 3-3c1.28 0 2.4.85 2.82 2H5v2h2.82zM19 17c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
  </svg>
);

// Acompanhamento do pedido: features/acompanhamento-pedido (repaginado em 23/10/2026)

// Função para obter URL completa de imagem
// Usa /api/uploads para passar pelo proxy do Kubernetes
// Slug derivado do nome do produto pra links de divulgacao (ex: "Addad Burger"
// vira "addad-burger"). Slug eh sempre derivado dinamicamente — se o lojista
// renomear o produto, o slug muda junto e o link antigo deixa de funcionar
// (decisao consciente: simplicidade > permanencia, marketing > paranoia).
const makeProductSlug = (name) => {
  if (!name) return "";
  return String(name)
    .normalize("NFD").replace(/[̀-ͯ]/g, "") // remove acentos
    .toLowerCase()
    .replace(/&/g, "-e-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
};

const buildProductPublicUrl = (productName) => {
  const slug = makeProductSlug(productName);
  if (!slug) return "";
  const host = (typeof window !== 'undefined' && window.location?.host) || "";
  const protocol = (typeof window !== 'undefined' && window.location?.protocol) || "https:";
  return `${protocol}//${host}/#/cardapio/p/${slug}`;
};

// Compartilha o produto via Web Share API (mobile abre folha nativa de
// compartilhamento — WhatsApp/Insta/etc) ou copia o link no clipboard
// Compartilha o cardapio da loja (botao do cabecalho no desktop). Mesma
// escada do shareProduct: navigator.share nativo quando existe, senao copia
// pro clipboard — Firefox e Safari desktop nao implementam o share nativo.
const shareStore = async (nomeLoja) => {
  const url = typeof window !== 'undefined' ? window.location.origin + '/#/cardapio' : '';
  const title = nomeLoja || 'Cardápio';
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text: `Confira o cardápio de ${title}`, url });
      return;
    } catch (e) {
      if (e && e.name === 'AbortError') return;
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link do cardápio copiado!");
  } catch (_) {
    toast.error("Não foi possível copiar o link");
  }
};

// (desktop). Sempre stopPropagation pra nao disparar o openProductPopup.
const shareProduct = async (product, evt) => {
  try { evt?.stopPropagation?.(); evt?.preventDefault?.(); } catch (_) {}
  const url = buildProductPublicUrl(product?.name);
  if (!url) {
    toast.error("Não foi possível gerar o link do produto");
    return;
  }
  const title = product?.name || "Cardápio";
  const text = `Olha esse ${title}!`;
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text, url });
      return;
    } catch (e) {
      // AbortError = usuario cancelou (nao mostra erro). Outros = fallback pra clipboard.
      if (e && e.name === 'AbortError') return;
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  } catch (_) {
    toast.error("Não foi possível copiar — selecione e copie manualmente");
  }
};

const getImageUrl = (url) => {
  if (!url) return null;
  // Se já é uma URL completa (http/https), retorna como está
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Se é uma URL de uploads (/uploads/products/xxx), converte para /api/uploads/products/xxx
  if (url.startsWith('/uploads/')) {
    return `/api${url}`;
  }
  // Outros casos, retorna como está
  return url;
};

// Formatar tempo de etapa: segundos → "X min Y s" ou "X min" ou "Y s"
const formatTempoEtapa = (seg) => {
  if (seg == null || seg === undefined) return "—";
  const m = Math.floor(seg / 60);
  const s = Math.floor(seg % 60);
  if (m === 0) return s === 0 ? "0 s" : `${s} s`;
  if (s === 0) return `${m} min`;
  return `${m} min ${s} s`;
};

// Verifica se a imagem é PNG (para usar fundo transparente do sistema)
const isPngImage = (url) => {
  if (!url) return false;
  return url.toLowerCase().endsWith('.png');
};

// Função helper para fazer requests com fallback
const fetchWithFallback = async (endpoint) => {
  try {
    const response = await axios.get(`${API}${endpoint}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching ${endpoint}:`, error);
    throw error;
  }
};

// Mini-barra de progresso de cupom — slim e chamativa, reusavel em qualquer lugar
// do fluxo de pedido (carrinho, checkout, etc). Mostra quanto falta pro proximo
// cupom desbloquear ou "Liberado!" em verde quando atingir. Mesma logica de
// "best coupon to highlight" da barra fixa do cardapio: cupom selecionado pelo
// cliente vence; senao pega o mais proximo de ser atingido. onClick opcional —
// se passado, vira clicavel pra abrir modal de cupons.
function CouponMiniBar({ visibleCoupons, selectedCouponCode, couponableSubtotal, darkMode, onClick }) {
  if (!visibleCoupons || visibleCoupons.length === 0) return null;
  const selected = selectedCouponCode ? visibleCoupons.find(c => c.code === selectedCouponCode) : null;
  const best = selected || [...visibleCoupons].sort((a, b) => {
    const minA = Number(a.min_order_value || 0);
    const minB = Number(b.min_order_value || 0);
    const faltaA = couponableSubtotal >= minA ? 0 : minA - couponableSubtotal;
    const faltaB = couponableSubtotal >= minB ? 0 : minB - couponableSubtotal;
    if (faltaA === 0 && faltaB > 0) return -1;
    if (faltaB === 0 && faltaA > 0) return 1;
    return faltaA - faltaB;
  })[0];
  if (!best) return null;
  const min = Number(best.min_order_value || 0);
  const reached = min === 0 || couponableSubtotal >= min;
  const progress = min === 0 ? 100 : Math.min(100, (couponableSubtotal / min) * 100);
  const falta = Math.max(0, min - couponableSubtotal);
  const isFreeDelivery = best.discount_type === 'free_delivery';
  const descontoLabel = isFreeDelivery
    ? 'frete grátis'
    : best.discount_type === 'percentage'
      ? `${Number(best.discount_value).toFixed(0)}%`
      : `R$ ${Number(best.discount_value).toFixed(2).replace('.', ',')}`;
  const Tag = onClick ? 'button' : 'div';
  // Background com gradient sutil + sombra interna pra dar profundidade.
  // Reached: verde solido com glow. Pendente: laranja com shimmer chamando atencao.
  const bgClass = reached
    ? 'bg-gradient-to-r from-green-500/20 via-emerald-500/15 to-green-500/20'
    : (darkMode
        ? 'bg-gradient-to-r from-orange-500/15 via-amber-500/12 to-orange-500/15'
        : 'bg-gradient-to-r from-orange-100/80 via-amber-50 to-orange-100/80');
  const borderColor = reached
    ? 'border-green-500/45'
    : 'border-orange-500/40';
  const shadow = reached
    ? 'shadow-[inset_0_-1px_0_rgba(34,197,94,0.4),0_2px_8px_rgba(34,197,94,0.18)]'
    : 'shadow-[inset_0_-1px_0_rgba(249,115,22,0.35),0_2px_8px_rgba(249,115,22,0.15)]';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`relative w-full overflow-hidden border-y-2 ${borderColor} ${bgClass} ${shadow} ${onClick ? 'active:opacity-90 active:scale-[0.99] transition-all' : ''}`}
      aria-label={onClick ? 'Ver cupons disponíveis' : undefined}
    >
      {/* Shimmer animado sweep — chama atencao sem ser irritante (so quando pendente) */}
      {!reached && <span className="pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-coupon-mini-shimmer" />}

      {/* Barra de progresso mais grossa no topo, com cantos arredondados */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${darkMode ? 'bg-zinc-800/70' : 'bg-orange-100'} overflow-hidden`}>
        <div
          className={`h-full transition-all duration-500 ${reached ? 'bg-gradient-to-r from-green-400 to-emerald-500' : 'bg-gradient-to-r from-orange-400 via-amber-400 to-orange-500'}`}
          style={{ width: `${progress}%`, boxShadow: reached ? '0 0 8px rgba(34,197,94,0.6)' : '0 0 6px rgba(249,115,22,0.5)' }}
        />
      </div>

      <div className="px-3 py-2 flex items-center justify-center gap-2 text-center">
        <span className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full ${reached ? 'bg-green-500/20' : 'bg-orange-500/20'} animate-coupon-mini-bounce`}>
          {isFreeDelivery
            ? <MotoHelmet className={`w-3.5 h-3.5 ${reached ? 'text-green-600 dark:text-green-400' : 'text-sky-500'}`} />
            : <Ticket className={`w-3.5 h-3.5 ${reached ? 'text-green-600 dark:text-green-400' : 'text-orange-500'}`} />}
        </span>
        {reached ? (
          <span className={`text-[12px] font-extrabold ${darkMode ? 'text-green-300' : 'text-green-700'} truncate tracking-tight`}>
            🎉 {isFreeDelivery ? 'Frete grátis liberado!' : `${descontoLabel} liberado!`}
          </span>
        ) : (
          <span className={`text-[12px] font-bold ${darkMode ? 'text-zinc-100' : 'text-gray-900'} truncate tracking-tight`}>
            Faltam <strong className="text-orange-600 dark:text-orange-400 text-[13px]">R$ {falta.toFixed(2).replace('.', ',')}</strong> pro <strong className={isFreeDelivery ? 'text-sky-600 dark:text-sky-400' : 'text-orange-600 dark:text-orange-400'}>{isFreeDelivery ? 'frete grátis' : descontoLabel}</strong>
          </span>
        )}
        {visibleCoupons.length > 1 && (
          <span className={`text-[10px] font-bold shrink-0 px-1.5 py-0.5 rounded-full shadow-sm ${reached ? 'bg-green-500 text-white' : 'bg-orange-500 text-white'}`}>
            +{visibleCoupons.length - 1}
          </span>
        )}
      </div>
    </Tag>
  );
}

// Popup de Produto para adicionar ao carrinho
function ProductPopup({ product, open, onClose, onAddToCart, darkMode, allProducts = [], activePromotions = [], editingItem = null, cashbackConfig = { ativo: false, percentual: 0 }, midias = null }) {
  const [imageError, setImageError] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [observation, setObservation] = useState("");
  const [comboStep, setComboStep] = useState(0); // 0 = selecionar tipo, 1+ = etapas, último = resumo
  const [selectedComboType, setSelectedComboType] = useState(null); // 'simples' ou 'combo'
  const [stepSelections, setStepSelections] = useState({}); // { stepIndex: [itemIds] }
  const [itemImageErrors, setItemImageErrors] = useState({}); // { itemId: true }
  const [stepSearch, setStepSearch] = useState(""); // Busca na etapa atual
  // Computador: a tela simples abre centralizada, como a de etapas (02/10/2026)
  const telaLarga = useTelaLarga();

  // Função para inicializar seleções com itens padrão
  const initializeDefaultSelections = (steps, filterComboOnly = false, comboType = null) => {
    const initialSelections = {};

    // Chaveia por índice ABSOLUTO (posição no array completo) — mesma convenção
    // do getRelevantSteps (v1.5.396). Filtrar só decide QUAIS etapas ganham
    // default, nunca a chave.
    (steps || []).forEach((step, absIndex) => {
      if (filterComboOnly && comboType === 'simples' && step.combo_only) return;
      if (step.default_item_id) {
        const itemExists = step.items?.some(item => item.product_id === step.default_item_id);
        if (itemExists) {
          initialSelections[absIndex] = [step.default_item_id];
        }
      }
    });

    return initialSelections;
  };

  // Reset/prefill quando abrir com novo produto. Em modo edit, prefill com as escolhas
  // anteriores do carrinho — cliente entra no MESMO fluxo de criacao (tela inicial: combo
  // selection se combo, ou primeira etapa se nao-combo com etapas) e pode navegar pra
  // frente/voltar livremente sem perder selecoes.
  useEffect(() => {
    if (open && product) {
      setImageError(false);
      setItemImageErrors({});
      setStepSearch("");

      const isComboProduct = product.product_type === 'combo';
      const hasSteps = product.order_steps && product.order_steps.length > 0;

      if (editingItem) {
        setQuantity(editingItem.quantity || 1);
        setObservation(editingItem.observation || "");
        setSelectedComboType(editingItem.combo_type || null);
        setStepSelections(
          editingItem.step_selections && typeof editingItem.step_selections === 'object'
            ? { ...editingItem.step_selections }
            : {}
        );
        // Edit abre direto no RESUMO ("Confirme seu pedido") — cliente ve todas as
        // escolhas pre-preenchidas + botao "Alterar" em cada etapa pra modificar uma
        // sem refazer o fluxo todo. Quantidade e observacao tambem sao editaveis
        // inline ali. Muito mais rapido pra ajustes pontuais.
        if (hasSteps || isComboProduct) {
          // Calcula totalSteps levando em conta o combo_type do editingItem (selected
          // ComboType ainda nao foi commitado neste tick, entao filtra manualmente).
          const editTipo = editingItem.combo_type || null;
          const filteredSteps = (product.order_steps || []).filter(s => {
            if (isComboProduct && editTipo === 'simples') return !s.combo_only;
            return true;
          });
          setComboStep(filteredSteps.length + 1); // summary = totalSteps + 1
        } else {
          setComboStep(0); // produto simples sem etapas: tela basica de qty/observacao
        }
        return;
      }

      setQuantity(1);
      setObservation("");
      setSelectedComboType(null);

      if (!isComboProduct && hasSteps) {
        const initialSelections = initializeDefaultSelections(product.order_steps);
        setStepSelections(initialSelections);
        setComboStep(1);
      } else if (isComboProduct) {
        setStepSelections({});
        setComboStep(0);
      } else {
        setStepSelections({});
        setComboStep(0);
      }
    }
  }, [open, product, editingItem]);

  // Limpar busca quando mudar de etapa
  useEffect(() => {
    setStepSearch("");
  }, [comboStep]);

  // Quando o tipo (combo/simples) for selecionado, inicializar seleções com itens padrão.
  // Em modo edit: se o cliente confirmar o MESMO tipo de antes, preserva as selecoes
  // anteriores (nao reseta) — caso contrario, reset com defaults pra cobrir steps novos
  // que podem ter aparecido ao trocar de simples pra combo (combo_only).
  const handleSelectComboType = (type) => {
    setSelectedComboType(type);

    if (product && product.order_steps && product.order_steps.length > 0) {
      const sameAsEditing = editingItem && editingItem.combo_type === type;
      if (sameAsEditing && editingItem.step_selections && typeof editingItem.step_selections === 'object') {
        setStepSelections({ ...editingItem.step_selections });
      } else {
        const initialSelections = initializeDefaultSelections(
          product.order_steps,
          true, // Filtrar combo_only
          type
        );
        setStepSelections(initialSelections);
      }
    }
  };

  if (!product) return null;

  const isCombo = product.product_type === 'combo';
  const hasOrderSteps = product.order_steps && product.order_steps.length > 0;
  
  // Calcular preço simples como 70% do preço do combo (ou usar campo específico se existir)
  const comboPrice = product.sale_price || 0;
  const simplePrice = product.simple_price || Math.round(comboPrice * 0.7 * 100) / 100;

  // Promoção do produto
  const getPopupPromo = (comboType) => {
    for (const promo of activePromotions) {
      const pp = (promo.products || []).find(p => p.product_id === product.id);
      if (pp) {
        if (comboType === 'combo' && pp.discount_value_combo) {
          return { promo, value: pp.discount_value_combo, type: promo.discount_type };
        }
        // Pizza por tamanho — sem valor único pra exibir aqui; popup pizza mostra preço promo por tamanho.
        if (pp.sizes && typeof pp.sizes === 'object' && pp.discount_value == null) return null;
        return { promo, value: pp.discount_value, type: promo.discount_type };
      }
    }
    return null;
  };
  const calcPopupPromoPrice = (originalPrice, promoInfo) => {
    if (!promoInfo) return null;
    if (promoInfo.type === 'percentage') return originalPrice * (1 - promoInfo.value / 100);
    if (promoInfo.type === 'fixed_price') return promoInfo.value;
    return null;
  };
  const promoSimple = getPopupPromo('simples');
  const promoCombo = getPopupPromo('combo');
  // Para combo: aplica no simplePrice; para não-combo: aplica no sale_price (comboPrice)
  const simplePricePromo = isCombo ? calcPopupPromoPrice(simplePrice, promoSimple) : calcPopupPromoPrice(comboPrice, promoSimple);
  const comboPricePromo = calcPopupPromoPrice(comboPrice, promoCombo);

  // Filtrar etapas baseado no tipo selecionado.
  // v1.5.396 — cada etapa carrega `_absIndex` (posição no array COMPLETO
  // product.order_steps). As seleções são chaveadas por esse índice absoluto,
  // NÃO pela posição na lista filtrada: a lista filtrada muda de tamanho ao
  // alternar Simples ↔ Combo (etapas combo_only somem) e as seleções antigas
  // passavam a apontar pra outra etapa — preço e itens saíam desalinhados
  // (caso real #21039 Point do Addad: combo montado, cobrado preço do simples).
  const getRelevantSteps = () => {
    if (!hasOrderSteps) return [];
    const isComboProduct = product.product_type === 'combo';
    return product.order_steps
      .map((step, absIndex) => ({ ...step, _absIndex: absIndex }))
      .filter(step => {
        // Etapas combo_only SÓ entram quando o tipo COMBO foi escolhido de fato.
        // v1.5.396 — antes o teste era `=== 'simples'`, então com o tipo AINDA
        // NÃO ESCOLHIDO (null) as etapas do combo (Acompanhamento/Bebida)
        // apareciam e iam pro pedido, enquanto calculateTotalPrice caía no
        // fallback do preço SIMPLES: comida de combo cobrada como simples
        // (caso real #21039 Point do Addad — R$ 35,90 + adicionais em vez de
        // R$ 48,90 + adicionais).
        if (isComboProduct && selectedComboType !== 'combo') {
          return !step.combo_only;
        }
        return true;
      });
  };

  const relevantSteps = getRelevantSteps();
  const totalSteps = relevantSteps.length;
  // +1 para a etapa de resumo
  const totalStepsWithSummary = totalSteps + 1;
  const isOnSummaryStep = comboStep > totalSteps && comboStep > 0;

  // Buscar foto do produto pelo ID
  const getProductPhoto = (productId) => {
    const prod = allProducts.find(p => p.id === productId);
    return prod?.photo_url || null;
  };
  
  // Verificar se produto está disponível (available)
  const isProductAvailable = (productId) => {
    const prod = allProducts.find(p => p.id === productId);
    return prod?.available !== false;
  };

  // Produto marcado como 'escondido' em Produtos→Cardápio OU arquivado/deletado
  // (não vem mais em /public/products) → some das etapas também.
  const isProductHidden = (productId) => {
    const prod = allProducts.find(p => p.id === productId);
    return !prod || prod.hidden === true;
  };

  // Buscar preço atual do produto pelo ID (para sincronizar com PRODUTOS)
  const getProductPrice = (productId) => {
    const prod = allProducts.find(p => p.id === productId);
    return prod?.sale_price || 0;
  };
  
  // Buscar descrição do produto pelo ID
  const getProductDescription = (productId) => {
    const prod = allProducts.find(p => p.id === productId);
    return prod?.description || "";
  };
  
  // Obter preço do item (verifica sync_prices da etapa)
  const getItemPrice = (item, step) => {
    // Se a etapa tem sync_prices ativo (ou não definido = default true), usa preço do produto
    if (step?.sync_prices !== false) {
      return getProductPrice(item.product_id);
    }
    // Se sync_prices está desativado, usa price_override
    // Se price_override está definido explicitamente como 0, é grátis
    if (item.price_override === 0) return 0;
    // Se price_override > 0, usa ele
    if (item.price_override > 0) return item.price_override;
    // Fallback: busca o preço atual do produto
    return getProductPrice(item.product_id);
  };

  // Calcular preço total baseado nas seleções das etapas
  const calculateTotalPrice = () => {
    // Para produtos normais (não combo), usa sale_price diretamente
    // Para combos, usa comboPrice ou simplePrice baseado na seleção
    let basePrice;
    if (!isCombo) {
      basePrice = simplePricePromo != null ? simplePricePromo : comboPrice;
    } else {
      if (selectedComboType === 'combo') {
        basePrice = comboPricePromo != null ? comboPricePromo : comboPrice;
      } else {
        basePrice = simplePricePromo != null ? simplePricePromo : simplePrice;
      }
    }
    
    // Adicionar preços das etapas se houver
    if (hasOrderSteps) {
      relevantSteps.forEach((step) => {
        const selectedItems = stepSelections[step._absIndex] || [];
        // Soma, Máximo ou Subtração: regra única em utils/precoEtapas.js
        const precos = selectedItems
          .map(itemId => step.items?.find(i => i.product_id === itemId))
          .filter(Boolean)
          .map(item => getItemPrice(item, step));
        basePrice += valorDaEtapa(step, precos);
      });
    }

    return Math.max(0, basePrice) * quantity;
  };

  const totalPrice = calculateTotalPrice();

  // Verificar se pode avançar para próxima etapa
  const canAdvanceStep = () => {
    if (comboStep === 0) {
      return selectedComboType !== null;
    }
    
    // Se está na etapa de resumo, sempre pode adicionar
    if (isOnSummaryStep) return true;
    
    const currentStepIndex = comboStep - 1;
    const currentStep = relevantSteps[currentStepIndex];
    if (!currentStep) return true;

    const selections = stepSelections[currentStep._absIndex] || [];
    const minSelections = currentStep.min_selections || 0;

    return selections.length >= minSelections;
  };

  // Avançar etapa
  const handleNextStep = () => {
    if (comboStep === 0) {
      if (!hasOrderSteps || relevantSteps.length === 0) {
        // Ir direto para resumo se não tem etapas
        setComboStep(1); // Vai para resumo (que será step 1 quando totalSteps = 0)
      } else {
        setComboStep(1);
      }
    } else if (comboStep <= totalSteps) {
      setComboStep(comboStep + 1); // Avança para próxima etapa ou resumo
    } else {
      handleAdd(); // Está no resumo, adiciona ao carrinho
    }
  };

  // Todos os handlers abaixo recebem `stepIndex` = posição na lista FILTRADA
  // (é o que o render conhece) e convertem pro índice ABSOLUTO antes de tocar
  // em stepSelections — a chave precisa sobreviver à troca Simples ↔ Combo.
  const absKey = (stepIndex) => relevantSteps[stepIndex]?._absIndex;

  // Toggle seleção de item - CORRIGIDO para permitir trocar seleção
  const toggleItemSelection = (stepIndex, itemId) => {
    const step = relevantSteps[stepIndex];
    if (!step) return;
    const key = step._absIndex;

    const currentSelections = stepSelections[key] || [];
    const isSelected = currentSelections.includes(itemId);
    const maxSelections = step.max_selections || 999;

    if (isSelected) {
      // Desselecionar
      setStepSelections({
        ...stepSelections,
        [key]: currentSelections.filter(id => id !== itemId)
      });
    } else {
      // Selecionar
      if (maxSelections === 1) {
        // Se só pode selecionar 1, substitui a seleção anterior
        setStepSelections({
          ...stepSelections,
          [key]: [itemId]
        });
      } else if (currentSelections.length < maxSelections) {
        // Adiciona à seleção
        setStepSelections({
          ...stepSelections,
          [key]: [...currentSelections, itemId]
        });
      }
    }
  };

  // Quantidade de um item específico na seleção da etapa
  const getItemQty = (stepIndex, itemId) => {
    const sel = stepSelections[absKey(stepIndex)] || [];
    return sel.filter(id => id === itemId).length;
  };

  // Incrementar qty de um item (respeita max_selections da etapa e max_qty individual)
  const incItemQty = (stepIndex, itemId) => {
    const step = relevantSteps[stepIndex];
    if (!step) return;
    const key = step._absIndex;
    const currentSelections = stepSelections[key] || [];
    const maxSelections = step.max_selections || 0;
    if (maxSelections > 0 && currentSelections.length >= maxSelections) return;
    // Max individual do item
    const stepItem = step.items?.find(i => i.product_id === itemId);
    const itemMax = stepItem?.max_qty || 0;
    if (itemMax > 0) {
      const currentQty = currentSelections.filter(id => id === itemId).length;
      if (currentQty >= itemMax) return;
    }
    setStepSelections({ ...stepSelections, [key]: [...currentSelections, itemId] });
  };

  // Decrementar qty (remove uma ocorrência)
  const decItemQty = (stepIndex, itemId) => {
    const key = absKey(stepIndex);
    const currentSelections = stepSelections[key] || [];
    const idx = currentSelections.lastIndexOf(itemId);
    if (idx < 0) return;
    setStepSelections({ ...stepSelections, [key]: [...currentSelections.slice(0, idx), ...currentSelections.slice(idx + 1)] });
  };

  // Obter resumo das seleções para a tela de resumo - COM PREÇOS (agrupando duplicatas em qty)
  const getSelectionsSummary = () => {
    const summary = [];
    relevantSteps.forEach((step, index) => {
      const selectedItems = stepSelections[step._absIndex] || [];
      const counts = {};
      const order = [];
      selectedItems.forEach(id => {
        if (counts[id] === undefined) order.push(id);
        counts[id] = (counts[id] || 0) + 1;
      });
      const itemsWithPrices = order.map(itemId => {
        const item = step.items?.find(i => i.product_id === itemId);
        const qty = counts[itemId] || 1;
        const unit = item ? getItemPrice(item, step) : 0;
        return {
          name: item?.product_name || '',
          qty,
          unitPrice: unit,
          price: unit * qty,
        };
      }).filter(i => i.name);

      if (itemsWithPrices.length > 0) {
        summary.push({
          stepName: step.name,
          items: itemsWithPrices,
          stepIndex: index
        });
      }
    });
    return summary;
  };

  const handleAdd = () => {
    // Guard (v1.5.396): produto combo SEM tipo escolhido não vai pro carrinho —
    // é o estado em que preço-base (simples) e etapas selecionadas podem
    // divergir. O fluxo normal já exige a escolha; isto cobre caminhos
    // alternativos (upsell, reabertura de popup, edição de item legado).
    if (isCombo && !selectedComboType) {
      setComboStep(0);
      return;
    }
    onAddToCart({
      ...product,
      quantity,
      observation: observation.trim() || null,
      combo_type: isCombo ? selectedComboType : null,
      step_selections: stepSelections,
      final_price: totalPrice / quantity,
      ...(editingItem ? { _editingCartItemId: editingItem.cartItemId || editingItem.id } : {}),
    });
    onClose();
  };

  const t = {
    bg: darkMode ? 'bg-zinc-900' : 'bg-white',
    bgMuted: darkMode ? 'bg-zinc-800' : 'bg-gray-100',
    bgCard: darkMode ? 'bg-zinc-800/80' : 'bg-white',
    bgInput: darkMode ? 'bg-zinc-700' : 'bg-gray-50',
    text: darkMode ? 'text-zinc-100' : 'text-gray-900',
    textMuted: darkMode ? 'text-zinc-400' : 'text-gray-500',
    border: darkMode ? 'border-zinc-600' : 'border-gray-200',
    // Cores específicas para itens selecionados
    selectedBg: darkMode ? 'bg-orange-500/20' : 'bg-orange-50',
    selectedBorder: 'border-orange-500',
    // Hover states
    hoverBorder: darkMode ? 'hover:border-orange-400' : 'hover:border-orange-300',
  };

  // TELA DE SELEÇÃO COMBO vs SIMPLES (para produtos tipo combo)
  if (isCombo && comboStep === 0) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent hideClose className={`sm:max-w-xl p-0 gap-0 overflow-hidden ${t.bg} border-0 rounded-2xl w-[95vw] sm:w-full`}>
          <DialogHeader className="sr-only">
            <DialogTitle>{product.name}</DialogTitle>
          </DialogHeader>
          {/* Header - Apenas Nome e Descrição */}
          <div className={`p-4 border-b ${t.border}`}>
            {/* Botoes superior direito: Compartilhar + Fechar */}
            <div className="absolute top-3 right-3 flex items-center gap-1.5 z-10">
              <button
                type="button"
                onClick={(e) => shareProduct(product, e)}
                className={`w-8 h-8 rounded-full ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-gray-100 hover:bg-gray-200'} flex items-center justify-center transition-colors`}
                aria-label={`Compartilhar ${product.name}`}
                title="Compartilhar produto"
              >
                <Share2 className={`w-4 h-4 ${t.text}`} strokeWidth={2.2} />
              </button>
              <button
                onClick={onClose}
                className={`w-8 h-8 rounded-full ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-gray-100 hover:bg-gray-200'} flex items-center justify-center transition-colors`}
              >
                <X className={`w-5 h-5 ${t.text}`} />
              </button>
            </div>

            <div className="pr-24">
              <h2 className={`text-xl font-bold ${t.text} leading-tight`}>{product.name}</h2>
              {product.description && (
                <p className={`text-sm ${t.textMuted} mt-1`}>{product.description}</p>
              )}
              <p className={`text-xs ${t.textMuted} mt-3 font-medium`}>Escolha como quer seu pedido:</p>
            </div>
          </div>

          {/* Cards de Opção - Dois Cards */}
          <div className="p-4">
            <div className="flex gap-3 items-center">
              {/* Card SIMPLES - 35% da largura */}
              <button
                onClick={() => handleSelectComboType('simples')}
                className={`w-[35%] flex-shrink-0 rounded-xl border-2 transition-all text-left overflow-hidden flex flex-col ${
                  selectedComboType === 'simples' 
                    ? `${t.selectedBorder} ${t.selectedBg}` 
                    : `${t.border} ${darkMode ? 'bg-zinc-800' : 'bg-white'} ${t.hoverBorder}`
                }`}
              >
                {/* Imagem do Simples */}
                <div className={`w-full aspect-square ${darkMode ? 'bg-gradient-to-b from-zinc-700 to-zinc-800' : 'bg-gradient-to-b from-orange-50 to-orange-100'}`}>
                  {(product.simple_photo_url || product.photo_url) && !imageError ? (
                    <img
                      src={getImageUrl(product.simple_photo_url || product.photo_url)}
                      alt={product.name}
                      className="w-full h-full object-contain p-2"
                    />
                  ) : (
                    <div className={`w-full h-full flex flex-col items-center justify-center gap-1 ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                      <ImageOff className="w-7 h-7" strokeWidth={1.5} />
                      <span className="text-[9px] uppercase tracking-wider font-medium">Sem foto</span>
                    </div>
                  )}
                </div>
                
                {/* Info do Simples */}
                <div className="p-2 text-center">
                  <p className={`font-bold ${t.text} text-xs uppercase tracking-wider`}>{product.simple_label || 'Simples'}</p>
                  {product.simple_description && product.simple_description.trim() && (
                    <p className={`text-[9px] ${t.textMuted} mt-0.5`}>{product.simple_description}</p>
                  )}
                  {simplePricePromo != null && simplePricePromo !== simplePrice ? (
                    <div className="mt-1">
                      <span className={`text-[10px] line-through ${t.textMuted}`}>R$ {simplePrice.toFixed(2).replace('.', ',')}</span>
                      <p className="text-base font-black text-green-500">R$ {simplePricePromo.toFixed(2).replace('.', ',')}</p>
                    </div>
                  ) : (
                    <p className="text-base font-black text-orange-500 mt-1">R$ {simplePrice.toFixed(2).replace('.', ',')}</p>
                  )}
                </div>
              </button>

              {/* Card COMBO - 65% da largura */}
              <button
                onClick={() => handleSelectComboType('combo')}
                className={`w-[65%] flex-shrink-0 rounded-2xl border-2 transition-all text-left overflow-hidden flex flex-col relative ${
                  selectedComboType === 'combo' 
                    ? `${t.selectedBorder} ${t.selectedBg}` 
                    : `${t.border} ${darkMode ? 'bg-zinc-800' : 'bg-white'} ${t.hoverBorder}`
                }`}
              >
                {/* Badge Recomendado - Melhorado */}
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold py-1.5 text-center z-10 flex items-center justify-center gap-1.5 shadow-md">
                  <span className="text-yellow-300">★</span>
                  RECOMENDADO
                  <span className="text-yellow-300">★</span>
                </div>
                
                {/* Imagem do Combo */}
                <div className={`w-full aspect-square pt-8 ${darkMode ? 'bg-gradient-to-b from-zinc-700 to-zinc-800' : 'bg-gradient-to-b from-orange-100 to-orange-200'}`}>
                  {(product.combo_photo_url || product.photo_url) && !imageError ? (
                    <img
                      src={getImageUrl(product.combo_photo_url || product.photo_url)}
                      alt={product.name}
                      className="w-full h-full object-contain p-2"
                    />
                  ) : (
                    <div className={`w-full h-full flex flex-col items-center justify-center gap-1 ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                      <ImageOff className="w-10 h-10" strokeWidth={1.5} />
                      <span className="text-[10px] uppercase tracking-wider font-medium">Sem foto</span>
                    </div>
                  )}
                </div>
                
                {/* Info do Combo */}
                <div className="p-3">
                  <p className={`font-bold ${t.text} text-sm uppercase tracking-wider`}>{product.combo_label || 'Combo'}</p>
                  {product.combo_description && product.combo_description.trim() && (
                    <p className={`text-[10px] ${t.textMuted} mt-0.5 line-clamp-1`}>
                      {product.combo_description}
                    </p>
                  )}
                  {comboPricePromo != null && comboPricePromo !== comboPrice ? (
                    <div className="mt-2">
                      <span className={`text-xs line-through ${t.textMuted}`}>R$ {comboPrice.toFixed(2).replace('.', ',')}</span>
                      <p className="text-xl font-black text-green-500">R$ {comboPricePromo.toFixed(2).replace('.', ',')}</p>
                    </div>
                  ) : (
                    <p className="text-xl font-black text-orange-500 mt-2">R$ {comboPrice.toFixed(2).replace('.', ',')}</p>
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className={`p-4 ${t.bgMuted} border-t ${t.border}`}>
            <button
              onClick={handleNextStep}
              disabled={!canAdvanceStep()}
              className={`w-full h-12 rounded-full font-bold text-white flex items-center justify-center gap-2 transition-all ${
                canAdvanceStep() 
                  ? 'bg-orange-500 hover:bg-orange-600' 
                  : `${darkMode ? 'bg-zinc-600' : 'bg-gray-300'} cursor-not-allowed`
              }`}
            >
              {/* Se tem etapas relevantes para o tipo selecionado, mostra "PRÓXIMA ETAPA" */}
              {selectedComboType && hasOrderSteps && product.order_steps.filter(step => {
                if (selectedComboType === 'simples') return !step.combo_only;
                return true;
              }).length > 0 ? (
                <>PRÓXIMA ETAPA <ChevronRight className="w-5 h-5" /></>
              ) : (
                <>Adicionar • R$ {(selectedComboType === 'combo' ? (comboPricePromo ?? comboPrice) : (simplePricePromo ?? simplePrice)).toFixed(2).replace('.', ',')}</>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // TELA DE RESUMO (última etapa) - Para combo e produtos com etapas
  const shouldShowSummary = (isCombo || (!isCombo && hasOrderSteps)) && isOnSummaryStep;
  if (shouldShowSummary) {
    const selectionsSummary = getSelectionsSummary();
    
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent hideClose className={`sm:max-w-md p-0 gap-0 overflow-hidden ${t.bg} border-0 rounded-2xl w-[92vw] sm:w-full max-h-[90vh] flex flex-col`}>
          <DialogHeader className="sr-only">
            <DialogTitle>Confirme seu pedido</DialogTitle>
          </DialogHeader>
          {/* Header */}
          <div className={`p-4 border-b ${t.border}`}>
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={() => setComboStep(totalSteps > 0 ? totalSteps : 0)}
                className={`w-8 h-8 rounded-full ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-gray-100 hover:bg-gray-200'} flex items-center justify-center transition-colors`}
              >
                <ChevronLeft className={`w-5 h-5 ${t.text}`} />
              </button>
              <span className={`text-sm ${t.textMuted}`}>Resumo do Pedido</span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={(e) => shareProduct(product, e)}
                  className={`w-8 h-8 rounded-full ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-gray-100 hover:bg-gray-200'} flex items-center justify-center transition-colors`}
                  aria-label={`Compartilhar ${product.name}`}
                  title="Compartilhar produto"
                >
                  <Share2 className={`w-4 h-4 ${t.text}`} strokeWidth={2.2} />
                </button>
                <button
                  onClick={onClose}
                  className={`w-8 h-8 rounded-full ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-gray-100 hover:bg-gray-200'} flex items-center justify-center transition-colors`}
                >
                  <X className={`w-5 h-5 ${t.text}`} />
                </button>
              </div>
            </div>
            <h2 className={`text-lg font-bold ${t.text} text-center`}>Confirme seu pedido</h2>
          </div>

          {/* Conteúdo do Resumo */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Foto e Info do Produto Principal - SEM PREÇO */}
            <div className="flex gap-4 items-center">
              <div className={`w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 ${darkMode ? 'bg-zinc-700' : 'bg-orange-50'}`}>
                {(selectedComboType === 'combo' ? (product.combo_photo_url || product.photo_url) : (product.simple_photo_url || product.photo_url)) && !imageError ? (
                  <img
                    src={getImageUrl(selectedComboType === 'combo' ? (product.combo_photo_url || product.photo_url) : (product.simple_photo_url || product.photo_url))}
                    alt={product.name}
                    className="w-full h-full object-contain"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className={`w-full h-full flex items-center justify-center ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                    <ImageOff className="w-8 h-8" strokeWidth={1.5} />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h3 className={`font-bold text-lg ${t.text}`}>{product.name}</h3>
                {product.description && (
                  <p className={`text-xs ${t.textMuted} mt-1 line-clamp-2`}>{product.description}</p>
                )}
              </div>
            </div>

            {/* Resumo das Escolhas — estilo pill com foto (identico ao PDV admin) */}
            <div className={`${darkMode ? 'bg-zinc-800' : 'bg-gray-100'} rounded-xl p-3 space-y-2.5 border ${t.border}`}>
              <p className={`text-[10px] uppercase tracking-wider font-semibold ${t.textMuted}`}>Suas escolhas</p>

              {/* Etapas com itens selecionados */}
              {selectionsSummary.map((selection, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <p className={`text-[10px] font-bold uppercase tracking-wider ${t.textMuted}`}>{selection.stepName}</p>
                    <button
                      onClick={() => setComboStep(selection.stepIndex + 1)}
                      className="text-[10px] font-semibold text-orange-500 hover:text-orange-600"
                    >
                      Alterar
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {selection.items.map((item, itemIdx) => {
                      const stepRef = relevantSteps[selection.stepIndex];
                      const itemRef = stepRef?.items?.find(i => i.product_name === item.name);
                      const photo = itemRef ? getProductPhoto(itemRef.product_id) : null;
                      return (
                        <div key={itemIdx} className={`flex items-center gap-1.5 ${darkMode ? 'bg-zinc-900' : 'bg-white'} border ${t.border} rounded-full pr-2 pl-0.5 py-0.5`}>
                          <div className={`w-7 h-7 rounded-full overflow-hidden shrink-0 ring-1 ${darkMode ? 'ring-zinc-700' : 'ring-gray-200'} ${darkMode ? 'bg-zinc-800' : 'bg-gray-100'}`}>
                            {photo ? (
                              <img src={getImageUrl(photo)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className={`w-full h-full flex items-center justify-center ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}><ImageOff className="w-3 h-3" strokeWidth={1.5} /></div>
                            )}
                          </div>
                          <span className={`text-[11px] font-medium ${t.text}`}>
                            {(item.qty && item.qty > 1) ? `${item.qty}× ` : ''}{item.name}
                          </span>
                          {item.price > 0 && (
                            <span className="text-[10px] font-bold text-orange-500">{sinalDaEtapa(stepRef)}R$ {item.price.toFixed(2).replace('.', ',')}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Campo de Observação */}
            <div>
              <label className={`text-sm font-medium mb-2 block ${t.text}`}>
                Observação (opcional)
              </label>
              <textarea
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                placeholder="Ex: sem cebola, molho à parte, ponto da carne..."
                className={`w-full resize-none rounded-xl p-3 text-sm ${darkMode ? 'bg-zinc-700' : 'bg-white'} ${t.text} border ${t.border} focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none`}
                maxLength={200}
                rows={3}
              />
              <p className={`text-xs ${t.textMuted} mt-1 text-right`}>{observation.length}/200</p>
            </div>

            {/* Quantidade - CENTRALIZADO E MAIS INTUITIVO */}
            <div className={`${darkMode ? 'bg-zinc-800' : 'bg-gray-100'} rounded-xl p-4`}>
              <p className={`text-sm font-medium ${t.text} text-center mb-3`}>
                Quantidade
                {/* "(em kg)" só no produto vendido por peso: "Guaraná 2 L" inteiro é por unidade */}
                {medidaDoProduto(product) && (
                  <span className={`text-xs ${t.textMuted} ml-1`}>(em {medidaDoProduto(product)})</span>
                )}
              </p>
              {vendePorPeso(product) ? (
                // KG/L: o mesmo controle do Balcão, do Delivery e da tela simples
                <QuantidadePeso product={product} quantity={quantity} setQuantity={setQuantity} variante={darkMode ? 'escuro' : 'claro'} />
              ) : product.is_divisible ? (
                <div className="flex items-center justify-center gap-3">
                  <button
                    className={`w-10 h-10 rounded-full ${darkMode ? 'bg-orange-900/30 hover:bg-orange-900/50' : 'bg-orange-100 hover:bg-orange-200'} flex items-center justify-center transition-all active:scale-95`}
                    onClick={() => setQuantity(Math.max(0.001, parseFloat((quantity - 0.5).toFixed(3))))}
                  >
                    <Minus className={`w-5 h-5 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                  </button>
                  <input
                    type="number"
                    step="0.001"
                    min="0.001"
                    value={quantity}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v) && v > 0) setQuantity(v);
                      else if (e.target.value === "") setQuantity(0.001);
                    }}
                    className={`w-24 text-center text-2xl font-bold rounded-lg px-2 py-1 ${darkMode ? 'bg-zinc-700' : 'bg-white'} ${t.text} border ${t.border} focus:ring-2 focus:ring-orange-500 outline-none`}
                  />
                  <button
                    className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center hover:bg-orange-600 transition-all active:scale-95 shadow-lg shadow-orange-500/30"
                    onClick={() => setQuantity(parseFloat((quantity + 0.5).toFixed(3)))}
                  >
                    <Plus className="w-5 h-5 text-white" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-4">
                  <button
                    className={`w-12 h-12 rounded-full ${darkMode ? 'bg-orange-900/30 hover:bg-orange-900/50' : 'bg-orange-100 hover:bg-orange-200'} flex items-center justify-center transition-all active:scale-95 ${quantity <= 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    <Minus className={`w-5 h-5 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                  </button>
                  <span className={`text-3xl font-bold ${t.text} w-16 text-center`}>{quantity}</span>
                  <button
                    className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center hover:bg-orange-600 transition-all active:scale-95 shadow-lg shadow-orange-500/30"
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    <Plus className="w-5 h-5 text-white" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer - Botão Adicionar / Salvar alterações */}
          <div className={`p-4 ${t.bgMuted} border-t ${t.border}`}>
            <button
              onClick={handleAdd}
              className="w-full h-14 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg flex items-center justify-center gap-2 transition-all shadow-lg shadow-orange-500/20 active:scale-[0.98]"
            >
              {editingItem ? <Check className="w-5 h-5" /> : <ShoppingCart className="w-5 h-5" />}
              {editingItem ? 'Salvar alterações' : 'Adicionar'} • R$ {totalPrice.toFixed(2).replace('.', ',')}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // TELA DE ETAPAS (order_steps) - Para COMBO e SIMPLES - COM FOTOS
  // Também para produtos não-combo que têm order_steps
  const shouldShowSteps = (isCombo && comboStep > 0 && comboStep <= totalSteps) || 
                          (!isCombo && hasOrderSteps && comboStep > 0 && comboStep <= totalSteps);
  
  if (shouldShowSteps && relevantSteps.length > 0) {
    const currentStepIndex = comboStep - 1;
    const currentStep = relevantSteps[currentStepIndex];
    const selections = stepSelections[currentStep?._absIndex] || [];

    // Se não existe essa etapa, vai para resumo
    if (!currentStep) {
      setComboStep(totalSteps + 1);
      return null;
    }
    
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent hideClose className={`sm:max-w-md p-0 gap-0 overflow-hidden ${t.bg} border-0 rounded-2xl w-[92vw] sm:w-full max-h-[90vh] flex flex-col`}>
          <DialogHeader className="sr-only">
            <DialogTitle>{currentStep.name || `Etapa ${comboStep}`}</DialogTitle>
          </DialogHeader>
          {/* Header */}
          <div className={`p-4 border-b ${t.border}`}>
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={() => setComboStep(comboStep - 1)}
                className={`w-8 h-8 rounded-full ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-gray-100 hover:bg-gray-200'} flex items-center justify-center transition-colors`}
              >
                <ChevronLeft className={`w-5 h-5 ${t.text}`} />
              </button>
              <span className={`text-sm ${t.textMuted}`}>Etapa {comboStep} de {totalStepsWithSummary}</span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={(e) => shareProduct(product, e)}
                  className={`w-8 h-8 rounded-full ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-gray-100 hover:bg-gray-200'} flex items-center justify-center transition-colors`}
                  aria-label={`Compartilhar ${product.name}`}
                  title="Compartilhar produto"
                >
                  <Share2 className={`w-4 h-4 ${t.text}`} strokeWidth={2.2} />
                </button>
                <button
                  onClick={onClose}
                  className={`w-8 h-8 rounded-full ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-gray-100 hover:bg-gray-200'} flex items-center justify-center transition-colors`}
                >
                  <X className={`w-5 h-5 ${t.text}`} />
                </button>
              </div>
            </div>
            <h2 className={`text-lg font-bold ${t.text} text-center`}>{currentStep.name || `Etapa ${comboStep}`}</h2>
            {(currentStep.min_selections > 0 || currentStep.max_selections > 0) && (
              <p className={`text-xs ${t.textMuted} text-center mt-1`}>
                {currentStep.min_selections > 0 && currentStep.max_selections > 0 && currentStep.min_selections === currentStep.max_selections 
                  ? `Selecione ${currentStep.min_selections} ${currentStep.min_selections === 1 ? 'opção' : 'opções'}`
                  : currentStep.min_selections > 0 && currentStep.max_selections > 0
                    ? `Selecione de ${currentStep.min_selections} a ${currentStep.max_selections} opções`
                    : currentStep.min_selections > 0 
                      ? `Selecione no mínimo ${currentStep.min_selections} ${currentStep.min_selections === 1 ? 'opção' : 'opções'}`
                      : currentStep.max_selections > 0 
                        ? `Selecione até ${currentStep.max_selections} ${currentStep.max_selections === 1 ? 'opção' : 'opções'}`
                        : 'Selecione suas opções'
                }
              </p>
            )}
            {/* Busca quando houver mais de 10 opções */}
            {(currentStep.items?.length || 0) > 10 && (
              <div className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-gray-100'}`}>
                <Search className={`w-4 h-4 ${t.textMuted}`} />
                <input
                  type="text"
                  value={stepSearch}
                  onChange={(e) => setStepSearch(e.target.value)}
                  placeholder="Buscar opção..."
                  className={`flex-1 bg-transparent border-none outline-none text-sm ${t.text}`}
                />
                {stepSearch && (
                  <button onClick={() => setStepSearch("")} className={`p-1 rounded-full ${darkMode ? 'hover:bg-zinc-600' : 'hover:bg-gray-200'}`}>
                    <X className={`w-3 h-3 ${t.textMuted}`} />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Itens da Etapa - GRID COM FOTOS */}
          <div className="p-3 flex-1 overflow-y-auto">
            {(() => {
              const allItems = (currentStep.items || []).filter(item => (!item.visible_modules || item.visible_modules.includes("Cardapio")) && !isProductHidden(item.product_id));
              const filteredItems = stepSearch.trim()
                ? allItems.filter(item => (item.product_name || "").toLowerCase().includes(stepSearch.toLowerCase()))
                : allItems;

              if (filteredItems.length === 0) {
                return <p className={`text-center ${t.textMuted} py-8`}>Nenhuma opção encontrada</p>;
              }

              // Ordena: itens disponiveis primeiro, esgotados pro final.
              // Mesma regra do listing principal do cardapio.
              const sortedItems = [...filteredItems].sort((a, b) => {
                const aAvail = isProductAvailable(a.product_id) ? 0 : 1;
                const bAvail = isProductAvailable(b.product_id) ? 0 : 1;
                return aAvail - bAvail;
              });

              return (
            <div className="grid grid-cols-3 gap-2">
              {sortedItems.map((item) => {
                const itemQty = getItemQty(currentStepIndex, item.product_id);
                const isSelected = itemQty > 0;
                const itemPhoto = getProductPhoto(item.product_id);
                const hasImageError = itemImageErrors[item.product_id];
                const itemAvailable = isProductAvailable(item.product_id);
                const allowQty = !!item.allow_quantity && (currentStep.max_selections === 0 || (currentStep.max_selections || 0) > 1);
                const totalSel = (selections || []).length;
                const maxReached = (currentStep.max_selections || 0) > 0 && totalSel >= (currentStep.max_selections || 0);

                const itemMaxQty = item.max_qty || 0;
                const itemMaxReached = itemMaxQty > 0 && itemQty >= itemMaxQty;

                return (
                  <div
                    key={item.product_id}
                    onClick={allowQty ? (e) => {
                      if (!itemAvailable) return;
                      // Se pode incrementar, adiciona 1; senao abre controle
                      if (!maxReached && !itemMaxReached) incItemQty(currentStepIndex, item.product_id);
                    } : () => itemAvailable && toggleItemSelection(currentStepIndex, item.product_id)}
                    role="button"
                    className={`relative p-1.5 rounded-lg border-2 transition-all text-left flex flex-col cursor-pointer ${
                      !itemAvailable
                        ? `${darkMode ? 'border-zinc-600 bg-zinc-800' : 'border-gray-300 bg-gray-100'} opacity-60 cursor-not-allowed`
                        : isSelected
                          ? `${t.selectedBorder} ${t.selectedBg}`
                          : `${t.border} ${t.bgCard} ${t.hoverBorder}`
                    }`}
                  >
                    {/* Badge de seleção (qty quando aplicavel) */}
                    {isSelected && itemAvailable && (
                      <div className="absolute top-1 right-1 min-w-5 h-5 px-1 rounded-full bg-orange-500 flex items-center justify-center z-10 shadow-md">
                        {allowQty
                          ? <span className="text-[10px] font-bold text-white">{itemQty}x</span>
                          : <Check className="w-3 h-3 text-white" />}
                      </div>
                    )}
                    
                    {/* Foto do Item */}
                    <div className={`w-full aspect-square rounded-md overflow-hidden mb-1.5 relative ${isSelected ? 'ring-2 ring-orange-500' : ''} ${isPngImage(itemPhoto) ? (darkMode ? 'bg-zinc-700' : 'bg-gray-100') : (darkMode ? 'bg-zinc-800' : 'bg-gray-50')} ${!itemAvailable ? 'grayscale' : ''}`}>
                      {itemPhoto && !hasImageError ? (
                        <img
                          src={getImageUrl(itemPhoto)}
                          alt={item.product_name}
                          className={`w-full h-full ${isPngImage(itemPhoto) ? 'object-contain p-1' : 'object-cover'}`}
                          onError={() => setItemImageErrors(prev => ({ ...prev, [item.product_id]: true }))}
                        />
                      ) : (
                        <div className={`w-full h-full flex items-center justify-center ${isSelected ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-400 dark:text-orange-500' : darkMode ? 'bg-zinc-700 text-zinc-500' : 'bg-gray-100 text-zinc-400'}`}>
                          <ImageOff className="w-5 h-5" strokeWidth={1.5} />
                        </div>
                      )}
                      {!itemAvailable && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <span className="text-white text-[10px] font-bold bg-red-500 px-1.5 py-0.5 rounded">INDISPONÍVEL</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Info do Item */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-xs ${t.text} line-clamp-2 leading-tight ${!itemAvailable ? 'line-through opacity-60' : ''}`} title={item.product_name}>{item.product_name}</p>
                      {(() => {
                        const itemDescription = getProductDescription(item.product_id);
                        return itemDescription ? (
                          <p className={`text-[10px] ${t.textMuted} line-clamp-2 mt-0.5`}>{itemDescription}</p>
                        ) : null;
                      })()}
                      {(() => {
                        const itemPrice = getItemPrice(item, currentStep);
                        if (!itemAvailable) {
                          return <p className="text-[10px] text-red-500 font-bold mt-0.5">ESGOTADO</p>;
                        }
                        return itemPrice > 0 ? (
                          <p className="text-xs text-orange-500 font-bold mt-0.5">
                            {sinalDaEtapa(currentStep)}R$ {itemPrice.toFixed(2).replace('.', ',')}
                          </p>
                        ) : (
                          <p className="text-[10px] text-green-500 font-bold mt-0.5 uppercase">
                            Grátis
                          </p>
                        );
                      })()}
                    </div>
                    {/* Controle de quantidade para itens com allow_quantity */}
                    {allowQty && itemAvailable && (
                      <div
                        className="flex items-center justify-between gap-1 mt-1.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); decItemQty(currentStepIndex, item.product_id); }}
                          disabled={itemQty === 0}
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-base font-bold ${
                            itemQty === 0
                              ? `${darkMode ? 'bg-zinc-700 text-zinc-500' : 'bg-gray-100 text-gray-300'} cursor-not-allowed`
                              : 'bg-orange-500 text-white hover:bg-orange-600'
                          }`}
                        >−</button>
                        <span className={`text-sm font-bold ${t.text}`}>{itemQty}{itemMaxQty > 0 ? `/${itemMaxQty}` : ''}</span>
                        <button
                          type="button"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); incItemQty(currentStepIndex, item.product_id); }}
                          disabled={maxReached || itemMaxReached}
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-base font-bold ${
                            (maxReached || itemMaxReached)
                              ? `${darkMode ? 'bg-zinc-700 text-zinc-500' : 'bg-gray-100 text-gray-300'} cursor-not-allowed`
                              : 'bg-orange-500 text-white hover:bg-orange-600'
                          }`}
                        >+</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
              );
            })()}
          </div>

          {/* Footer */}
          <div className={`p-4 ${t.bgMuted} border-t ${t.border}`}>
            <button
              onClick={handleNextStep}
              disabled={!canAdvanceStep()}
              className={`w-full h-12 rounded-full font-bold text-white flex items-center justify-center gap-2 transition-all ${
                canAdvanceStep() 
                  ? 'bg-orange-500 hover:bg-orange-600 shadow-lg shadow-orange-500/20' 
                  : `${darkMode ? 'bg-zinc-600' : 'bg-gray-300'} cursor-not-allowed`
              }`}
            >
              {comboStep < totalSteps ? (
                <>PRÓXIMA ETAPA <ChevronRight className="w-5 h-5" /></>
              ) : (
                <>REVISAR PEDIDO <ChevronRight className="w-5 h-5" /></>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // TELA PADRÃO (produtos normais ou última etapa)
  // No computador abre centralizada, igual à tela de etapas (pedido do dono,
  // 02/10/2026: "o popup de um é centralizado, o outro fica colado embaixo").
  // No celular continua a gaveta que sobe de baixo.
  const pesoNoCorpo = vendePorPeso(product);
  // Foto: no PC até 40% da tela; em produto de quilo, menor (30% no PC, 34% no
  // celular) para os botões de peso caberem sem rolar o popup.
  const alturaFotoPadrao = pesoNoCorpo
    ? `${telaLarga ? 'max-h-[30vh]' : 'max-h-[34vh]'} flex-shrink-0`
    : (telaLarga ? 'max-h-[40vh] flex-shrink-0' : '');
  const conteudoPadrao = (
    <>
        {/* Imagem Quadrada no Topo */}
        <div className={`relative w-full aspect-square ${alturaFotoPadrao} ${darkMode ? 'bg-gradient-to-b from-zinc-800 to-zinc-900' : 'bg-gradient-to-b from-orange-100 to-orange-50'}`}>
          {midias?.length > 0 ? (
            <CarrosselMidias midias={midias} alt={product.name} escuro={darkMode} />
          ) : product.photo_url && !imageError ? (
            <img
              src={getImageUrl(product.photo_url)}
              alt={product.name}
              className="w-full h-full object-contain"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className={`w-full h-full flex flex-col items-center justify-center gap-2 ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
              <ImageOff className="w-14 h-14" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-wider font-medium">Sem foto</span>
            </div>
          )}
          {/* Botoes superior direito: Compartilhar + Fechar (sobre a imagem) */}
          <div className="absolute top-3 right-3 flex items-center gap-1.5">
            <button
              type="button"
              onClick={(e) => shareProduct(product, e)}
              className="w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-colors"
              aria-label={`Compartilhar ${product.name}`}
              title="Compartilhar produto"
            >
              <Share2 className="w-4 h-4 text-white" strokeWidth={2.2} />
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="p-4 sm:p-5 overflow-y-auto flex-1">
          {/* Nome e Preço */}
          <div className="mb-4">
            <h2 className={`text-xl font-bold ${t.text} mb-1`}>{product.name}</h2>
            {product.description && (
              <p className={`${t.textMuted} text-sm leading-relaxed`}>{product.description}</p>
            )}
          </div>
          
          {/* Preço Destacado */}
          {product.product_type === 'combo' ? (
            <div className="flex justify-center items-start gap-8 mb-5">
              <div className="flex flex-col items-center">
                <span className={`text-xs font-medium ${t.textMuted} uppercase tracking-wide mb-1`}>Individual</span>
                {simplePricePromo != null && simplePricePromo !== simplePrice ? (
                  <>
                    <span className={`text-sm line-through ${t.textMuted}`}>R$ {simplePrice.toFixed(2).replace('.', ',')}</span>
                    <span className="text-2xl font-bold text-green-500">R$ {simplePricePromo.toFixed(2).replace('.', ',')}</span>
                  </>
                ) : (
                  <span className="text-2xl font-bold text-orange-500">R$ {simplePrice.toFixed(2).replace('.', ',')}</span>
                )}
              </div>
              <div className="flex flex-col items-center">
                <span className={`text-xs font-medium ${t.textMuted} uppercase tracking-wide mb-1`}>Combo</span>
                {comboPricePromo != null && comboPricePromo !== comboPrice ? (
                  <>
                    <span className={`text-sm line-through ${t.textMuted}`}>R$ {comboPrice.toFixed(2).replace('.', ',')}</span>
                    <span className="text-2xl font-bold text-green-500">R$ {comboPricePromo.toFixed(2).replace('.', ',')}</span>
                  </>
                ) : (
                  <span className="text-2xl font-bold text-orange-500">R$ {comboPrice.toFixed(2).replace('.', ',')}</span>
                )}
              </div>
            </div>
          ) : (
            <div className="mb-5">
              {(() => {
                // "de/por" decorativo v1.5.282+: aparece se product.original_price > sale_price
                // e não há promoção global (simplePricePromo == null || == comboPrice).
                const decoOrig = (simplePricePromo == null || simplePricePromo === comboPrice)
                  && Number(product?.original_price || 0) > comboPrice && comboPrice > 0
                  ? Number(product.original_price) : null;
                if (simplePricePromo != null && simplePricePromo !== comboPrice) {
                  return (
                    <div className="flex items-center justify-center gap-2">
                      <span className={`text-lg line-through ${t.textMuted}`}>R$ {comboPrice.toFixed(2).replace('.', ',')}</span>
                      <span className="text-2xl font-bold text-green-500">R$ {simplePricePromo.toFixed(2).replace('.', ',')}</span>
                    </div>
                  );
                }
                if (decoOrig != null) {
                  return (
                    <div className="flex items-center justify-center gap-2">
                      <span className={`text-lg line-through ${t.textMuted}`}>R$ {decoOrig.toFixed(2).replace('.', ',')}</span>
                      <span className="text-2xl font-bold text-orange-500">R$ {comboPrice.toFixed(2).replace('.', ',')}</span>
                    </div>
                  );
                }
                return (
                  <div className="text-2xl font-bold text-orange-500">
                    R$ {comboPrice.toFixed(2).replace('.', ',')}
                  </div>
                );
              })()}
              {/* Badge de cashback no popup do produto — v1.5.304+. Some quando o produto
                  não gera cashback (promoção, "de/por" ou oferta que não acumula). */}
              {cashbackConfig.ativo && cashbackConfig.percentual > 0 && beneficiosDoProduto(product, activePromotions).ganhaCashback && (
                <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                  <Coins className="w-3.5 h-3.5" />
                  {Number(cashbackConfig.percentual).toFixed(0)}% de cashback nesta compra
                </div>
              )}
            </div>
          )}
          {/* Oferta/promoção que não aceita cupom: avisa antes de o cliente chegar no checkout
              (vale pra produto simples e combo). */}
          {!beneficiosDoProduto(product, activePromotions).aceitaCupom && (
            <div className="-mt-3 mb-4">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold ${darkMode ? 'bg-zinc-700 text-zinc-200' : 'bg-gray-200 text-gray-700'}`} data-e2e="popup-sem-cupom">
                <Ticket className="w-3.5 h-3.5" />
                Não vale com cupom de desconto
              </span>
            </div>
          )}

          {/* Quilo/litro: os mesmos botões da tela de etapas, do Balcão e do Delivery.
              Antes o produto SEM etapa só tinha o − / + de 100 g no rodapé (caso
              "Bovino - Rocambole" × "Bovino - Acém", Chiquinho, 02/10/2026). */}
          {pesoNoCorpo && (
            <div className={`mb-4 ${darkMode ? 'bg-zinc-800' : 'bg-gray-100'} rounded-xl p-4`} data-e2e="popup-peso">
              <p className={`text-sm font-medium ${t.text} text-center mb-3`}>
                Quantidade <span className={`text-xs ${t.textMuted} ml-1`}>(em {medidaDoProduto(product)})</span>
              </p>
              <QuantidadePeso product={product} quantity={quantity} setQuantity={setQuantity} variante={darkMode ? 'escuro' : 'claro'} />
            </div>
          )}

          {/* Campo de Observação - Compacto */}
          <div>
            <label className={`text-sm font-medium mb-1.5 block ${t.textMuted}`}>Observação (opcional)</label>
            <textarea
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              placeholder="Ex: sem cebola, molho à parte..."
              className={`w-full resize-none rounded-xl p-3 text-sm ${darkMode ? 'bg-zinc-800' : 'bg-gray-100'} ${t.text} border-0 focus:ring-2 focus:ring-orange-500 outline-none`}
              maxLength={200}
              rows={2}
            />
          </div>
        </div>

        {/* Footer Fixo - Clean */}
        <div className={`p-4 ${t.bgMuted} flex items-center gap-3`}>
          {/* Controle de Quantidade - Compacto */}
          {pesoNoCorpo ? null : product.is_divisible ? (
            // Divisível por unidade (ex.: meia porção): passo de 0,5. Quilo/litro usa o controle do corpo.
            <div className={`flex items-center gap-1 ${t.bg} rounded-full border ${t.border} px-2 py-1`}>
              <button
                className={`w-7 h-7 flex items-center justify-center ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-orange-50'} rounded-full transition-colors`}
                onClick={() => setQuantity(Math.max(0.001, parseFloat((quantity - 0.5).toFixed(3))))}
              >
                <Minus className={`w-3.5 h-3.5 ${t.text}`} />
              </button>
              <input
                type="number"
                step="0.001"
                min="0.001"
                value={quantity}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v) && v > 0) setQuantity(v);
                }}
                className={`w-14 text-center text-sm font-bold ${t.text} bg-transparent border-0 focus:outline-none`}
              />
              <button
                className={`w-7 h-7 flex items-center justify-center ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-orange-50'} rounded-full transition-colors`}
                onClick={() => setQuantity(parseFloat((quantity + 0.5).toFixed(3)))}
              >
                <Plus className={`w-3.5 h-3.5 ${t.text}`} />
              </button>
            </div>
          ) : (
            <div className={`flex items-center gap-0 ${t.bg} rounded-full border ${t.border} overflow-hidden`}>
              <button
                className={`w-10 h-10 flex items-center justify-center ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-orange-50'} transition-colors ${quantity <= 1 ? 'opacity-30' : ''}`}
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
              >
                <Minus className={`w-4 h-4 ${t.text}`} />
              </button>
              <span className={`w-8 text-center font-bold ${t.text}`}>{quantity}</span>
              <button
                className={`w-10 h-10 flex items-center justify-center ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-orange-50'} transition-colors`}
                onClick={() => setQuantity(quantity + 1)}
              >
                <Plus className={`w-4 h-4 ${t.text}`} />
              </button>
            </div>
          )}

          {/* Botão Adicionar ou Ver Opções */}
          {!isCombo && hasOrderSteps && comboStep === 0 ? (
            <button
              onClick={() => {
                // Inicializar seleções e ir para etapas
                const initialSelections = initializeDefaultSelections(product.order_steps);
                setStepSelections(initialSelections);
                setComboStep(1);
              }}
              className="flex-1 h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors shadow-lg shadow-orange-500/20"
            >
              Personalizar <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleAdd}
              className="flex-1 h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors shadow-lg shadow-orange-500/20"
            >
              {editingItem ? 'Salvar' : 'Adicionar'} • R$ {totalPrice.toFixed(2).replace('.', ',')}
            </button>
          )}
        </div>
    </>
  );
  if (telaLarga) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent hideClose className={`sm:max-w-md p-0 gap-0 overflow-hidden ${t.bg} border-0 rounded-2xl w-[92vw] sm:w-full max-h-[90vh] flex flex-col`} data-e2e="popup-produto-centralizado">
          <DialogHeader className="sr-only">
            <DialogTitle>{product.name}</DialogTitle>
          </DialogHeader>
          {conteudoPadrao}
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="bottom" hideClose className={`p-0 gap-0 overflow-hidden ${t.bg} border-0 rounded-t-2xl max-h-[92vh] h-auto flex flex-col max-w-[480px] mx-auto inset-x-0`}>
        <SheetTitle className="sr-only">{product.name}</SheetTitle>
        {/* Handle visual de bottom sheet */}
        <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
          <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-zinc-700' : 'bg-gray-300'}`} />
        </div>
        {conteudoPadrao}
      </SheetContent>
    </Sheet>
  );
}

/**
 * Step 0 do checkout: escolher DIA + HORÁRIO pra pedido agendado.
 * v1.5.375 — wizard em 2 sub-etapas:
 *   1. Cards de DIA (1 chamada em `/public/scheduling/days-overview`) com
 *      contagem de horários livres; dia fechado/lotado aparece desabilitado
 *      com o motivo. Janela de 1 dia pula direto pra etapa de horário.
 *   2. Grade de HORÁRIOS do dia escolhido, agrupada por período (manhã/tarde/
 *      noite), com botão "trocar" pra voltar e confirmação mostrando o horário.
 */
function SchedulePickerStep({ t, darkMode, scheduledAt, onConfirm, windowDays = 7 }) {
  const [view, setView] = useState("day"); // "day" | "time"
  const [overview, setOverview] = useState({ days: null, loading: true });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [dayInfo, setDayInfo] = useState({ slots: [], reason: null, loading: false });
  const [confirming, setConfirming] = useState(false);

  // Labels amigáveis por data ISO ("Hoje", "Amanhã", "Quinta").
  const dayMeta = (iso, idx) => {
    const [y, m, dd] = iso.split("-").map(Number);
    const d = new Date(y, m - 1, dd);
    const wd = d.toLocaleDateString("pt-BR", { weekday: "long" }).replace("-feira", "");
    const label = idx === 0 ? "Hoje" : idx === 1 ? "Amanhã" : wd.charAt(0).toUpperCase() + wd.slice(1);
    const sub = d.toLocaleDateString("pt-BR", { day: "numeric", month: "long" });
    return { label, sub };
  };

  // 1 chamada pra janela inteira — alimenta os cards de dia.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/scheduling/days-overview`)
      .then((res) => {
        if (cancelled) return;
        const days = Array.isArray(res.data?.days) ? res.data.days : [];
        setOverview({ days, loading: false });
        // Janela "só hoje": pula a etapa de dia direto pro horário.
        const abertos = days.filter((d) => d.n_slots > 0);
        if (days.length === 1 && abertos.length === 1) {
          setSelectedDate(abertos[0].date);
          setView("time");
        }
      })
      .catch(() => { if (!cancelled) setOverview({ days: [], loading: false }); });
    return () => { cancelled = true; };
  }, []);

  // Slots do dia escolhido
  useEffect(() => {
    if (!selectedDate) return;
    let cancelled = false;
    setDayInfo({ slots: [], reason: null, loading: true });
    setSelectedTime(null);
    axios.get(`${API}/public/scheduling/slots`, { params: { date: selectedDate } })
      .then((res) => {
        if (cancelled) return;
        setDayInfo({
          slots: Array.isArray(res.data?.slots) ? res.data.slots : [],
          reason: res.data?.reason || null,
          loading: false,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setDayInfo({ slots: [], reason: "Erro ao carregar horários.", loading: false });
      });
    return () => { cancelled = true; };
  }, [selectedDate]);

  const pickDay = (iso) => {
    setSelectedDate(iso);
    setView("time");
  };

  const backToDays = () => {
    setView("day");
    setSelectedTime(null);
  };

  const handleConfirm = () => {
    if (!selectedDate || !selectedTime) return;
    setConfirming(true);
    onConfirm(`${selectedDate}T${selectedTime}:00`);
  };

  // Agrupa horários por período do dia — só períodos com slots aparecem.
  const periodos = useMemo(() => {
    const grupos = [
      { key: "manha", label: "Manhã", icon: "☀️", test: (h) => h < 12 },
      { key: "tarde", label: "Tarde", icon: "🌤️", test: (h) => h >= 12 && h < 18 },
      { key: "noite", label: "Noite", icon: "🌙", test: (h) => h >= 18 },
    ];
    return grupos
      .map((g) => ({ ...g, slots: dayInfo.slots.filter((s) => g.test(parseInt(s.slice(0, 2), 10))) }))
      .filter((g) => g.slots.length > 0);
  }, [dayInfo.slots]);

  const selIdx = overview.days ? overview.days.findIndex((d) => d.date === selectedDate) : -1;
  const selMeta = selectedDate && selIdx >= 0 ? dayMeta(selectedDate, selIdx) : null;
  const janelaUmDia = (overview.days || []).length === 1;

  // ───────────────────────── Etapa 1: DIA ─────────────────────────
  if (view === "day") {
    return (
      <div className="p-6 space-y-5">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-3">
            <Calendar className="w-7 h-7 text-orange-500" />
          </div>
          <p className={`${t.text} font-bold text-lg leading-tight`}>Pra quando você quer?</p>
          <p className={`${t.textMuted} text-sm mt-1`}>
            Estamos fechados agora — escolha o dia e a gente prepara tudo pro seu horário.
          </p>
        </div>

        {overview.loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`h-[68px] rounded-2xl border-2 ${t.border} animate-pulse opacity-40`} />
            ))}
          </div>
        ) : (overview.days || []).length === 0 ? (
          <div className={`rounded-xl border-2 border-dashed ${t.border} p-6 text-center ${t.textMuted} text-sm`}>
            <Clock className="w-6 h-6 mx-auto mb-2 opacity-40" />
            Sem dias disponíveis pra agendamento no momento.
          </div>
        ) : (
          <div className="space-y-2 max-h-[46dvh] overflow-y-auto pr-0.5">
            {overview.days.map((d, idx) => {
              const meta = dayMeta(d.date, idx);
              const habilitado = d.n_slots > 0;
              const motivoCurto = !habilitado
                ? (d.reason || "").includes("limite") ? "Lotado"
                  : (d.reason || "").includes("fechada") ? "Fechado"
                  : "Indisponível"
                : null;
              return (
                <button
                  key={d.date}
                  type="button"
                  disabled={!habilitado}
                  onClick={() => pickDay(d.date)}
                  className={`w-full flex items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-all ${
                    habilitado
                      ? `${t.border} hover:border-orange-400 active:scale-[0.99]`
                      : `${t.border} opacity-45 cursor-not-allowed`
                  }`}
                >
                  <div className={`w-11 h-11 rounded-xl flex flex-col items-center justify-center shrink-0 ${habilitado ? "bg-orange-500/10 text-orange-500" : `${t.textMuted}`}`}>
                    <span className="text-base font-extrabold leading-none tabular-nums">{d.date.slice(8, 10)}</span>
                    <span className="text-[9px] uppercase font-bold leading-tight">{new Date(d.date + "T12:00:00").toLocaleDateString("pt-BR", { month: "short" }).replace(".", "")}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-[15px] leading-tight ${t.text}`}>{meta.label}</p>
                    <p className={`text-xs ${t.textMuted} truncate`}>{meta.sub}</p>
                  </div>
                  {habilitado ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-[11px] font-bold text-green-500 whitespace-nowrap">
                        {d.n_slots} horário{d.n_slots > 1 ? "s" : ""}
                      </span>
                      <ChevronRight className={`w-4 h-4 ${t.textMuted}`} />
                    </div>
                  ) : (
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full border ${t.border} ${t.textMuted} shrink-0`}>
                      {motivoCurto}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ──────────────────────── Etapa 2: HORÁRIO ────────────────────────
  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-orange-500/10 text-orange-500 flex items-center justify-center shrink-0">
          <Clock className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`${t.text} font-bold text-[15px] leading-tight`}>
            {selMeta ? `${selMeta.label}, ${selMeta.sub}` : "Escolha o horário"}
          </p>
          <p className={`${t.textMuted} text-xs`}>Que horas você quer receber?</p>
        </div>
        {!janelaUmDia && (
          <button
            type="button"
            onClick={backToDays}
            className="text-orange-500 text-xs font-bold uppercase tracking-wide px-3 py-2 rounded-lg hover:bg-orange-500/10 shrink-0"
          >
            Trocar dia
          </button>
        )}
      </div>

      {dayInfo.loading ? (
        <div className="flex items-center justify-center h-28">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
        </div>
      ) : dayInfo.slots.length === 0 ? (
        <div className={`rounded-xl border-2 border-dashed ${t.border} p-6 text-center ${t.textMuted} text-sm`}>
          <Clock className="w-6 h-6 mx-auto mb-2 opacity-40" />
          {dayInfo.reason || "Sem horários disponíveis nesse dia."}
        </div>
      ) : (
        <div className="space-y-4 max-h-[42dvh] overflow-y-auto pr-0.5">
          {periodos.map((g) => (
            <div key={g.key}>
              <p className={`text-[11px] uppercase tracking-wider ${t.textMuted} mb-2 font-bold`}>
                {g.icon} {g.label}
              </p>
              <div className="grid grid-cols-4 gap-2">
                {g.slots.map((hhmm) => {
                  const active = hhmm === selectedTime;
                  return (
                    <button
                      key={hhmm}
                      type="button"
                      onClick={() => setSelectedTime(hhmm)}
                      className={`rounded-xl border-2 px-2 py-2.5 text-sm font-bold tabular-nums transition-all ${
                        active
                          ? "border-orange-500 bg-orange-500 text-white shadow-md shadow-orange-500/30 scale-[1.03]"
                          : `${t.border} ${t.text} hover:border-orange-300 active:scale-95`
                      }`}
                    >
                      {hhmm}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Resumo da escolha — aparece quando um horário é selecionado */}
      {selectedTime && (
        <div className="rounded-xl border-2 border-orange-500/40 bg-orange-500/[0.06] px-4 py-3 flex items-center gap-2.5">
          <CheckCircle className="w-5 h-5 text-orange-500 shrink-0" />
          <p className={`text-sm ${t.text}`}>
            <strong>{selMeta ? selMeta.label : ""}</strong> às <strong>{selectedTime}</strong> — a loja confirma seu pedido antes de preparar.
          </p>
        </div>
      )}

      <Button
        onClick={handleConfirm}
        disabled={!selectedDate || !selectedTime || confirming}
        className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold disabled:opacity-50"
      >
        {selectedTime ? `Agendar pra ${selectedTime}` : "Escolha um horário"}
        <ChevronRight className="w-5 h-5 ml-1" />
      </Button>
    </div>
  );
}

/** Bloco compacto em cima dos steps 1..3 mostrando quando o pedido vai chegar.
 * Mostra janela de 30 min a partir do horário escolhido (ex: 20:00 → "20:00 - 20:30")
 * — margem defensiva pra atrasos naturais de cozinha/entrega. */
function ScheduledSummary({ iso, t, onEdit }) {
  const [dayLabel, timeLabel] = (() => {
    try {
      const [d, hm] = String(iso).split("T");
      const [y, mo, da] = d.split("-").map((n) => parseInt(n, 10));
      const [hh, mm] = (hm || "00:00").split(":");
      const dt = new Date(y, mo - 1, da);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diff = Math.round((dt - today) / (1000 * 60 * 60 * 24));
      let dl;
      if (diff === 0) dl = "Hoje";
      else if (diff === 1) dl = "Amanhã";
      else dl = dt.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
      // Janela de +30 min pra dar margem
      const startMinutes = parseInt(hh, 10) * 60 + parseInt(mm, 10);
      const endMinutes = startMinutes + 30;
      const eh = String(Math.floor(endMinutes / 60) % 24).padStart(2, "0");
      const em = String(endMinutes % 60).padStart(2, "0");
      return [dl, `${hh}:${mm} - ${eh}:${em}`];
    } catch { return [iso, ""]; }
  })();

  return (
    <div className={`rounded-xl border-2 border-orange-500/40 bg-orange-500/[0.06] p-3 flex items-center gap-3 mb-4`}>
      <div className="w-10 h-10 rounded-lg bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
        <Calendar className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[10px] uppercase tracking-wider text-orange-500 font-bold`}>Entrega prevista</p>
        <p className={`font-bold ${t.text} leading-tight`}>{dayLabel} · entre {timeLabel}</p>
      </div>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-orange-500 text-xs font-semibold hover:underline"
        >
          Editar
        </button>
      )}
    </div>
  );
}

// Componente de Checkout (Endereço + Pagamento)
function CheckoutModal({ open, onClose, cart, cartTotal, couponableSubtotal, client, darkMode, onOrderComplete, stats = {}, visibleCoupons = [], selectedCouponCode = null, onSelectCoupon, allProducts = [], storeOpen = true, schedulingConfig = { ativo: false }, cashbackConfig = { ativo: false, percentual: 0 }, cashbackSaldo = 0, onCashbackConsumed = () => {}, activePromotions = [], onOfertasDesatualizadas = () => {}, sugestoes = null }) {
  // Subtotal couponavel: se nao foi passado (retrocompat), assume que tudo aceita cupom
  const couponableBase = (typeof couponableSubtotal === 'number' && couponableSubtotal >= 0) ? couponableSubtotal : cartTotal;
  // Agendamento: obrigatório quando loja fechada + config ativa. Cliente escolhe
  // dia + horário no step 0 (antes de tudo). Ficando aberta, ignora.
  const agendamentoRequerido = !storeOpen && !!schedulingConfig?.ativo;
  const [step, setStep] = useState(agendamentoRequerido ? 0 : 1); // 0 = Agendamento, 1 = Tipo, 2 = Endereço, 3 = Pagamento
  const [scheduledAt, setScheduledAt] = useState(null); // ISO string ou null
  const [deliveryType, setDeliveryType] = useState(null); // 'pickup' ou 'delivery'
  const [minOrderModalOpen, setMinOrderModalOpen] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: 'Casa',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cep: ''
  });
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  // Cupons Cortesia: cliente digita código no checkout e sistema valida via /api/coupons/validate
  const [cupomCodigo, setCupomCodigo] = useState("");
  const [cupomInfo, setCupomInfo] = useState(null); // { valid, discount, error, coupon }
  const [cupomValidating, setCupomValidating] = useState(false);
  const [showCupomInput, setShowCupomInput] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  // Quando true (default), o endereco recem-cadastrado e persistido (servidor + localStorage)
  // pra reaparecer nas proximas compras. Cliente pode desmarcar pra usar so neste pedido.
  const [saveAddressForNext, setSaveAddressForNext] = useState(true);
  // Erros por campo do form de novo endereco — limpa quando o campo eh editado.
  // { endereco?, bairro?, numero? }
  const [addressErrors, setAddressErrors] = useState({});
  const [showPixModal, setShowPixModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Proteção contra duplo clique
  // Estados para popup de troco
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [needsChange, setNeedsChange] = useState(null); // null = não respondeu, true = precisa, false = não precisa
  const [changeAmount, setChangeAmount] = useState('');
  
  // Estado para popup de confirmação de deletar endereço
  const [deleteAddressDialog, setDeleteAddressDialog] = useState({ open: false, addressId: null, addressLabel: '' });
  
  // Estados para autocomplete de bairros e ruas
  const [bairros, setBairros] = useState([]);
  const [ruasSugestoes, setRuasSugestoes] = useState([]);
  const [bairrosSugestoes, setBairrosSugestoes] = useState([]);
  const [valorEntrega, setValorEntrega] = useState(0);

  // Calcular total incluindo entrega
  const totalComEntrega = useMemo(() => {
    const subtotal = cartTotal;
    const entrega = (deliveryType === 'delivery' && valorEntrega > 0) ? valorEntrega : 0;
    return subtotal + entrega;
  }, [cartTotal, deliveryType, valorEntrega]);

  // Cashback aplicado ao pedido (v1.5.317+):
  //   - Só existe quando cliente logado + config ativa + saldo > 0
  //   - Switch começa DESATIVADO, cliente precisa opt-in no step 3
  //   - Cap: SUM(itens NÃO em promoção) — cashback não vale pra frete nem itens promocionais
  //   - Se cupom aplicado, cashbackDisponivel = 0 (cupom E cashback são mutuamente exclusivos)
  const [useCashback, setUseCashback] = useState(false);
  const isItemPromotional = (item) => {
    // Campanha Inteligente: o item marcado segue o switch "acumula com cashback" da loja.
    if (item.oferta_chave) return !item.oferta_acumula_cashback;
    // Sinal 1: promoção ativa cobre este product_id (e combo_type quando aplicável)
    const promoHit = (activePromotions || []).some(promo => !promo.is_daily_offer &&
      (promo.products || []).some(pp => String(pp.product_id) === String(item.id))
    );
    if (promoHit) return true;
    // Sinal 2: original_price > preço final (promo decorativa "de/por")
    const finalP = Number(item.final_price ?? item.sale_price ?? 0);
    const origP = Number(item.original_price || 0);
    return origP > 0 && finalP > 0 && origP > finalP;
  };
  const cashbackEligibleSubtotal = (cart || []).reduce((sum, item) => {
    if (isItemPromotional(item)) return sum;
    const price = Number(item.final_price ?? item.sale_price ?? 0);
    return sum + price * (item.quantity || 0);
  }, 0);
  const hasCoupon = !!(cupomInfo && cupomInfo.valid);
  // Exceção: o prêmio do Cartão Fidelidade, quando a loja deixa somar com cashback.
  const cupomSomaCashback = hasCoupon && !!(visibleCoupons || []).find(c => c.code === cupomCodigo)?._premio?.somaCashback;
  const cashbackDisponivel = (client && cashbackConfig.ativo && cashbackSaldo > 0 && (!hasCoupon || cupomSomaCashback))
    ? Math.min(cashbackSaldo, cashbackEligibleSubtotal) : 0;
  const cashbackAplicado = useCashback ? cashbackDisponivel : 0;
  // Auto-desliga o switch se, por qualquer razão, a elegibilidade zerou (cliente
  // aplicou cupom depois de ligar, tirou último item não-promo do carrinho, etc.)
  useEffect(() => {
    if (useCashback && cashbackDisponivel <= 0) setUseCashback(false);
  }, [cashbackDisponivel, useCashback]);

  // Total final que o cliente realmente vai pagar — desconta o cupom aplicado
  // (quando valido) + cashback aplicado. Usado em todos os pontos de UI que mostram
  // o "Total do pedido" como dialog de troco, validacao do valor de troco etc.
  const totalFinal = useMemo(() => {
    const desconto = (cupomInfo && cupomInfo.valid) ? Number(cupomInfo.discount || 0) : 0;
    return Math.max(0, totalComEntrega - desconto - cashbackAplicado);
  }, [totalComEntrega, cupomInfo, cashbackAplicado]);

  // Auto-aplica cupom quando o cliente chega na etapa final (pagamento).
  // Prioridade: (1) selectedCouponCode — cupom escolhido no modal do cardapio, (2) melhor
  // cupom elegivel automaticamente. Se o cliente nao escolheu nada, NAO auto-aplica —
  // vamos mostrar a lista de opcoes na etapa 3 para ele decidir.
  const autoAppliedRef = useRef(false);
  useEffect(() => {
    if (autoAppliedRef.current) return;
    if (step !== 3) return;
    if (cupomInfo) return;
    if (!visibleCoupons || visibleCoupons.length === 0) return;
    if (!cartTotal || cartTotal <= 0) return;
    // Filtra elegiveis: min_order_value aplica sobre o subtotal couponavel
    const eligible = visibleCoupons.filter(c => {
      const m = Number(c.min_order_value || 0);
      return couponableBase >= m && premioCabeNaSacola(c, cart);
    });
    if (eligible.length === 0) return;
    // Prioridade: selecao explicita do cliente (modal do cardapio) > melhor cupom automatico
    let best = null;
    if (selectedCouponCode) {
      best = eligible.find(c => c.code === selectedCouponCode) || null;
    }
    if (!best) {
      // Auto-escolhe: cupom elegivel com maior desconto estimado sobre o subtotal couponavel
      const estDiscount = (c) => {
        const v = Number(c.discount_value || 0);
        if (c.discount_type === 'percentage') {
          const raw = couponableBase * (v / 100);
          const cap = Number(c.max_discount_value || 0);
          return cap > 0 ? Math.min(raw, cap) : raw;
        }
        return v;
      };
      // O prêmio do cartão só entra quando o cliente escolhe usar (pode querer guardar).
      best = eligible.filter(c => !c._premio).sort((a, b) => estDiscount(b) - estDiscount(a))[0] || null;
    }
    if (!best?.code) return;
    autoAppliedRef.current = true;
    (async () => {
      const phone = (client?.telefone || client?.phone || "").trim();
      try {
        const res = await axios.post(`${API}/coupons/validate`, {
          code: best.code,
          order_total: totalComEntrega,
          products_total: couponableBase,
          delivery_fee: (deliveryType === 'delivery' && valorEntrega > 0) ? valorEntrega : 0,
          customer_phone: phone || null,
          customer_id: client?.id || null,
          modulo: "Cardapio",
        });
        if (res.data?.valid) {
          setCupomCodigo(best.code);
          setCupomInfo(res.data);
          toast.success(`Cupom ${best.code} aplicado: −R$ ${Number(res.data.discount || 0).toFixed(2).replace('.', ',')}`);
        } else {
          autoAppliedRef.current = false;
        }
      } catch (e) {
        autoAppliedRef.current = false;
      }
    })();
  }, [step, cupomInfo, visibleCoupons, cartTotal, couponableBase, totalComEntrega, client, selectedCouponCode, deliveryType, valorEntrega]);

  // Reset do auto-apply quando o checkout abre/fecha
  useEffect(() => {
    if (!open) autoAppliedRef.current = false;
  }, [open]);

  // Revalidacao do cupom quando carrinho/frete mudam: se o cliente removeu itens e
  // caiu abaixo do min_order_value (ou mudou de entrega p/ retirada etc), o desconto
  // precisa ser recalculado — caso contrario fica "congelado" com o valor antigo.
  useEffect(() => {
    if (!cupomCodigo || !cupomInfo?.valid) return;
    const handle = setTimeout(async () => {
      const phone = (client?.telefone || client?.phone || "").trim();
      try {
        const res = await axios.post(`${API}/coupons/validate`, {
          code: cupomCodigo,
          order_total: totalComEntrega,
          products_total: couponableBase,
          delivery_fee: (deliveryType === 'delivery' && valorEntrega > 0) ? valorEntrega : 0,
          customer_phone: phone || null,
          customer_id: client?.id || null,
          modulo: "Cardapio",
        });
        if (res.data?.valid) {
          const newDiscount = Number(res.data.discount || 0);
          const oldDiscount = Number(cupomInfo.discount || 0);
          if (newDiscount !== oldDiscount) setCupomInfo(res.data);
        } else {
          setCupomInfo(null);
          setCupomCodigo("");
          // libera o auto-apply p/ reavaliar cupons elegiveis com o novo total
          autoAppliedRef.current = false;
          toast.error(res.data?.error || `Cupom ${cupomCodigo} não é mais válido`);
        }
      } catch (e) {}
    }, 250);
    return () => clearTimeout(handle);
  }, [cartTotal, couponableBase, totalComEntrega, deliveryType, valorEntrega, cupomCodigo]);

  // Carregar bairros
  useEffect(() => {
    const fetchBairros = async () => {
      try {
        const res = await axios.get(`${API}/bairros`);
        setBairros(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("Erro ao carregar bairros:", error);
        setBairros([]);
      }
    };
    fetchBairros();
  }, []);

  // Buscar sugestões de ruas
  const handleRuaChange = async (value) => {
    setNewAddress(prev => ({ ...prev, endereco: value }));
    if (value.length >= 2) {
      try {
        const res = await axios.get(`${API}/ruas/search?termo=${encodeURIComponent(value)}`);
        setRuasSugestoes(res.data);
      } catch (error) {
        setRuasSugestoes([]);
      }
    } else {
      setRuasSugestoes([]);
    }
  };

  // Filtrar sugestões de bairros
  const handleBairroChange = (value) => {
    setNewAddress(prev => ({ ...prev, bairro: value }));
    if (value.length >= 1) {
      const filtered = bairros.filter(b => 
        b.nome.toLowerCase().includes(value.toLowerCase())
      );
      setBairrosSugestoes(filtered);
    } else {
      setBairrosSugestoes([]);
    }
    // Limpar valor de entrega se mudar bairro
    setValorEntrega(0);
  };

  // Selecionar rua da sugestão
  const handleSelectRua = (rua) => {
    const bairroNome = (rua.bairro_nome || '').trim();
    setNewAddress(prev => ({
      ...prev,
      endereco: rua.nome,
      bairro: bairroNome || prev.bairro,
      cep: rua.bairro_cep || rua.cep || prev.cep
    }));
    setRuasSugestoes([]);
    // Se tem bairro, buscar valor de entrega (comparação com trim)
    if (bairroNome) {
      const bairro = bairros.find(b => b.nome.trim().toLowerCase() === bairroNome.toLowerCase());
      if (bairro) {
        setValorEntrega(bairro.valor_entrega || 0);
      }
    }
  };

  // Selecionar bairro da sugestão
  const handleSelectBairro = (bairro) => {
    setNewAddress(prev => ({
      ...prev,
      bairro: bairro.nome.trim(), // Normaliza o nome do bairro
      cep: bairro.cep || prev.cep
    }));
    setBairrosSugestoes([]);
    setShowBairroDropdown(false);
    setValorEntrega(bairro.valor_entrega || 0);
  };

  // Estado para dropdown de bairros
  const [showBairroDropdown, setShowBairroDropdown] = useState(false);
  
  // Verificar se CEP único está ativo (todos os bairros têm o mesmo CEP)
  const cepUnicoAtivo = Array.isArray(bairros) && bairros.length > 0 && bairros.every(b => b.cep && b.cep === bairros[0].cep);
  const cepUnicoValue = cepUnicoAtivo ? bairros[0].cep : '';

  // Buscar formas de pagamento do backend
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        // Usar endpoint público para cardapio
        const response = await axios.get(`${API}/public/payment-methods/cardapio`);
        
        // Mapear formas de pagamento do backend para o formato do frontend
        const mappedMethods = response.data.map(pm => {
          // Mapear códigos do backend para IDs do frontend
          const codeToIdMap = {
            'dinheiro': 'cash',
            'pix': 'pix',
            'pix_online': 'pix_online',
            'cartao_online': 'cartao_online',
            'credito': 'credit',
            'debito': 'debit',
            'cartao': 'credit' // cartao genérico vira crédito
          };

          // Mapear ícones
          const iconMap = {
            'dinheiro': Banknote,
            'pix': QrCode,
            'pix_online': QrCode,
            'cartao_online': CreditCard,
            'credito': CreditCard,
            'debito': CreditCard,
            'cartao': CreditCard
          };

          // Mapear cores
          const colorMap = {
            'dinheiro': 'text-green-500',
            'pix': 'text-teal-500',
            'pix_online': 'text-orange-500',
            'cartao_online': 'text-orange-500',
            'credito': 'text-blue-500',
            'debito': 'text-purple-500',
            'cartao': 'text-blue-500'
          };

          let id = codeToIdMap[pm.code] || pm.code;

          // Se for pix_online ou cartao_online, usar diretamente
          if (pm.code === 'pix_online') {
            id = 'pix_online';
          } else if (pm.code === 'cartao_online') {
            id = 'cartao_online';
          } else if (pm.code === 'pix') {
            // PIX tradicional mantém como pix
            id = 'pix';
          }
          
          const icon = iconMap[pm.code] || CreditCard;
          const color = colorMap[pm.code] || 'text-gray-500';
          
          return {
            id: id,
            code: pm.code, // Manter código original do backend
            label: pm.name,
            icon: icon,
            color: color
          };
        });
        
        setAvailablePaymentMethods(mappedMethods);
      } catch (error) {
        console.error("Erro ao carregar formas de pagamento:", error);
        // Fallback para formas padrão se houver erro
        setAvailablePaymentMethods([
          { id: 'pix', code: 'pix', label: 'PIX', icon: QrCode, color: 'text-teal-500' },
          { id: 'credit', code: 'credito', label: 'Cartão de Crédito', icon: CreditCard, color: 'text-blue-500' },
          { id: 'debit', code: 'debito', label: 'Cartão de Débito', icon: CreditCard, color: 'text-purple-500' },
          { id: 'cash', code: 'dinheiro', label: 'Dinheiro', icon: Banknote, color: 'text-green-500' },
        ]);
      }
    };
    
    if (open) {
      fetchPaymentMethods();
    }
  }, [open]);

  // Reset quando abrir
  useEffect(() => {
    if (open && client) {
      setStep(agendamentoRequerido ? 0 : 1);
      setScheduledAt(null);
      setDeliveryType(null);
      setSelectedAddress(null);
      setPaymentMethod(null);
      setShowNewAddressForm(false);
      setShowChangeDialog(false);
      setNeedsChange(null);
      setChangeAmount('');
      fetchAddresses();
    }
  }, [open, client]);

  const fetchAddresses = async () => {
    setLoadingAddresses(true);
    try {
      let allAddresses = [];

      // 1. Carregar do servidor (se tem ID local)
      if (client?.id) {
        const response = await axios.get(`${API}/client-addresses/${client.id}`, comPulseira());
        allAddresses = response.data || [];
      }

      // 2. Carregar do localStorage (endereços salvos no dispositivo)
      const phone = client?.telefone || client?.phone;
      if (phone) {
        const localKey = `nucleo_addresses_${phone.replace(/\D/g, '')}`;
        try {
          const localAddrs = JSON.parse(localStorage.getItem(localKey) || '[]');
          // Mesclar: local tem prioridade, evitar duplicatas por bairro+endereco
          const serverKeys = new Set(allAddresses.map(a => `${a.endereco}-${a.numero}-${a.bairro}`.toLowerCase()));
          for (const la of localAddrs) {
            const key = `${la.endereco}-${la.numero}-${la.bairro}`.toLowerCase();
            if (!serverKeys.has(key)) {
              allAddresses.push({ ...la, id: la.id || `local_${Date.now()}_${Math.random()}`, source: 'local' });
            }
          }
        } catch (_) {}
      }

      setAddresses(allAddresses);
      const defaultAddr = allAddresses.find(a => a.is_default) || allAddresses[0];
      if (defaultAddr) {
        setSelectedAddress(defaultAddr.id);
        const bairro = bairros.find(b => b.nome.trim().toLowerCase() === (defaultAddr.bairro || '').trim().toLowerCase());
        if (bairro) {
          setValorEntrega(bairro.valor_entrega || 0);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar enderecos:", error);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleSaveNewAddress = async () => {
    // Coleta todos os erros de uma vez pra mostrar feedback visual em cada campo
    // ao inves de toast generico. Cliente ve exatamente o que falta corrigir.
    const errs = {};
    if (!newAddress.endereco?.trim()) errs.endereco = "Informe a rua";
    if (!newAddress.numero?.trim()) errs.numero = "Informe o número";
    if (!newAddress.bairro?.trim()) errs.bairro = "Selecione o bairro";
    if (Object.keys(errs).length > 0) {
      setAddressErrors(errs);
      toast.error("Preencha os campos destacados");
      return;
    }
    const bairroSelecionado = bairros.find(b => b.nome.trim().toLowerCase() === newAddress.bairro.trim().toLowerCase());
    if (!bairroSelecionado) {
      setAddressErrors({ bairro: "Selecione um bairro da lista" });
      toast.error("Selecione um bairro válido da lista");
      return;
    }
    setAddressErrors({});
    setSavingAddress(true);
    const addrData = {
      ...newAddress,
      bairro: bairroSelecionado.nome.trim(),
      is_default: addresses.length === 0
    };

    try {
      let savedAddr;

      // Persiste no servidor + localStorage apenas se o cliente optou por salvar.
      // Se desmarcou, o endereco existe so na sessao atual (id local efemero).
      if (saveAddressForNext && client?.id) {
        const response = await axios.post(`${API}/client-addresses`, {
          client_id: client.id,
          ...addrData
        }, comPulseira());
        savedAddr = response.data;
      } else {
        savedAddr = { id: `local_${Date.now()}`, ...addrData, source: 'local', _ephemeral: !saveAddressForNext };
      }

      const phone = client?.telefone || client?.phone;
      if (saveAddressForNext && phone) {
        const localKey = `nucleo_addresses_${phone.replace(/\D/g, '')}`;
        try {
          const existing = JSON.parse(localStorage.getItem(localKey) || '[]');
          existing.unshift({ ...addrData, id: savedAddr.id, created_at: new Date().toISOString() });
          localStorage.setItem(localKey, JSON.stringify(existing.slice(0, 5)));
        } catch (_) {}
      }

      setAddresses(prev => [savedAddr, ...prev]);
      setSelectedAddress(savedAddr.id);
      setValorEntrega(bairroSelecionado.valor_entrega || 0);
      setShowNewAddressForm(false);
      setNewAddress({ label: 'Casa', endereco: '', numero: '', complemento: '', bairro: '', cep: '' });
      toast.success(saveAddressForNext ? "Endereço salvo para próximas compras!" : "Endereço usado neste pedido");
    } catch (error) {
      toast.error("Erro ao salvar endereço");
    } finally {
      setSavingAddress(false);
    }
  };

  // Abrir popup de confirmação para deletar endereço
  const handleDeleteAddressClick = (e, addr) => {
    e.stopPropagation(); // Evita selecionar o endereço ao clicar no delete
    setDeleteAddressDialog({ open: true, addressId: addr.id, addressLabel: addr.label });
  };

  // Confirmar deleção do endereço
  const handleConfirmDeleteAddress = async () => {
    const { addressId } = deleteAddressDialog;
    try {
      await axios.delete(`${API}/client-addresses/${addressId}`, comPulseira());
      setAddresses(prev => prev.filter(a => a.id !== addressId));
      if (selectedAddress === addressId) {
        setSelectedAddress(null);
      }
      toast.success("Endereço removido!");
    } catch (error) {
      toast.error("Erro ao remover endereço");
    } finally {
      setDeleteAddressDialog({ open: false, addressId: null, addressLabel: '' });
    }
  };

  const handleSelectDeliveryType = (type) => {
    // Validacao de pedido minimo (apenas para entrega via cardapio)
    const pedidoMinimo = Number(stats?.pedido_minimo) || 0;
    if (type === 'delivery' && pedidoMinimo > 0 && cartTotal < pedidoMinimo) {
      setMinOrderModalOpen(true);
      return;
    }
    setDeliveryType(type);
    if (type === 'pickup') {
      setStep(3); // Pula direto para pagamento
      trackFunnel("revision");
    } else {
      setStep(2); // Vai para seleção de endereço
    }
  };

  const handleConfirmAddress = () => {
    if (!selectedAddress && !showNewAddressForm) {
      toast.error("Selecione um endereço ou cadastre um novo");
      return;
    }
    setStep(3);
    trackFunnel("revision");
  };

  // Quando seleciona forma de pagamento
  const handleSelectPayment = (method) => {
    setPaymentMethod(method);

    // Se for dinheiro, abrir dialog de troco — detecta por id/code/label contendo "dinheiro" ou igual a "cash"
    const methodObj = (paymentMethods || []).find(m => m.id === method || m.code === method) || {};
    const idStr = String(method || '').toLowerCase();
    const codeStr = String(methodObj.code || '').toLowerCase();
    const labelStr = String(methodObj.label || '').toLowerCase();
    const isDinheiro = idStr === 'cash' || idStr.includes('dinheiro') || codeStr.includes('dinheiro') || labelStr.includes('dinheiro');
    if (isDinheiro) {
      setShowChangeDialog(true);
    }
    // PIX Online: apenas seleciona, não abre modal automaticamente
    // O modal será aberto ao clicar em "Finalizar Pedido"
  };

  // Funções auxiliares para montar dados de combo/acompanhamentos
  // Regras em utils/etapasDoItem.js (as mesmas que a Mesa QR usa)
  const getItemStepsSummaryHelper = (item) => resumoEtapasDoItem(item);
  const getItemSubitemsHelper = (item) => subitemsDoItem(item, allProducts);

  // Criar pedido e abrir modal PIX
  const handlePixOnlinePayment = async () => {
    // Proteção contra duplo clique
    if (isSubmitting) {
      return;
    }

    // Validar se deliveryType está definido — volta o step 1 pra cliente corrigir
    if (!deliveryType) {
      toast.error("Selecione o tipo de entrega primeiro");
      setStep(1);
      return;
    }

    // Se for delivery, validar endereço — volta direto pra aba de endereço
    if (deliveryType === 'delivery' && !selectedAddress) {
      toast.error("Selecione um endereço de entrega");
      setStep(2);
      return;
    }

    // Validar carrinho
    if (!cart || cart.length === 0) {
      toast.error("Carrinho vazio");
      return;
    }
    
    // Validar cliente (aceitar global sem ID local)
    if (!client || (!client.id && !client.global && !client.phone)) {
      toast.error("Cliente nao identificado. Faca login novamente.");
      return;
    }
    
    // Validar valor total
    if (!totalComEntrega || totalComEntrega <= 0) {
      toast.error("Valor do pedido inválido");
      return;
    }
    
    const selectedAddr = deliveryType === 'delivery' ? addresses.find(a => a.id === selectedAddress) : null;
    
    const pedidoData = {
      cliente_id: client.id || null,
      cliente_nome: client.nome,
      cliente_telefone: client.telefone || client.phone,
      cliente_email: client.email,
      items: cart.map(item => ({
        id: item.id,
        product_id: item.id,
        nome: item.name,
        quantidade: item.quantity,
        preco: item.final_price || item.sale_price,
        observacao: item.observation || null,
        photo_url: item.photo_url || null,
        combo_type: item.combo_type || null,
        subitems: getItemSubitemsHelper(item),
        etapas: getItemStepsSummaryHelper(item),
        is_promotional: isItemPromotional(item),
        oferta_chave: item.oferta_chave || null,
        sugestao_lugar: item.sugestao_lugar || undefined,
        sugestao_qtd: item.sugestao_qtd || undefined,
        pizza_meta: item.pizza ? {
          size_id: item.pizza.size_id,
          size_name: item.pizza.size_name,
          flavor_ids: item.pizza.flavor_ids || [],
          border_id: item.pizza.border_id || null,
          border_name: item.pizza.border_name || null,
          pricing_rule: item.pizza.pricing_rule || null,
        } : null,
      })),
      total: totalComEntrega,
      forma_pagamento: 'pix_online',
      tipo_entrega: deliveryType,
      endereco_label: deliveryType === 'delivery' && selectedAddr ? selectedAddr.label : null,
      endereco_rua: deliveryType === 'delivery' && selectedAddr ? selectedAddr.endereco : null,
      endereco_numero: deliveryType === 'delivery' && selectedAddr ? selectedAddr.numero : null,
      endereco_complemento: deliveryType === 'delivery' && selectedAddr ? selectedAddr.complemento : null,
      endereco_bairro: deliveryType === 'delivery' && selectedAddr ? selectedAddr.bairro : null,
      endereco_cep: deliveryType === 'delivery' && selectedAddr ? selectedAddr.cep : null,
      modulo: "Cardapio",
      sms_ref: obterSmsRef(),
      origem_anuncio: obterOrigemAnuncio(),
      valor_entrega: deliveryType === 'delivery' ? valorEntrega : 0,
      cupom_codigo: cupomInfo && cupomInfo.valid ? cupomCodigo : undefined,
      cupom_desconto: cupomInfo && cupomInfo.valid ? Number(cupomInfo.discount || 0) : undefined,
      cashback_usado: cashbackAplicado > 0 ? cashbackAplicado : undefined,
      // Recuperador de Vendas: pedido que veio do link da conversa com a CLEO.
      recuperador_token: tokenDaConversa() || undefined,
      payment_status: 'PENDENTE_PIX',
      scheduled_at: scheduledAt || undefined,
    };

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/pedidos`, pedidoData, { timeout: 30000 });

      if (!response.data || !response.data.id) {
        throw new Error("Resposta inválida do servidor");
      }

      const newPedido = response.data;
      setCurrentOrderId(newPedido.id);
      setShowPixModal(true);
      if (cashbackAplicado > 0) onCashbackConsumed();
    } catch (error) {
      // 409 = preço mudou (inclusive oferta que expirou): rebusca as ofertas do dia.
      if (error?.response?.status === 409) onOfertasDesatualizadas();
      if (error.code === 'ECONNABORTED') {
        toast.error("Tempo de espera esgotado. Tente novamente.");
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || "Dados inválidos. Verifique suas informações.");
      } else if (error.response?.status >= 500) {
        toast.error("Erro no servidor. Tente novamente em alguns instantes.");
      } else {
        toast.error(error.response?.data?.detail || "Erro ao criar pedido. Tente novamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Callback quando PIX é confirmado
  const handlePixConfirmed = () => {
    window.dispatchEvent(new Event('pedidosUpdated'));
    toast.success("Pagamento confirmado!");
    trackPurchase({ id: currentOrderId, items: cart, value: totalComEntrega }); clarityEvento("pedido_feito");
    onOrderComplete({ id: currentOrderId });
    onClose();
  };

  // Criar pedido e abrir modal de cartão online
  const handleCartaoOnlinePayment = async () => {
    if (isSubmitting) return;

    if (!deliveryType) { toast.error("Selecione o tipo de entrega primeiro"); setStep(1); return; }
    if (deliveryType === 'delivery' && !selectedAddress) { toast.error("Selecione um endereço de entrega"); setStep(2); return; }
    if (!cart || cart.length === 0) { toast.error("Carrinho vazio"); return; }
    if (!client || !client.id) { toast.error("Cliente não identificado. Faça login novamente."); return; }
    if (!totalComEntrega || totalComEntrega <= 0) { toast.error("Valor do pedido inválido"); return; }

    const selectedAddr = deliveryType === 'delivery' ? addresses.find(a => a.id === selectedAddress) : null;

    const pedidoData = {
      cliente_id: client.id || null,
      cliente_nome: client.nome,
      cliente_telefone: client.telefone || client.phone,
      cliente_email: client.email,
      items: cart.map(item => ({
        id: item.id,
        product_id: item.id,
        nome: item.name,
        quantidade: item.quantity,
        preco: item.final_price || item.sale_price,
        observacao: item.observation || null,
        photo_url: item.photo_url || null,
        combo_type: item.combo_type || null,
        subitems: getItemSubitemsHelper(item),
        etapas: getItemStepsSummaryHelper(item),
        is_promotional: isItemPromotional(item),
        oferta_chave: item.oferta_chave || null,
        sugestao_lugar: item.sugestao_lugar || undefined,
        sugestao_qtd: item.sugestao_qtd || undefined,
        pizza_meta: item.pizza ? {
          size_id: item.pizza.size_id,
          size_name: item.pizza.size_name,
          flavor_ids: item.pizza.flavor_ids || [],
          border_id: item.pizza.border_id || null,
          border_name: item.pizza.border_name || null,
          pricing_rule: item.pizza.pricing_rule || null,
        } : null,
      })),
      total: totalComEntrega,
      forma_pagamento: 'cartao_online',
      tipo_entrega: deliveryType,
      endereco_label: deliveryType === 'delivery' && selectedAddr ? selectedAddr.label : null,
      endereco_rua: deliveryType === 'delivery' && selectedAddr ? selectedAddr.endereco : null,
      endereco_numero: deliveryType === 'delivery' && selectedAddr ? selectedAddr.numero : null,
      endereco_complemento: deliveryType === 'delivery' && selectedAddr ? selectedAddr.complemento : null,
      endereco_bairro: deliveryType === 'delivery' && selectedAddr ? selectedAddr.bairro : null,
      endereco_cep: deliveryType === 'delivery' && selectedAddr ? selectedAddr.cep : null,
      modulo: "Cardapio",
      sms_ref: obterSmsRef(),
      origem_anuncio: obterOrigemAnuncio(),
      valor_entrega: deliveryType === 'delivery' ? valorEntrega : 0,
      cupom_codigo: cupomInfo && cupomInfo.valid ? cupomCodigo : undefined,
      cupom_desconto: cupomInfo && cupomInfo.valid ? Number(cupomInfo.discount || 0) : undefined,
      cashback_usado: cashbackAplicado > 0 ? cashbackAplicado : undefined,
      // Recuperador de Vendas: pedido que veio do link da conversa com a CLEO.
      recuperador_token: tokenDaConversa() || undefined,
      payment_status: 'pending',
      scheduled_at: scheduledAt || undefined,
    };

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/pedidos`, pedidoData, { timeout: 30000 });
      if (!response.data || !response.data.id) throw new Error("Resposta inválida do servidor");
      const newPedido = response.data;
      setCurrentOrderId(newPedido.id);
      setShowCardModal(true);
      if (cashbackAplicado > 0) onCashbackConsumed();
    } catch (error) {
      // 409 = preço mudou (inclusive oferta que expirou): rebusca as ofertas do dia.
      if (error?.response?.status === 409) onOfertasDesatualizadas();
      if (error.code === 'ECONNABORTED') {
        toast.error("Tempo de espera esgotado. Tente novamente.");
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || "Dados inválidos. Verifique suas informações.");
      } else if (error.response?.status >= 500) {
        toast.error("Erro no servidor. Tente novamente em alguns instantes.");
      } else {
        toast.error(error.response?.data?.detail || "Erro ao criar pedido. Tente novamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Callback quando cartão é confirmado
  const handleCardConfirmed = () => {
    window.dispatchEvent(new Event('pedidosUpdated'));
    toast.success("Pagamento com cartão confirmado!");
    trackPurchase({ id: currentOrderId, items: cart, value: totalComEntrega }); clarityEvento("pedido_feito");
    onOrderComplete({ id: currentOrderId });
    onClose();
  };

  // Confirmar troco e finalizar
  const handleConfirmChange = () => {
    if (needsChange && (!changeAmount || parseFloat(changeAmount) <= totalFinal)) {
      toast.error("Informe um valor maior que o total do pedido");
      return;
    }
    setShowChangeDialog(false);
    finalizeOrder();
  };

  const handleFinishOrder = () => {
    if (!paymentMethod) {
      toast.error("Selecione uma forma de pagamento");
      return;
    }
    
    // Verificar se é PIX Online (verificação mais robusta)
    const selectedMethod = availablePaymentMethods.find(m => m.id === paymentMethod);
    const isPixOnline = paymentMethod === 'pix_online' || selectedMethod?.code === 'pix_online';
    
    // PIX Online é tratado separadamente
    if (isPixOnline) {
      return; // Não deve chegar aqui, mas por segurança
    }
    
    // Se for dinheiro e ainda não respondeu sobre troco — detecta por id/code/label
    const methodObj = (paymentMethods || []).find(m => m.id === paymentMethod || m.code === paymentMethod) || {};
    const pmIsDinheiro = String(paymentMethod || '').toLowerCase() === 'cash'
      || String(paymentMethod || '').toLowerCase().includes('dinheiro')
      || String(methodObj.code || '').toLowerCase().includes('dinheiro')
      || String(methodObj.label || '').toLowerCase().includes('dinheiro');
    if (pmIsDinheiro && needsChange === null) {
      setShowChangeDialog(true);
      return;
    }
    
    finalizeOrder();
  };

  const finalizeOrder = async () => {
    // Proteção contra duplo clique
    if (isSubmitting) {
      return;
    }

    // Validar carrinho
    if (!cart || cart.length === 0) {
      toast.error("Carrinho vazio");
      return;
    }

    const selectedAddr = addresses.find(a => a.id === selectedAddress);

    // Mapear forma de pagamento para o formato esperado
    // Mapear ID do frontend para código do backend
    const selectedMethod = paymentMethods.find(m => m.id === paymentMethod);
    const backendCode = selectedMethod?.code || (() => {
      // Fallback para mapeamento antigo
      const fallbackMap = {
        'pix': 'pix',
        'credit': 'credito',
        'debit': 'debito',
        'cash': 'dinheiro'
      };
      return fallbackMap[paymentMethod] || paymentMethod;
    })();

    // Guarda defensiva: se mesmo com o fallback o code ficou vazio, bloqueia
    // o checkout. Backend também rejeita (defesa em profundidade), mas aqui
    // damos uma mensagem mais clara antes da request.
    if (!backendCode || !String(backendCode).trim()) {
      toast.error("Selecione uma forma de pagamento válida");
      return;
    }

    // Criar pedido via API (banco de dados)
    const pedidoData = {
      cliente_id: client.id || null,
      cliente_nome: client.nome,
      cliente_telefone: client.telefone || client.phone,
      cliente_email: client.email,
      items: cart.map(item => ({
        id: item.id,
        product_id: item.id,
        nome: item.name,
        quantidade: item.quantity,
        preco: item.final_price || item.sale_price,
        observacao: item.observation || null,
        photo_url: item.photo_url || null,
        combo_type: item.combo_type || null,
        subitems: getItemSubitemsHelper(item),
        etapas: getItemStepsSummaryHelper(item),
        is_promotional: isItemPromotional(item),
        oferta_chave: item.oferta_chave || null,
        sugestao_lugar: item.sugestao_lugar || undefined,
        sugestao_qtd: item.sugestao_qtd || undefined,
        pizza_meta: item.pizza ? {
          size_id: item.pizza.size_id,
          size_name: item.pizza.size_name,
          flavor_ids: item.pizza.flavor_ids || [],
          border_id: item.pizza.border_id || null,
          border_name: item.pizza.border_name || null,
          pricing_rule: item.pizza.pricing_rule || null,
        } : null,
      })),
      total: totalComEntrega,
      forma_pagamento: backendCode,
      troco_precisa: (() => {
        const mo = (paymentMethods || []).find(m => m.id === paymentMethod || m.code === paymentMethod) || {};
        const isDin = String(paymentMethod || '').toLowerCase() === 'cash' || String(paymentMethod || '').toLowerCase().includes('dinheiro') || String(mo.code || '').toLowerCase().includes('dinheiro') || String(mo.label || '').toLowerCase().includes('dinheiro');
        return isDin ? needsChange : false;
      })(),
      troco_valor: (() => {
        const mo = (paymentMethods || []).find(m => m.id === paymentMethod || m.code === paymentMethod) || {};
        const isDin = String(paymentMethod || '').toLowerCase() === 'cash' || String(paymentMethod || '').toLowerCase().includes('dinheiro') || String(mo.code || '').toLowerCase().includes('dinheiro') || String(mo.label || '').toLowerCase().includes('dinheiro');
        return isDin && needsChange ? parseFloat(changeAmount) : null;
      })(),
      tipo_entrega: deliveryType,
      endereco_label: deliveryType === 'delivery' && selectedAddr ? selectedAddr.label : null,
      endereco_rua: deliveryType === 'delivery' && selectedAddr ? selectedAddr.endereco : null,
      endereco_numero: deliveryType === 'delivery' && selectedAddr ? selectedAddr.numero : null,
      endereco_complemento: deliveryType === 'delivery' && selectedAddr ? selectedAddr.complemento : null,
      endereco_bairro: deliveryType === 'delivery' && selectedAddr ? selectedAddr.bairro : null,
      endereco_cep: deliveryType === 'delivery' && selectedAddr ? selectedAddr.cep : null,
      modulo: "Cardapio",
      sms_ref: obterSmsRef(),
      origem_anuncio: obterOrigemAnuncio(),
      valor_entrega: deliveryType === 'delivery' ? valorEntrega : 0,
      cupom_codigo: cupomInfo && cupomInfo.valid ? cupomCodigo : undefined,
      cupom_desconto: cupomInfo && cupomInfo.valid ? Number(cupomInfo.discount || 0) : undefined,
      cashback_usado: cashbackAplicado > 0 ? cashbackAplicado : undefined,
      // Recuperador de Vendas: pedido que veio do link da conversa com a CLEO.
      recuperador_token: tokenDaConversa() || undefined,
      scheduled_at: scheduledAt || undefined,
    };

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/pedidos`, pedidoData);
      const newPedido = response.data;

      // Disparar evento para atualizar outras abas/componentes
      window.dispatchEvent(new Event('pedidosUpdated'));

      toast.success(`Pedido ${newPedido.codigo} realizado com sucesso! 🎉`);
      trackPurchase({ codigo: newPedido.codigo, id: newPedido.id, items: cart, value: totalComEntrega }); clarityEvento("pedido_feito");
      if (cashbackAplicado > 0) onCashbackConsumed();
      onOrderComplete(newPedido);
      onClose();
    } catch (error) {
      // 409 = preço mudou (inclusive oferta que expirou): rebusca as ofertas do dia.
      if (error?.response?.status === 409) onOfertasDesatualizadas();
      console.error("Erro ao criar pedido:", error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      // TURNO: 409 com `X-Nucleo-Reason: shift` é o item que saiu do cardápio
      // deste horário. Diferente dos outros 409 ("atualize a página"), aqui o
      // cliente precisa REMOVER o item — então volta pro carrinho em vez de
      // ficar preso na tela de checkout relendo a mesma mensagem.
      if (status === 409 && error?.response?.headers?.['x-nucleo-reason'] === 'shift') {
        toast.error(detail || 'Esse item não faz parte do cardápio deste horário.', { duration: 9000 });
        onClose();
        return;
      }
      if (status === 403 && detail) {
        // Mensagens de negocio do backend: loja fechada, fora de horario, etc.
        toast.error(detail, { duration: 6000 });
      } else if (detail) {
        toast.error(detail);
      } else {
        toast.error("Erro ao criar pedido. Tente novamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const t = {
    bg: darkMode ? 'bg-zinc-900' : 'bg-white',
    bgCard: darkMode ? 'bg-zinc-800' : 'bg-gray-50',
    bgMuted: darkMode ? 'bg-zinc-800' : 'bg-gray-100',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textMuted: darkMode ? 'text-zinc-400' : 'text-gray-500',
    border: darkMode ? 'border-zinc-700' : 'border-gray-200',
    input: darkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900',
  };

  // Usar formas de pagamento do backend (ou fallback se vazio)
  const paymentMethodsRaw = availablePaymentMethods.length > 0
    ? availablePaymentMethods
    : [
        { id: 'pix', code: 'pix', label: 'PIX', icon: QrCode, color: 'text-teal-500' },
        { id: 'credit', code: 'credito', label: 'Cartão de Crédito', icon: CreditCard, color: 'text-blue-500' },
        { id: 'debit', code: 'debito', label: 'Cartão de Débito', icon: CreditCard, color: 'text-purple-500' },
        { id: 'cash', code: 'dinheiro', label: 'Dinheiro', icon: Banknote, color: 'text-green-500' },
      ];
  // Whitelist configurada pelo lojista pra AGENDAMENTOS (semântica v1.5.278+):
  //   - null → dono nunca configurou; aceita todas
  //   - []   → dono desmarcou tudo; NENHUMA aparece
  //   - [x,y] → whitelist explícita
  // Aplica só quando o pedido é agendado (scheduledAt setado).
  const paymentMethods = (() => {
    if (!scheduledAt) return paymentMethodsRaw;
    const wl = schedulingConfig?.formas_pagamento;
    if (wl === null || wl === undefined) return paymentMethodsRaw; // nunca configurou
    const wlNorm = wl.map((c) => String(c).toLowerCase());
    return paymentMethodsRaw.filter((m) => {
      const c = String(m.code || m.id || "").toLowerCase();
      return wlNorm.includes(c);
    });
  })();

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent data-clarity-mask="true" side="bottom" hideClose className={`p-0 gap-0 overflow-hidden ${t.bg} ${t.text} border-0 max-h-[95vh] h-[95vh] rounded-t-2xl max-w-[480px] mx-auto inset-x-0 flex flex-col`}>
        <SheetTitle className="sr-only">Checkout</SheetTitle>
        {/* Handle visual */}
        <div className="flex justify-center pt-2 pb-1 bg-orange-500 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-white/40" />
        </div>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-orange-500 p-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {step > (agendamentoRequerido ? 0 : 1) && (
              <button
                onClick={() => setStep(step === 3 && deliveryType === 'pickup' ? 1 : step - 1)}
                className="text-white hover:bg-white/20 p-1 rounded-full"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            <h2 className="text-xl font-bold text-white">
              {step === 0 && "Agendar Pedido"}
              {step === 1 && "Tipo de Entrega"}
              {step === 2 && "Endereço de Entrega"}
              {step === 3 && "Forma de Pagamento"}
            </h2>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Steps Indicator — inclui bolinha 0 (agendamento) quando requerido */}
        <div className="flex items-center justify-center gap-2 p-4 border-b border-orange-200/20 flex-shrink-0">
          {(agendamentoRequerido ? [0, 1, 2, 3] : [1, 2, 3]).map((s, idx, arr) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                ${step >= s ? 'bg-orange-500 text-white' : `${t.bgMuted} ${t.textMuted}`}
              `}>
                {step > s ? <Check className="w-4 h-4" /> : (s === 0 ? <Calendar className="w-4 h-4" /> : s)}
              </div>
              {idx < arr.length - 1 && <div className={`w-8 h-0.5 ${step > s ? 'bg-orange-500' : t.bgMuted}`} />}
            </div>
          ))}
        </div>

        {/* Mini barra de progresso de cupom — oculta no fluxo de agendamento
            (cliente não tá comprando "pra agora" — cupom/frete grátis é distração). */}
        {!agendamentoRequerido && !scheduledAt && (
          <CouponMiniBar
            visibleCoupons={visibleCoupons}
            selectedCouponCode={selectedCouponCode}
            couponableSubtotal={couponableBase}
            darkMode={darkMode}
          />
        )}

        {/* Carteira do cliente (v1.5.316+) — barra slim visível em TODAS as etapas
            quando cashback está ativo e cliente logado. No step 3 o cliente pode
            optar por USAR via switch (vide abaixo). */}
        {cashbackConfig.ativo && client && cashbackSaldo > 0 && (
          <div className={`px-3 py-2 flex items-center justify-center gap-2 ${darkMode ? 'bg-emerald-900/20 border-b border-emerald-800/40' : 'bg-emerald-50 border-b border-emerald-200'}`}>
            <Coins className={`w-4 h-4 shrink-0 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <span className={`text-[12px] font-semibold ${darkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
              Sua carteira: <strong className={darkMode ? 'text-emerald-200' : 'text-emerald-900'}>R$ {cashbackSaldo.toFixed(2).replace('.', ',')}</strong>
              {cashbackAplicado > 0 && (
                <>
                  {' · '}usando <strong className={darkMode ? 'text-emerald-200' : 'text-emerald-900'}>R$ {cashbackAplicado.toFixed(2).replace('.', ',')}</strong>
                </>
              )}
            </span>
          </div>
        )}

        {/* Conteudo das etapas — scroll interno para telas pequenas */}
        <div className="flex-1 overflow-y-auto overscroll-contain">

        {/* Step 0: Agendamento — só quando loja fechada + agendamento ativo */}
        {step === 0 && (
          <SchedulePickerStep
            t={t}
            darkMode={darkMode}
            scheduledAt={scheduledAt}
            onConfirm={(iso) => {
              setScheduledAt(iso);
              setStep(1);
            }}
            windowDays={schedulingConfig.janela_dias_max || 7}
          />
        )}

        {/* Step 1: Tipo de Entrega */}
        {step === 1 && (
          <div className="p-6 space-y-4">
            {scheduledAt && <ScheduledSummary iso={scheduledAt} t={t} onEdit={() => setStep(0)} />}
            <p className={`${t.textMuted} text-center mb-6`}>Como você prefere receber seu pedido?</p>

            <button
              onClick={() => handleSelectDeliveryType('delivery')}
              className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4
                ${deliveryType === 'delivery'
                  ? 'border-orange-500 bg-orange-500/10'
                  : `${t.border} hover:border-orange-300`
                }
              `}
            >
              <div className="w-14 h-14 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                <Truck className="w-7 h-7 text-orange-500" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <h3 className={`font-bold text-lg ${t.text}`}>Entrega</h3>
                <p className={`text-sm ${t.textMuted}`}>Receba no conforto da sua casa</p>
                {/* Meta: frete e tempo medio - scoped por tenant via stats prop */}
                {(stats.delivery_fee_min != null || stats.avg_order_time_min != null) && (
                  <div className={`flex items-center gap-3 mt-2 pt-2 border-t ${t.border} text-[11px] flex-wrap`}>
                    {stats.delivery_fee_min != null && (
                      <span className="flex items-center gap-1">
                        <Bike className={`w-3.5 h-3.5 ${t.textMuted}`} />
                        <span className={t.textMuted}>
                          <span className={`font-semibold ${t.text}`}>
                            {stats.delivery_fee_min === stats.delivery_fee_max
                              ? `R$ ${stats.delivery_fee_min.toFixed(2).replace('.', ',')}`
                              : `R$ ${stats.delivery_fee_min.toFixed(2).replace('.', ',')} – R$ ${stats.delivery_fee_max.toFixed(2).replace('.', ',')}`}
                          </span>
                          <span className="ml-1">entrega</span>
                        </span>
                      </span>
                    )}
                    {stats.avg_order_time_min != null && (
                      <span className="flex items-center gap-1">
                        <Clock className={`w-3.5 h-3.5 ${t.textMuted}`} />
                        <span className={t.textMuted}>
                          <span className={`font-semibold ${t.text}`}>
                            {Math.max(1, stats.avg_order_time_min)} min
                          </span>
                          <span className="ml-1">médio</span>
                        </span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </button>

            <button
              onClick={() => handleSelectDeliveryType('pickup')}
              className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4
                ${deliveryType === 'pickup'
                  ? 'border-orange-500 bg-orange-500/10'
                  : `${t.border} hover:border-orange-300`
                }
              `}
            >
              <div className="w-14 h-14 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                <Store className="w-7 h-7 text-orange-500" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <h3 className={`font-bold text-lg ${t.text}`}>Retirar no Local</h3>
                <p className={`text-sm ${t.textMuted}`}>Retire seu pedido no estabelecimento</p>
              </div>
            </button>
          </div>
        )}

        {/* Step 2: Endereço */}
        {step === 2 && (
          <div className="p-6 space-y-4">
            {scheduledAt && <ScheduledSummary iso={scheduledAt} t={t} onEdit={() => setStep(0)} />}
            {loadingAddresses ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : (
              <>
                {/* Botão para novo endereço - sempre a primeira opcao */}
                {!showNewAddressForm && (
                  <button
                    onClick={() => setShowNewAddressForm(true)}
                    className={`w-full p-4 rounded-xl border-2 border-dashed ${t.border} hover:border-orange-500 transition-all flex items-center justify-center gap-2 ${t.textMuted} mb-3`}
                  >
                    <Plus className="w-5 h-5" />
                    Cadastrar novo endereço
                  </button>
                )}

                {/* Endereços salvos */}
                {addresses.length > 0 && !showNewAddressForm && (
                  <div className="space-y-3">
                    <p className={`${t.textMuted} text-sm`}>Ou selecione um endereço salvo:</p>
                    {addresses.map(addr => {
                      const bairro = bairros.find(b => (b.nome || '').trim().toLowerCase() === (addr.bairro || '').trim().toLowerCase());
                      const addrFee = bairro?.valor_entrega;
                      return (
                        <div
                          key={addr.id}
                          className={`relative w-full p-4 rounded-xl border-2 transition-all text-left cursor-pointer
                            ${selectedAddress === addr.id
                              ? 'border-orange-500 bg-orange-500/10'
                              : `${t.border} hover:border-orange-300`
                            }
                          `}
                          onClick={() => {
                            setSelectedAddress(addr.id);
                            setValorEntrega(addrFee || 0);
                          }}
                        >
                          {/* Botão de deletar */}
                          <button
                            onClick={(e) => handleDeleteAddressClick(e, addr)}
                            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center transition-colors z-10"
                            title="Remover endereço"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>

                          <div className="flex items-start gap-3 pr-8">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${selectedAddress === addr.id ? 'bg-orange-500' : t.bgMuted}`}>
                              {addr.label === 'Casa' ? <Home className={`w-5 h-5 ${selectedAddress === addr.id ? 'text-white' : t.textMuted}`} />
                              : <Building className={`w-5 h-5 ${selectedAddress === addr.id ? 'text-white' : t.textMuted}`} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold">{addr.label}</span>
                                {addr.is_default && <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">Padrão</span>}
                              </div>
                              <p className={`text-sm ${t.textMuted} mt-1`}>
                                {addr.endereco}, {addr.numero} {addr.complemento && `- ${addr.complemento}`}
                              </p>
                              <p className={`text-sm ${t.textMuted}`}>{addr.bairro} - {addr.cep}</p>
                              {/* Valor de entrega do bairro */}
                              <div className={`flex items-center gap-1.5 mt-2 pt-2 border-t ${t.border} text-xs`}>
                                <Bike className={`w-3.5 h-3.5 ${t.textMuted}`} />
                                {addrFee != null && addrFee > 0 ? (
                                  <span className={t.textMuted}>
                                    Taxa de entrega <span className={`font-bold ${t.text}`}>R$ {Number(addrFee).toFixed(2).replace('.', ',')}</span>
                                  </span>
                                ) : addrFee === 0 ? (
                                  <span className="font-bold text-green-500">Entrega grátis</span>
                                ) : (
                                  <span className="text-red-500 font-medium">Bairro fora da area de entrega</span>
                                )}
                              </div>
                            </div>
                            {selectedAddress === addr.id && (
                              <Check className="w-6 h-6 text-orange-500 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Form de novo endereço */}
                {showNewAddressForm && (
                  <div className={`p-4 rounded-xl ${t.bgCard} space-y-4`}>
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">Novo Endereço</h3>
                      <button onClick={() => setShowNewAddressForm(false)} className={t.textMuted}>
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setNewAddress(prev => ({ ...prev, label: 'Casa' }))}
                        className={`p-2 rounded-lg border text-sm font-medium transition-all
                          ${newAddress.label === 'Casa' ? 'border-orange-500 bg-orange-500/10 text-orange-500' : `${t.border}`}
                        `}
                      >
                        🏠 Casa
                      </button>
                      <button
                        onClick={() => setNewAddress(prev => ({ ...prev, label: 'Trabalho' }))}
                        className={`p-2 rounded-lg border text-sm font-medium transition-all
                          ${newAddress.label === 'Trabalho' ? 'border-orange-500 bg-orange-500/10 text-orange-500' : `${t.border}`}
                        `}
                      >
                        🏢 Trabalho
                      </button>
                    </div>

                    {/* Campo Rua com autocomplete */}
                    <div className="relative">
                      <Input
                        placeholder="Rua, Avenida..."
                        value={newAddress.endereco}
                        onChange={e => { handleRuaChange(e.target.value); if (addressErrors.endereco) setAddressErrors(prev => ({ ...prev, endereco: undefined })); }}
                        className={`${t.input} ${addressErrors.endereco ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                        aria-invalid={!!addressErrors.endereco}
                      />
                      {addressErrors.endereco && <p className="text-xs text-red-500 mt-1">{addressErrors.endereco}</p>}
                      {ruasSugestoes.length > 0 && (
                        <div className={`absolute z-50 w-full mt-1 max-h-48 overflow-auto rounded-lg border shadow-lg ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                          {ruasSugestoes.map(rua => (
                            <button
                              key={rua.id}
                              onClick={() => handleSelectRua(rua)}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-orange-500/10 transition-colors ${darkMode ? 'text-white' : 'text-gray-900'}`}
                            >
                              <span className="font-medium">{rua.nome}</span>
                              {rua.bairro_nome && <span className="text-gray-500 ml-2">- {rua.bairro_nome}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Input
                          placeholder="Número"
                          value={newAddress.numero}
                          onChange={e => { setNewAddress(prev => ({ ...prev, numero: e.target.value })); if (addressErrors.numero) setAddressErrors(prev => ({ ...prev, numero: undefined })); }}
                          className={`${t.input} ${addressErrors.numero ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                          aria-invalid={!!addressErrors.numero}
                        />
                        {addressErrors.numero && <p className="text-xs text-red-500 mt-1">{addressErrors.numero}</p>}
                      </div>
                      <Input
                        placeholder="Complemento"
                        value={newAddress.complemento}
                        onChange={e => setNewAddress(prev => ({ ...prev, complemento: e.target.value }))}
                        className={t.input}
                      />
                    </div>

                    {/* Campo Bairro - Com busca por digitação (só aceita cadastrados) */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <div className="relative">
                          <Input
                            placeholder="Digite ou selecione o bairro *"
                            value={newAddress.bairro}
                            onChange={e => {
                              setNewAddress(prev => ({ ...prev, bairro: e.target.value }));
                              setShowBairroDropdown(true);
                              if (addressErrors.bairro) setAddressErrors(prev => ({ ...prev, bairro: undefined }));
                            }}
                            onFocus={() => setShowBairroDropdown(true)}
                            className={`${t.input} ${addressErrors.bairro ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                            aria-invalid={!!addressErrors.bairro}
                          />
                          <button
                            type="button"
                            onClick={() => setShowBairroDropdown(!showBairroDropdown)}
                            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors`}
                            title="Ver todos os bairros"
                          >
                            <ChevronDown className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'} transition-transform ${showBairroDropdown ? 'rotate-180' : ''}`} />
                          </button>
                        </div>
                        {showBairroDropdown && (
                          <>
                            {/* Overlay para fechar ao clicar fora */}
                            <div className="fixed inset-0 z-40" onClick={() => setShowBairroDropdown(false)} />
                            <div className={`absolute z-50 w-full mt-1 max-h-48 overflow-auto rounded-lg border shadow-lg ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                              {bairros.length === 0 ? (
                                <div className={`px-3 py-4 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Nenhum bairro cadastrado
                                </div>
                              ) : (
                                <>
                                  {bairros
                                    .filter(b => !newAddress.bairro || b.nome.trim().toLowerCase().includes(newAddress.bairro.trim().toLowerCase()))
                                    .map(bairro => (
                                      <button
                                        key={bairro.id}
                                        onClick={() => handleSelectBairro(bairro)}
                                        className={`w-full px-3 py-2 text-left text-sm hover:bg-orange-500/10 transition-colors flex items-center justify-between ${darkMode ? 'text-white' : 'text-gray-900'} ${newAddress.bairro.trim().toLowerCase() === bairro.nome.trim().toLowerCase() ? 'bg-orange-500/20' : ''}`}
                                      >
                                        <span className="font-medium">{bairro.nome}</span>
                                        {bairro.valor_entrega > 0 && (
                                          <span className="text-green-500 text-xs">+R$ {bairro.valor_entrega.toFixed(2).replace(".", ",")}</span>
                                        )}
                                      </button>
                                    ))
                                  }
                                  {bairros.filter(b => !newAddress.bairro || b.nome.trim().toLowerCase().includes(newAddress.bairro.trim().toLowerCase())).length === 0 && (
                                    <div className={`px-3 py-3 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Nenhum bairro encontrado
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </>
                        )}
                        {addressErrors.bairro && <p className="text-xs text-red-500 mt-1">{addressErrors.bairro}</p>}
                      </div>
                      <Input
                        placeholder="CEP"
                        value={cepUnicoAtivo ? cepUnicoValue : newAddress.cep}
                        onChange={e => !cepUnicoAtivo && setNewAddress(prev => ({ ...prev, cep: e.target.value }))}
                        className={`${t.input} ${cepUnicoAtivo ? 'opacity-70 cursor-not-allowed' : ''}`}
                        readOnly={cepUnicoAtivo}
                      />
                    </div>

                    {/* Mostrar valor de entrega do bairro */}
                    {valorEntrega > 0 && (
                      <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
                          <Truck className="w-4 h-4" />
                          Taxa de entrega: <strong>R$ {valorEntrega.toFixed(2).replace(".", ",")}</strong>
                        </p>
                      </div>
                    )}

                    {/* Opcao de persistir endereco — default ON pra que o cliente nao
                        precise redigitar nas proximas compras. Pode desmarcar (privacidade
                        / dispositivo compartilhado) e usar so neste pedido. */}
                    <label className={`flex items-center gap-2.5 cursor-pointer select-none p-2 -mx-2 rounded-lg ${darkMode ? 'hover:bg-zinc-700/50' : 'hover:bg-gray-50'}`}>
                      <input
                        type="checkbox"
                        checked={saveAddressForNext}
                        onChange={e => setSaveAddressForNext(e.target.checked)}
                        className="w-4 h-4 rounded accent-orange-500 cursor-pointer"
                      />
                      <span className={`text-xs ${t.textMuted} flex-1`}>
                        💾 Salvar este endereço para próximas compras
                      </span>
                    </label>

                    <Button
                      onClick={handleSaveNewAddress}
                      disabled={savingAddress}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      {savingAddress ? "Salvando..." : (saveAddressForNext ? "Salvar Endereço" : "Usar neste pedido")}
                    </Button>
                  </div>
                )}

                {/* Botão continuar */}
                {!showNewAddressForm && selectedAddress && (
                  <Button
                    onClick={handleConfirmAddress}
                    className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold mt-4"
                  >
                    Continuar para Pagamento
                  </Button>
                )}
              </>
            )}
          </div>
        )}

        {/* Step 3: Pagamento */}
        {step === 3 && (
          <div className="p-6 space-y-4">
            {scheduledAt && <ScheduledSummary iso={scheduledAt} t={t} onEdit={() => setStep(0)} />}
            {/* Resumo */}
            <div className={`p-4 rounded-xl ${t.bgCard}`}>
              <div className="flex items-center gap-2 mb-2">
                {deliveryType === 'pickup' ? <Store className="w-5 h-5 text-orange-500" /> : <Truck className="w-5 h-5 text-orange-500" />}
                <span className="font-semibold">{deliveryType === 'pickup' ? 'Retirada no Local' : 'Entrega'}</span>
              </div>
              {deliveryType === 'delivery' && selectedAddress && (
                <p className={`text-sm ${t.textMuted}`}>
                  <MapPin className="w-4 h-4 inline mr-1" />
                  {addresses.find(a => a.id === selectedAddress)?.endereco}, {addresses.find(a => a.id === selectedAddress)?.numero}
                </p>
              )}
            </div>

            {/* v1.5.358+: aviso consolidado quando "Peça de novo" recalculou
                preço de pizza pelo pricing_rule atual (o valor do pedido antigo
                estava desatualizado). Mostra cada item afetado com de/por. */}
            {(cart || []).some(it => it._price_updated) && (
              <div className="p-3 rounded-xl border-2 border-amber-400 bg-amber-500/10">
                <p className="text-amber-700 dark:text-amber-300 font-bold text-sm flex items-center gap-1.5 mb-1.5">
                  ⚠️ Preço atualizado
                </p>
                <p className="text-[11px] text-amber-700/90 dark:text-amber-300/90 mb-2">
                  O preço de item(ns) do "Peça de novo" mudou desde a última compra. Confira antes de finalizar:
                </p>
                <ul className="space-y-1">
                  {(cart || []).filter(it => it._price_updated).map((it) => (
                    <li key={it.cartItemId || it.id} className="text-[11px] text-amber-800 dark:text-amber-200">
                      • <strong>{it.name}</strong>: R$ {it._price_updated.oldPrice.toFixed(2).replace('.', ',')} → <strong>R$ {it._price_updated.newPrice.toFixed(2).replace('.', ',')}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className={`${t.textMuted} text-sm`}>Selecione a forma de pagamento:</p>

            {/* Métodos de pagamento */}
            <div className="space-y-3">
              {/* Pagamentos online - Destaque no topo */}
              {paymentMethods.filter(m => m.id === 'pix_online' || m.code === 'pix_online').map(method => (
                <button
                  key={method.id}
                  onClick={() => handleSelectPayment(method.id)}
                  className="w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 relative"
                  style={{
                    borderColor: 'hsl(var(--primary))',
                    backgroundColor: paymentMethod === method.id ? 'hsl(var(--primary) / 0.1)' : 'hsl(var(--primary) / 0.05)',
                  }}
                >
                  <div className="absolute -top-2 right-3 px-2 py-0.5 text-white text-xs font-bold rounded-full" style={{ backgroundColor: 'hsl(var(--primary))' }}>
                    Recomendado
                  </div>
                  <method.icon className="w-6 h-6" style={{ color: 'hsl(var(--primary))' }} />
                  <div className="flex-1 text-left">
                    <span className="font-semibold">{method.label}</span>
                    <p className={`text-xs ${t.textMuted}`}>Pagamento instantaneo via QR Code</p>
                  </div>
                  {paymentMethod === method.id && <Check className="w-5 h-5" style={{ color: 'hsl(var(--primary))' }} />}
                </button>
              ))}

              {/* Cartao Online */}
              {paymentMethods.filter(m => m.id === 'cartao_online' || m.code === 'cartao_online').map(method => (
                <button
                  key={method.id}
                  onClick={() => handleSelectPayment(method.id)}
                  className="w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4"
                  style={{
                    borderColor: 'hsl(var(--primary))',
                    backgroundColor: paymentMethod === method.id ? 'hsl(var(--primary) / 0.1)' : 'hsl(var(--primary) / 0.05)',
                  }}
                >
                  <method.icon className="w-6 h-6" style={{ color: 'hsl(var(--primary))' }} />
                  <div className="flex-1 text-left">
                    <span className="font-semibold">{method.label}</span>
                    <p className={`text-xs ${t.textMuted}`}>Credito ou debito via Mercado Pago</p>
                  </div>
                  {paymentMethod === method.id && <Check className="w-5 h-5" style={{ color: 'hsl(var(--primary))' }} />}
                </button>
              ))}

              {/* Separador visual */}
              {paymentMethods.some(m => ['pix_online', 'cartao_online'].includes(m.id) || ['pix_online', 'cartao_online'].includes(m.code)) && (
                <div className="flex items-center gap-3 py-1">
                  <div className={`flex-1 h-px ${t.border}`}></div>
                  <span className={`text-xs ${t.textMuted}`}>Outras opções</span>
                  <div className={`flex-1 h-px ${t.border}`}></div>
                </div>
              )}

              {/* Outros metodos de pagamento */}
              {paymentMethods.filter(m => !['pix_online', 'cartao_online'].includes(m.id) && !['pix_online', 'cartao_online'].includes(m.code)).map(method => (
                <button
                  key={method.id}
                  onClick={() => handleSelectPayment(method.id)}
                  className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4
                    ${paymentMethod === method.id
                      ? ''
                      : `${t.border} hover:border-gray-400`
                    }
                  `}
                  style={paymentMethod === method.id ? {
                    borderColor: 'hsl(var(--primary))',
                    backgroundColor: 'hsl(var(--primary) / 0.1)',
                  } : {}}
                >
                  <method.icon className={`w-6 h-6 ${method.color}`} />
                  <span className="font-medium flex-1 text-left">{method.label}</span>
                  {paymentMethod === method.id && <Check className="w-5 h-5" style={{ color: 'hsl(var(--primary))' }} />}
                </button>
              ))}
            </div>

            {/* Info troco se selecionou dinheiro */}
            {paymentMethod === 'cash' && needsChange !== null && (
              <div className={`p-3 rounded-lg ${t.bgCard} border ${t.border}`}>
                <p className={`text-sm ${t.textMuted}`}>
                  💵 {needsChange 
                    ? `Troco para R$ ${parseFloat(changeAmount).toFixed(2).replace('.', ',')}` 
                    : 'Não precisa de troco'}
                </p>
              </div>
            )}

            {/* Cupom na etapa final: cupom aplicado (com Remover), lista de elegiveis ou input manual */}
            <div className="mt-4 space-y-2">
              <AvisoCupom nomes={nomesSemCupom(cart, activePromotions)} baseCupom={couponableBase} darkMode={darkMode} />
              {cupomInfo && cupomInfo.valid ? (
                <div className={`flex items-center justify-between gap-2 p-2.5 rounded-xl border border-green-500/30 bg-green-500/10`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                      {cupomInfo?.coupon?.discount_type === 'free_delivery' ? (
                        <MotoHelmet className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                      ) : (
                        <Gift className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-green-700 dark:text-green-400 truncate">
                        {cupomInfo?.coupon?.discount_type === 'free_delivery' ? `Frete grátis (${cupomCodigo})` : `Cupom ${cupomCodigo}`}
                      </p>
                      <p className="text-[10px] text-muted-foreground">−R$ {Number(cupomInfo.discount || 0).toFixed(2).replace('.', ',')}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setCupomInfo(null);
                      setCupomCodigo("");
                      setShowCupomInput(false);
                      // Limpa a selecao vinda do modal para que o auto-apply nao reaplique
                      if (onSelectCoupon) onSelectCoupon(null);
                      autoAppliedRef.current = true; // bloqueia auto-reapply apos remocao manual
                    }}
                    className="text-[11px] text-red-500 hover:text-red-600 font-semibold px-2 py-1"
                  >
                    Remover
                  </button>
                </div>
              ) : (
                <>
                  {/* Lista de cupons elegiveis para o cliente escolher manualmente */}
                  {(() => {
                    const eligibles = (visibleCoupons || []).filter(c => couponableBase >= Number(c.min_order_value || 0) && premioCabeNaSacola(c, cart));
                    if (eligibles.length === 0) return null;
                    return (
                      <div className="space-y-1.5">
                        <p className={`text-[11px] font-semibold ${t.textMuted}`}>Cupons disponíveis para esse pedido:</p>
                        {eligibles.map((c) => {
                          const cIsFree = c.discount_type === 'free_delivery';
                          const est = cIsFree
                            ? (deliveryType === 'delivery' ? (valorEntrega || 0) : 0)
                            : c.discount_type === 'percentage'
                              ? (() => {
                                  const raw = couponableBase * (Number(c.discount_value || 0) / 100);
                                  const cap = Number(c.max_discount_value || 0);
                                  return cap > 0 ? Math.min(raw, cap) : raw;
                                })()
                              : Number(c.discount_value || 0);
                          const cVal = cIsFree
                            ? 'FRETE GRÁTIS'
                            : c.discount_type === 'percentage'
                              ? `${Number(c.discount_value).toFixed(0)}% OFF`
                              : `R$ ${Number(c.discount_value).toFixed(2).replace('.', ',')} OFF`;
                          // Aplica o cupom — quando ja ha um diferente aplicado, pede confirmacao
                          // antes de trocar (especialmente importante quando troca um desconto em
                          // valor por frete gratis ou vice-versa, pois sao mutuamente exclusivos).
                          const applyCoupon = async () => {
                            const phone = (client?.telefone || client?.phone || "").trim();
                            setCupomValidating(true);
                            try {
                              const res = await axios.post(`${API}/coupons/validate`, {
                                code: c.code,
                                order_total: totalComEntrega,
                                products_total: couponableBase,
                                delivery_fee: (deliveryType === 'delivery' && valorEntrega > 0) ? valorEntrega : 0,
                                customer_phone: phone || null,
                                customer_id: client?.id || null,
                                modulo: "Cardapio",
                              });
                              if (res.data?.valid) {
                                setCupomCodigo(c.code);
                                setCupomInfo(res.data);
                                if (onSelectCoupon) onSelectCoupon(c.code);
                                toast.success(`Cupom ${c.code} aplicado: −R$ ${Number(res.data.discount || 0).toFixed(2).replace('.', ',')}`);
                              } else {
                                toast.error(res.data?.error || "Cupom inválido");
                              }
                            } catch (e) {
                              toast.error("Erro ao aplicar cupom");
                            } finally {
                              setCupomValidating(false);
                            }
                          };
                          const hasOtherCoupon = cupomInfo?.valid && cupomCodigo && cupomCodigo !== c.code;
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => {
                                if (hasOtherCoupon) {
                                  toast.warning(`Trocar cupom ${cupomCodigo} por ${c.code}?`, {
                                    description: "Só é possível usar um cupom por pedido.",
                                    duration: 8000,
                                    action: { label: "Trocar", onClick: applyCoupon },
                                  });
                                  return;
                                }
                                applyCoupon();
                              }}
                              disabled={cupomValidating}
                              className={`w-full flex items-center justify-between gap-2 p-2.5 rounded-xl border-2 ${t.border} hover:border-orange-500 hover:bg-orange-500/5 transition-colors text-left disabled:opacity-50`}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <div className={`w-7 h-7 rounded-full ${cIsFree ? 'bg-sky-500/15' : 'bg-orange-500/15'} flex items-center justify-center shrink-0`}>
                                  {cIsFree ? <MotoHelmet className="w-3.5 h-3.5 text-sky-500" /> : <Gift className="w-3.5 h-3.5 text-orange-500" />}
                                </div>
                                <div className="min-w-0">
                                  <p className={`text-xs font-bold ${t.text} truncate`}>{c.code} <span className={cIsFree ? 'text-sky-500' : 'text-orange-500'}>· {cVal}</span></p>
                                  <p className={`text-[10px] ${t.textMuted}`}>{cIsFree ? (est > 0 ? `Economize R$ ${est.toFixed(2).replace('.', ',')} no frete` : 'Frete grátis') : `Economize R$ ${est.toFixed(2).replace('.', ',')}`}</p>
                                </div>
                              </div>
                              <span className={`text-[11px] font-semibold shrink-0 ${cIsFree ? 'text-sky-500' : 'text-orange-500'}`}>Usar</span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {!showCupomInput ? (
                    <button
                      onClick={() => setShowCupomInput(true)}
                      className={`w-full flex items-center justify-between gap-2 p-2.5 rounded-xl border border-dashed ${darkMode ? 'border-purple-500/40 hover:bg-purple-500/10' : 'border-purple-500/40 hover:bg-purple-500/5'} transition-colors`}
                    >
                      <span className={`flex items-center gap-2 text-sm font-medium ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                        <Gift className="w-4 h-4" />
                        Use um CUPOM de desconto
                      </span>
                      <ChevronRight className="w-4 h-4 text-purple-500" />
                    </button>
                  ) : (
                <div className={`p-3 rounded-xl ${t.bgCard} space-y-2`}>
                  <div className="flex items-center justify-between">
                    <label className={`text-xs ${t.textMuted} font-semibold`}>Cupom de desconto</label>
                    <button
                      onClick={() => { setShowCupomInput(false); setCupomCodigo(""); setCupomInfo(null); }}
                      className="text-[11px] text-muted-foreground hover:text-foreground"
                    >
                      Fechar
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={cupomCodigo}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => {
                        setCupomCodigo(e.target.value.toUpperCase());
                        if (cupomInfo) setCupomInfo(null);
                      }}
                      placeholder="Digite o CUPOM"
                      className={`flex-1 h-10 px-3 rounded-lg border text-sm font-mono tracking-wider text-center uppercase ${t.text} ${t.bg}`}
                      autoFocus
                    />
                    <Button
                      onClick={async () => {
                        const code = (cupomCodigo || "").trim().toUpperCase();
                        if (!code) return;
                        // `client` é a prop do CheckoutModal; `loggedClient` só existe no
                        // componente de fora (01/11/2026: dava ReferenceError silencioso e
                        // o Aplicar do cupom digitado não fazia nada desde 23/04/2026).
                        const phone = (client?.telefone || client?.phone || "").trim();
                        setCupomValidating(true);
                        try {
                          const res = await axios.post(`${API}/coupons/validate`, {
                            code,
                            order_total: totalComEntrega,
                            products_total: couponableBase,
                            delivery_fee: (deliveryType === 'delivery' && valorEntrega > 0) ? valorEntrega : 0,
                            customer_phone: phone || null,
                            customer_id: client?.id || null,
                            modulo: "Cardapio",
                          });
                          if (res.data?.valid) {
                            setCupomInfo(res.data);
                            setShowCupomInput(false);
                            toast.success(`Cupom aplicado: -R$ ${Number(res.data.discount || 0).toFixed(2).replace(".", ",")}`);
                          } else {
                            setCupomInfo({ valid: false, error: res.data?.error });
                            toast.error(res.data?.error || "Cupom inválido");
                          }
                        } catch (err) {
                          const msg = err?.response?.data?.detail || "Erro ao validar cupom";
                          setCupomInfo({ valid: false, error: msg });
                          toast.error(msg);
                        } finally {
                          setCupomValidating(false);
                        }
                      }}
                      disabled={!cupomCodigo || cupomValidating}
                      className="h-10 bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {cupomValidating ? "..." : "Aplicar"}
                    </Button>
                  </div>
                  {cupomInfo && !cupomInfo.valid && cupomInfo.error && (
                    <p className="text-[11px] text-red-500">{cupomInfo.error}</p>
                  )}
                </div>
              )}
                </>
              )}
            </div>

            {/* Usar Cashback — switch opt-in (v1.5.316+). Só aparece quando
                cliente logado + config ativa + saldo > 0. Começa OFF por design;
                cliente precisa marcar pra descontar. Se cupom aplicado OU carrinho
                só tem promoções, switch fica desabilitado com mensagem explicando. */}
            {cashbackConfig.ativo && client && cashbackSaldo > 0 && (
              <div className={`p-3 rounded-xl border-2 ${useCashback ? (darkMode ? 'border-emerald-500 bg-emerald-500/10' : 'border-emerald-500 bg-emerald-50') : `${t.border} ${t.bgCard}`} ${cashbackDisponivel <= 0 ? 'opacity-70' : ''}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-9 h-9 rounded-full ${useCashback ? 'bg-emerald-500' : (darkMode ? 'bg-emerald-500/20' : 'bg-emerald-500/15')} flex items-center justify-center shrink-0`}>
                      <Coins className={`w-4 h-4 ${useCashback ? 'text-white' : 'text-emerald-500'}`} />
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-bold ${t.text}`}>Usar Cashback</p>
                      <p className={`text-[11px] ${t.textMuted}`}>
                        Saldo disponível: <strong className={darkMode ? 'text-emerald-300' : 'text-emerald-700'}>R$ {cashbackSaldo.toFixed(2).replace('.', ',')}</strong>
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={useCashback}
                    onCheckedChange={setUseCashback}
                    disabled={cashbackDisponivel <= 0}
                    className="data-[state=checked]:bg-emerald-500 shrink-0"
                  />
                </div>
                {useCashback && cashbackDisponivel > 0 && (
                  <p className={`text-[11px] mt-2 pt-2 border-t ${darkMode ? 'border-emerald-500/30 text-emerald-300' : 'border-emerald-200 text-emerald-700'}`}>
                    <strong>R$ {cashbackAplicado.toFixed(2).replace('.', ',')}</strong> aplicado neste pedido.
                  </p>
                )}
                {cashbackDisponivel <= 0 && (
                  <p className={`text-[11px] mt-2 pt-2 border-t ${darkMode ? 'border-zinc-700 text-zinc-400' : 'border-gray-200 text-gray-500'}`}>
                    {hasCoupon
                      ? 'Não é possível usar cashback junto com cupom no mesmo pedido.'
                      : 'Cashback não vale para produtos em promoção ou em oferta — adicione outros produtos para usar.'}
                  </p>
                )}
              </div>
            )}

            {sugestoes && <div className="mt-4" data-e2e="sugestoes-antes-de-finalizar">{sugestoes}</div>}

            {/* Total e Finalizar — versao compacta */}
            <div className={`p-3 rounded-xl ${t.bgCard} mt-4`}>
              <div className="space-y-1 mb-3 text-sm">
                <div className={`flex justify-between items-center ${t.textMuted}`}>
                  <span>Subtotal</span>
                  <span className="font-medium">R$ {cartTotal.toFixed(2).replace('.', ',')}</span>
                </div>
                {deliveryType === 'delivery' && valorEntrega > 0 && (
                  <div className={`flex justify-between items-center ${t.textMuted}`}>
                    <span>Taxa de entrega</span>
                    <span className="font-medium">R$ {valorEntrega.toFixed(2).replace('.', ',')}</span>
                  </div>
                )}
                {cupomInfo && cupomInfo.valid && (
                  <div className="flex justify-between items-center text-green-600 dark:text-green-400">
                    <span>{cupomInfo?.coupon?.discount_type === 'free_delivery' ? `Frete grátis (${cupomCodigo})` : `Cupom ${cupomCodigo}`}</span>
                    <span className="font-medium">−R$ {Number(cupomInfo.discount || 0).toFixed(2).replace('.', ',')}</span>
                  </div>
                )}
                {cashbackAplicado > 0 && (
                  <div className="flex justify-between items-center text-emerald-600 dark:text-emerald-400">
                    <span className="flex items-center gap-1"><Coins className="w-3.5 h-3.5" /> Cashback aplicado</span>
                    <span className="font-medium">−R$ {cashbackAplicado.toFixed(2).replace('.', ',')}</span>
                  </div>
                )}
                <div className="border-t pt-2 mt-1 flex justify-between items-center">
                  <span className={`${t.text} font-bold text-base`}>Total</span>
                  <span className="text-xl font-bold text-orange-500">
                    R$ {totalFinal.toFixed(2).replace('.', ',')}
                  </span>
                </div>
              </div>
              <Button
                onClick={() => {
                  const selectedMethod = availablePaymentMethods.find(m => m.id === paymentMethod);
                  const isPixOnline = paymentMethod === 'pix_online' || selectedMethod?.code === 'pix_online';
                  const isCartaoOnline = paymentMethod === 'cartao_online' || selectedMethod?.code === 'cartao_online';

                  if (isPixOnline) {
                    handlePixOnlinePayment();
                    return;
                  }
                  if (isCartaoOnline) {
                    handleCartaoOnlinePayment();
                    return;
                  }

                  handleFinishOrder();
                }}
                disabled={!paymentMethod || (paymentMethod === 'cash' && needsChange === null)}
                className="w-full h-14 bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg disabled:opacity-50"
              >
                {scheduledAt ? "Confirmar Agendamento" : "Finalizar Pedido"}
              </Button>
            </div>
          </div>
        )}

        </div>
        {/* /scroll interno das etapas */}

        {/* Dialog de Troco */}
        {showChangeDialog && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className={`${t.bg} rounded-2xl p-6 max-w-sm w-full shadow-xl`}>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Banknote className="w-8 h-8 text-green-600" />
                </div>
                <h3 className={`text-xl font-bold ${t.text}`}>Vai precisar de troco?</h3>
                <p className={`${t.textMuted} text-sm mt-2`}>
                  Total do pedido: <span className="font-bold text-orange-500">R$ {totalFinal.toFixed(2).replace('.', ',')}</span>
                </p>
                {cupomInfo && cupomInfo.valid && Number(cupomInfo.discount || 0) > 0 && (
                  <p className="text-[11px] text-green-600 dark:text-green-400 mt-1">
                    (cupom −R$ {Number(cupomInfo.discount).toFixed(2).replace('.', ',')} já aplicado)
                  </p>
                )}
              </div>

              {needsChange === null ? (
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => setNeedsChange(false)}
                    variant="outline"
                    className={`h-12 ${t.border}`}
                  >
                    Não preciso
                  </Button>
                  <Button
                    onClick={() => setNeedsChange(true)}
                    className="h-12 bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    Sim, preciso
                  </Button>
                </div>
              ) : needsChange ? (
                <div className="space-y-4">
                  <div>
                    <Label className={`text-sm ${t.textMuted}`}>Troco para quanto?</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-bold text-green-600">R$</span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={changeAmount}
                        onChange={(e) => setChangeAmount(sanitizeCurrencyInput(e.target.value))}
                        placeholder="0.00"
                        className={`pl-12 h-14 text-xl font-bold ${t.input}`}
                        autoFocus
                      />
                    </div>
                    {changeAmount && parseFloat(changeAmount) > totalFinal && (
                      <p className={`text-sm text-green-600 mt-2`}>
                        Troco: R$ {(parseFloat(changeAmount) - totalFinal).toFixed(2).replace('.', ',')}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={() => { setNeedsChange(null); setChangeAmount(''); }}
                      variant="outline"
                      className={t.border}
                    >
                      Voltar
                    </Button>
                    <Button
                      onClick={handleConfirmChange}
                      disabled={!changeAmount || parseFloat(changeAmount) <= totalFinal}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      Confirmar
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={handleConfirmChange}
                  className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white"
                >
                  Continuar
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Dialog de Confirmação de Deletar Endereço */}
        {deleteAddressDialog.open && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className={`${t.bg} rounded-2xl p-6 max-w-sm w-full shadow-xl`}>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-red-500" />
                </div>
                <h3 className={`text-xl font-bold ${t.text}`}>Remover endereço?</h3>
                <p className={`${t.textMuted} text-sm mt-2`}>
                  Deseja remover o endereço <span className="font-bold">"{deleteAddressDialog.addressLabel}"</span>?
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={() => setDeleteAddressDialog({ open: false, addressId: null, addressLabel: '' })}
                  variant="outline"
                  className={`h-12 ${t.border}`}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmDeleteAddress}
                  className="h-12 bg-red-500 hover:bg-red-600 text-white"
                >
                  Remover
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>

      {/* Modal PIX Online */}
      {showPixModal && currentOrderId && (
        <PixOnlineModal
          open={showPixModal}
          onClose={() => {
            setShowPixModal(false);
            setCurrentOrderId(null);
          }}
          orderId={currentOrderId}
          customerId={client.id}
          amount={totalComEntrega}
          onPaymentConfirmed={handlePixConfirmed}
        />
      )}

      {/* Modal Cartão Online */}
      {showCardModal && currentOrderId && (
        <CartaoOnlineModal
          open={showCardModal}
          onClose={() => {
            setShowCardModal(false);
            setCurrentOrderId(null);
          }}
          orderId={currentOrderId}
          customerId={client.id}
          amount={totalComEntrega}
          onPaymentConfirmed={handleCardConfirmed}
        />
      )}

      {/* Modal: Pedido mínimo não atingido */}
      {(() => {
        const pedidoMinimo = Number(stats?.pedido_minimo) || 0;
        const falta = Math.max(0, pedidoMinimo - cartTotal);
        return (
          <Dialog open={minOrderModalOpen} onOpenChange={setMinOrderModalOpen}>
            <DialogContent className={`max-w-sm ${t.bg} ${t.text} ${t.border}`}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base">
                  <div className="w-9 h-9 rounded-full bg-orange-500/15 flex items-center justify-center">
                    <ShoppingCart className="w-4.5 h-4.5 text-orange-500" />
                  </div>
                  Pedido mínimo não atingido
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-1">
                <p className={`text-sm ${t.textMuted}`}>
                  Para entregas, o pedido mínimo é de <span className={`font-semibold ${t.text}`}>R$ {pedidoMinimo.toFixed(2).replace('.', ',')}</span>.
                </p>
                <div className="rounded-xl border border-orange-500/30 bg-orange-500/5 p-3 text-center">
                  <p className={`text-[11px] uppercase tracking-wide ${t.textMuted}`}>Faltam</p>
                  <p className="text-2xl font-bold text-orange-500 tabular-nums tracking-tight mt-0.5">
                    R$ {falta.toFixed(2).replace('.', ',')}
                  </p>
                  <p className={`text-[11px] ${t.textMuted} mt-0.5`}>para liberar a entrega</p>
                </div>
              </div>
              <div className="flex flex-col gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => { setMinOrderModalOpen(false); onClose(); }}
                  className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition-colors active:scale-95"
                >
                  Continuar comprando
                </button>
              </div>
            </DialogContent>
          </Dialog>
        );
      })()}
    </Sheet>
  );
}

// Ícones oficiais das redes sociais (paths originais do brand de cada plataforma).
// Usamos SVG inline em monocromático para herdar a cor do container (currentColor) — fica
// elegante em qualquer tema e mantém a forma reconhecível de cada marca.
/**
 * Bloco "Sobre a loja" dentro do InfoLojaModal. Mostra cards visuais com
 * tempo médio de pedido, pedido mínimo e nota — todos vindos do mesmo
 * `cardapioStats` que alimenta o hero. Layout grid auto-fit (2 ou 3 colunas
 * dependendo de quantos dados existem).
 *
 * Nota: sempre renderizada (5 estrelas como fallback quando vazia, como o
 * dono pediu). Tempo e mínimo só renderizam quando há valor real.
 */
function SobreLojaSection({ stats, t, darkMode, onOpenReviews }) {
  const tempoMin = stats?.avg_order_time_min;
  const minimo = stats?.pedido_minimo;
  const avg = stats?.review_avg;
  const count = stats?.review_count || 0;
  const hasReviews = avg != null && count > 0;

  const items = [];
  if (tempoMin != null && tempoMin > 0) {
    items.push({
      key: "tempo",
      icon: <Clock className="w-4 h-4 text-primary" />,
      value: `${tempoMin} min`,
      label: "tempo médio",
    });
  }
  if ((minimo || 0) > 0) {
    items.push({
      key: "minimo",
      icon: <ShoppingCart className="w-4 h-4 text-primary" />,
      value: `R$ ${Number(minimo).toFixed(2).replace(".", ",")}`,
      label: "pedido mínimo",
    });
  }
  items.push({
    key: "nota",
    icon: <Star className="w-4 h-4 text-amber-500 fill-amber-500" />,
    value: hasReviews ? Number(avg).toFixed(1).replace(".", ",") : (
      <span className="inline-flex items-center gap-0.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={i} className="w-3 h-3 text-amber-500 fill-amber-500" />
        ))}
      </span>
    ),
    label: hasReviews
      ? `${count} avalia${count === 1 ? "ção" : "ções"}`
      : "sem avaliações",
    clickable: hasReviews && typeof onOpenReviews === "function",
  });

  if (items.length === 0) return null;

  const colsClass = items.length === 1
    ? "grid-cols-1"
    : items.length === 2
    ? "grid-cols-2"
    : "grid-cols-3";

  return (
    <section>
      <h3 className={`text-xs font-bold uppercase tracking-wider ${t.textMuted} mb-2`}>
        Sobre a loja
      </h3>
      <div className={`grid ${colsClass} gap-2`}>
        {items.map((it) => {
          const cls = `rounded-xl border ${t.border} px-3 py-2.5 flex flex-col items-center justify-center text-center gap-1 ${darkMode ? "bg-zinc-900/30" : "bg-gray-50"} ${it.clickable ? "hover:border-primary/60 hover:shadow-sm cursor-pointer transition-all" : ""}`;
          if (it.clickable) {
            return (
              <button
                key={it.key}
                type="button"
                onClick={onOpenReviews}
                className={cls}
                title="Ver todas as avaliações"
              >
                {it.icon}
                <div className={`text-sm font-bold ${t.text} tabular-nums leading-none`}>{it.value}</div>
                <div className={`text-[10px] uppercase tracking-wider ${t.textMuted}`}>{it.label}</div>
                <div className="text-[9px] text-primary font-semibold uppercase tracking-wider mt-0.5">Ver todas →</div>
              </button>
            );
          }
          return (
            <div key={it.key} className={cls}>
              {it.icon}
              <div className={`text-sm font-bold ${t.text} tabular-nums leading-none`}>{it.value}</div>
              <div className={`text-[10px] uppercase tracking-wider ${t.textMuted}`}>{it.label}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Badge de nota média no hero do cardápio. Sem avaliação ainda → mostra 5
 * estrelas amarelas cheias (loja nova / "merece nota máxima"). Com nota,
 * mostra "★ 4.8" + contagem ("(123)") quando o total cabe.
 */
function CardapioRatingBadge({ stats, t }) {
  const avg = stats?.review_avg;
  const count = stats?.review_count || 0;
  const hasReviews = avg != null && count > 0;
  return (
    <div className="flex items-center gap-1.5" title={hasReviews ? `Nota média de ${count} avaliação${count === 1 ? "" : "ões"}` : "Loja sem avaliações ainda"}>
      {hasReviews ? (
        <>
          <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
          <span className={t.textMuted}>
            <span className={`font-semibold ${t.text} tabular-nums`}>
              {Number(avg).toFixed(1).replace(".", ",")}
            </span>
            {count > 0 && (
              <span className="ml-1 tabular-nums">({count})</span>
            )}
          </span>
        </>
      ) : (
        <span className="inline-flex items-center gap-0.5" aria-label="Sem avaliações ainda">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} className="w-3 h-3 text-amber-500 fill-amber-500" />
          ))}
        </span>
      )}
    </div>
  );
}

/**
 * Modal "Informações da Loja" — abre ao clicar na logo, status ou meta tempo/frete do header.
 * Mostra: status atual, horários da semana, endereço (com link Google Maps), formas de
 * pagamento aceitas, redes sociais e telefone de contato.
 */
/**
 * Formata número de telefone BR no padrão pedido pelo dono:
 *  - Celular (11 dígitos):  (XX)X.XXXX-XXXX  →  (34)9.9965-6307
 *  - Fixo    (10 dígitos):  (XX)XXXX-XXXX    →  (34)3221-5500
 * Aceita entrada com ou sem formatação; ignora prefixo "+55"/"55" se presente.
 * Se não bater em 10/11 dígitos depois de limpar, retorna o número original
 * pra não esconder erro de cadastro do lojista.
 */
function formatPhoneBR(raw) {
  if (!raw) return "";
  let digits = String(raw).replace(/\D/g, "");
  if (digits.length === 13 && digits.startsWith("55")) digits = digits.slice(2);
  if (digits.length === 12 && digits.startsWith("55")) digits = digits.slice(2);
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)})${digits.slice(2, 3)}.${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)})${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

/**
 * Modal público de avaliações — abre ao clicar no card "N avaliações" do
 * InfoLojaModal. Busca `/public/cardapio-reviews` (só reviews visíveis, com
 * nome parcialmente ofuscado no backend). Mostra: nome, data, nota em
 * estrelas, e comentário (quando existir). Transparência sobre a nota
 * exibida no hero.
 */
function PublicReviewsModal({ onClose, darkMode, t, fantasyName }) {
  const PAGE_SIZE = 10;
  const [items, setItems] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [windowDays, setWindowDays] = useState(90);
  const [avgAll, setAvgAll] = useState(null);
  const [dist, setDist] = useState({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/public/cardapio-reviews`, { params: { page, page_size: PAGE_SIZE } })
      .then((res) => {
        if (cancelled) return;
        setItems(Array.isArray(res.data?.reviews) ? res.data.reviews : []);
        setDimensions(Array.isArray(res.data?.dimensions) ? res.data.dimensions : []);
        setTotal(Number(res.data?.total) || 0);
        setTotalPages(Number(res.data?.total_pages) || 0);
        setWindowDays(Number(res.data?.window_days) || 90);
        setAvgAll(res.data?.avg_all != null ? Number(res.data.avg_all) : null);
        setDist({
          5: Number(res.data?.distribution?.["5"]) || 0,
          4: Number(res.data?.distribution?.["4"]) || 0,
          3: Number(res.data?.distribution?.["3"]) || 0,
          2: Number(res.data?.distribution?.["2"]) || 0,
          1: Number(res.data?.distribution?.["1"]) || 0,
        });
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
        setDimensions([]);
        setTotal(0);
        setTotalPages(0);
        setAvgAll(null);
        setDist({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page]);

  // Constrói lista de páginas exibidas no paginador: sempre mostra 1 e última,
  // 2 vizinhas da atual, e "…" quando pula. Ex.: 1 ... 4 [5] 6 ... 12
  const pageWindow = (() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const out = new Set([1, totalPages, page, page - 1, page + 1]);
    const nums = [...out].filter(n => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const withGaps = [];
    for (let i = 0; i < nums.length; i++) {
      if (i > 0 && nums[i] - nums[i - 1] > 1) withGaps.push("...");
      withGaps.push(nums[i]);
    }
    return withGaps;
  })();

  const capitalize = (s) => (s || "").charAt(0).toUpperCase() + (s || "").slice(1);
  // Ordena ratings do turno respeitando ordem da campanha; chaves órfãs no final.
  const orderRatings = (ratings) => {
    const out = [];
    const seen = new Set();
    const rObj = ratings || {};
    for (const d of dimensions || []) {
      if (d.key && rObj[d.key] != null) {
        out.push([d.key, Number(rObj[d.key]) || 0, d.label || capitalize(d.key)]);
        seen.add(d.key);
      }
    }
    for (const [k, v] of Object.entries(rObj)) {
      if (!seen.has(k)) out.push([k, Number(v) || 0, capitalize(k)]);
    }
    return out;
  };

  const avg = avgAll;

  const fmtData = (raw) => {
    if (!raw) return "";
    try {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return "";
      const now = new Date();
      const diffMs = now - d;
      const diffDay = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDay === 0) return "hoje";
      if (diffDay === 1) return "ontem";
      if (diffDay < 7) return `há ${diffDay} dias`;
      if (diffDay < 30) {
        const w = Math.floor(diffDay / 7);
        return `há ${w} semana${w === 1 ? "" : "s"}`;
      }
      return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return ""; }
  };

  const stars = (rating) => {
    const rounded = Math.round((Number(rating) || 0) * 2) / 2;
    return [1, 2, 3, 4, 5].map((i) => {
      const full = i <= Math.floor(rounded);
      const half = !full && i - 0.5 <= rounded;
      return (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${full || half ? "text-amber-500 fill-amber-500" : "text-gray-300 dark:text-zinc-600"}`}
        />
      );
    });
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className={`${t.bgCard} rounded-2xl border ${t.border} shadow-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-start gap-3 px-5 py-4 border-b ${t.border} ${t.bgCard} rounded-t-2xl`}>
          <div className="flex-1 min-w-0">
            <h2 className={`text-base font-bold ${t.text} leading-tight`}>Avaliações</h2>
            <p className={`text-xs ${t.textMuted} mt-0.5 truncate`}>
              {fantasyName ? `Clientes que avaliaram ${fantasyName}` : "Clientes que avaliaram a loja"}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg ${darkMode ? "hover:bg-zinc-700" : "hover:bg-gray-100"} transition-colors flex-shrink-0`}
            aria-label="Fechar"
          >
            <X className={`w-4 h-4 ${t.text}`} />
          </button>
        </div>

        {/* Corpo scrollável */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Resumo — média + distribuição */}
          {total > 0 && (
            <div className={`rounded-xl border ${t.border} p-4 ${darkMode ? "bg-zinc-900/30" : "bg-gray-50"}`}>
              <div className="flex items-center gap-4">
                <div className="text-center flex-shrink-0">
                  <div className={`text-3xl font-black ${t.text} tabular-nums leading-none`}>
                    {avg != null ? avg.toFixed(1).replace(".", ",") : "—"}
                  </div>
                  <div className="flex items-center gap-0.5 justify-center mt-1">
                    {stars(avg)}
                  </div>
                  <div className={`text-[10px] uppercase tracking-wider ${t.textMuted} mt-1`}>
                    {total} avalia{total === 1 ? "ção" : "ções"}
                  </div>
                </div>
                <div className="flex-1 space-y-1">
                  {[5, 4, 3, 2, 1].map((n) => {
                    const c = dist[n] || 0;
                    const pct = total > 0 ? Math.round((c / total) * 100) : 0;
                    return (
                      <div key={n} className="flex items-center gap-2 text-[11px]">
                        <span className={`${t.textMuted} w-3 text-right tabular-nums`}>{n}</span>
                        <Star className="w-2.5 h-2.5 text-amber-500 fill-amber-500 flex-shrink-0" />
                        <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${darkMode ? "bg-zinc-800" : "bg-gray-200"}`}>
                          <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className={`${t.textMuted} w-6 tabular-nums text-right`}>{c}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <p className={`text-[10px] ${t.textMuted} mt-3 flex items-start gap-1`}>
                <span aria-hidden>ℹ️</span>
                <span>Nota agregada das avaliações dos últimos {windowDays} dias.</span>
              </p>
            </div>
          )}

          {/* Lista */}
          {loading ? (
            <div className={`flex items-center justify-center py-10 ${t.textMuted}`}>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : total === 0 ? (
            <div className={`text-center py-10 ${t.textMuted}`}>
              <Star className={`w-10 h-10 mx-auto mb-2 opacity-30`} />
              <p className="text-sm">Ainda sem avaliações por aqui.</p>
              <p className="text-xs mt-1">Faça um pedido e seja o primeiro a avaliar!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((r, idx) => {
                const perDim = orderRatings(r.ratings);
                return (
                  <div
                    key={idx}
                    className={`rounded-xl border ${t.border} p-3 ${darkMode ? "bg-zinc-900/30" : "bg-white"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className={`text-sm font-bold ${t.text} truncate`}>{r.customer_name}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          {stars(r.rating_avg)}
                          <span className={`text-[11px] ${t.textMuted} ml-1 tabular-nums`}>
                            {Number(r.rating_avg).toFixed(1).replace(".", ",")} de média
                          </span>
                        </div>
                      </div>
                      <span className={`text-[10px] ${t.textMuted} whitespace-nowrap flex-shrink-0`}>
                        {fmtData(r.submitted_at)}
                      </span>
                    </div>

                    {/* Notas por critério (dimensões da campanha) */}
                    {perDim.length > 0 && (
                      <div className={`mt-2.5 pt-2.5 border-t ${t.border} space-y-1`}>
                        {perDim.map(([key, val, label]) => (
                          <div key={key} className="flex items-center gap-2">
                            <span className={`text-[11px] ${t.textMuted} flex-1 min-w-0 truncate`}>{label}</span>
                            <span className="inline-flex items-center gap-0.5 flex-shrink-0">
                              {stars(val)}
                            </span>
                            <span className={`text-[11px] font-semibold ${t.text} tabular-nums w-6 text-right flex-shrink-0`}>
                              {Number(val).toFixed(1).replace(".", ",")}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {r.comment && (
                      <p className={`text-xs ${t.text} mt-2.5 pt-2.5 border-t ${t.border} leading-relaxed whitespace-pre-wrap break-words italic`}>
                        “{r.comment}”
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer com paginador — só quando cabe mais de uma página */}
        {totalPages > 1 && (
          <div className={`border-t ${t.border} px-3 py-2.5 flex items-center justify-center gap-1 flex-shrink-0 ${darkMode ? "bg-zinc-900/40" : "bg-gray-50"}`}>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className={`px-2 h-7 rounded-md text-xs font-semibold transition-colors ${page <= 1 || loading ? "opacity-40 cursor-not-allowed" : (darkMode ? "hover:bg-zinc-700 text-zinc-200" : "hover:bg-gray-200 text-gray-700")}`}
              aria-label="Página anterior"
            >
              ‹
            </button>
            {pageWindow.map((n, i) => n === "..." ? (
              <span key={`gap-${i}`} className={`px-1 text-xs ${t.textMuted}`}>…</span>
            ) : (
              <button
                key={n}
                type="button"
                onClick={() => setPage(n)}
                disabled={loading}
                className={`min-w-[28px] h-7 px-1.5 rounded-md text-xs font-semibold tabular-nums transition-colors ${n === page ? "bg-primary text-primary-foreground" : (darkMode ? "hover:bg-zinc-700 text-zinc-300" : "hover:bg-gray-200 text-gray-700")}`}
                aria-current={n === page ? "page" : undefined}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className={`px-2 h-7 rounded-md text-xs font-semibold transition-colors ${page >= totalPages || loading ? "opacity-40 cursor-not-allowed" : (darkMode ? "hover:bg-zinc-700 text-zinc-200" : "hover:bg-gray-200 text-gray-700")}`}
              aria-label="Próxima página"
            >
              ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoLojaModal({
  onClose, companySettings, businessHours,
  isOpen, closingTime, manuallyClosed, closeReason, nextOpenTime,
  darkMode, t, cardapioStats, onOpenReviews, schedulingActive = false,
}) {
  const DIAS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

  // Fetch das formas de pagamento ativas no Cardápio (mesma rota que o checkout usa).
  // Auto-isolado: não depende de state do componente pai pra evitar erros de escopo.
  const [paymentMethods, setPaymentMethods] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/public/payment-methods/cardapio`)
      .then((res) => {
        if (!cancelled) setPaymentMethods(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => { if (!cancelled) setPaymentMethods([]); });
    return () => { cancelled = true; };
  }, []);

  // dia atual (0=Seg, 6=Dom) — mesma convenção do businessHours.day_of_week
  const _now = new Date();
  let _todayIdx = _now.getDay() - 1;
  if (_todayIdx < 0) _todayIdx = 6;

  const phone = companySettings?.phone || "";
  const phoneDigits = phone.replace(/\D/g, "");
  const address = companySettings?.address || "";
  const instagram = companySettings?.instagram || "";
  const facebook = companySettings?.facebook || "";
  const tiktok = companySettings?.tiktok || "";
  const kwai = companySettings?.kwai || "";
  const email = companySettings?.email || "";

  const _normalizeUrl = (raw, prefix) => {
    if (!raw) return "";
    const s = String(raw).trim();
    if (!s) return "";
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    // Se for um @handle ou nome de usuário, prefixa com a URL da rede
    return `${prefix}${s.replace(/^@/, "")}`;
  };
  const instagramUrl = _normalizeUrl(instagram, "https://instagram.com/");
  const facebookUrl = _normalizeUrl(facebook, "https://facebook.com/");
  const tiktokUrl = _normalizeUrl(tiktok, "https://tiktok.com/@");
  const kwaiUrl = _normalizeUrl(kwai, "https://kwai.com/@");

  const mapsUrl = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : "";
  const whatsappUrl = phoneDigits.length >= 10
    ? `https://wa.me/55${phoneDigits}`
    : "";

  // Lista única de formas de pagamento (descontando duplicatas e nomes vazios)
  const formasUnicas = Array.from(new Set(
    (paymentMethods || []).map((m) => m.name || m.code || "").filter(Boolean)
  ));

  // Horários colapsáveis: só mostra hoje por padrão, click expande os outros dias
  const [hoursExpanded, setHoursExpanded] = useState(false);

  const _hoursForDay = (idx) => {
    const h = (businessHours || []).find((x) => x.day_of_week === idx);
    if (!h || !h.is_open) return { aberto: false, texto: "Fechado" };
    const t1 = `${h.opening_time || ""} – ${h.closing_time || ""}`;
    const texto = [
      t1,
      h.has_second_period ? `${h.opening_time_2 || ""} – ${h.closing_time_2 || ""}` : null,
      h.has_third_period ? `${h.opening_time_3 || ""} – ${h.closing_time_3 || ""}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
    return { aberto: true, texto };
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className={`${t.bgCard} rounded-2xl border ${t.border} shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header com logo + nome + slogan + botão fechar */}
        <div className={`relative flex items-start gap-3 px-5 py-4 border-b ${t.border} sticky top-0 ${t.bgCard} z-10 rounded-t-2xl`}>
          <div className="w-12 h-12 rounded-full bg-white border-2 border-white shadow-sm overflow-hidden flex items-center justify-center flex-shrink-0">
            {companySettings?.logo_url ? (
              <img
                src={getImageUrl(companySettings.logo_url)}
                alt={companySettings.company_name || 'Logo'}
                className="w-full h-full object-cover"
              />
            ) : (
              <UtensilsCrossed className="w-6 h-6 text-primary" />
            )}
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <h2 className={`text-base font-bold ${t.text} leading-tight truncate`}>
              {companySettings?.fantasy_name || companySettings?.company_name || 'Restaurante'}
            </h2>
            {companySettings?.slogan && (
              <p className={`text-xs ${t.textMuted} mt-0.5 line-clamp-2`}>{companySettings.slogan}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg ${darkMode ? "hover:bg-zinc-700" : "hover:bg-gray-100"} transition-colors flex-shrink-0`}
            aria-label="Fechar"
          >
            <X className={`w-4 h-4 ${t.text}`} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Status atual */}
          <div className="flex items-center justify-center">
            {isOpen ? (
              <span className="inline-flex items-center gap-1.5 bg-green-500/15 text-green-600 dark:text-green-400 px-3 py-1.5 rounded-full text-xs font-semibold">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                Aberto agora{closingTime && ` · fecha às ${closingTime}`}
              </span>
            ) : schedulingActive ? (
              <span className="inline-flex items-center gap-1.5 bg-amber-500/15 text-amber-600 dark:text-amber-400 px-3 py-1.5 rounded-full text-xs font-semibold">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                Aceitando agendamentos{nextOpenTime && !manuallyClosed ? ` · abre ${nextOpenTime}` : ""}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 bg-red-500/15 text-red-600 dark:text-red-400 px-3 py-1.5 rounded-full text-xs font-semibold">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                {manuallyClosed
                  ? `Fechado${closeReason ? ` · ${closeReason}` : ""}`
                  : `Fechado${nextOpenTime ? ` · abre ${nextOpenTime}` : ""}`}
              </span>
            )}
          </div>

          {/* Sobre a loja — tempo médio, pedido mínimo, avaliação. Cada item
              só aparece se tem valor pra mostrar; nota sempre mostra (5
              estrelas como fallback pra loja sem avaliações ainda). */}
          <SobreLojaSection stats={cardapioStats} t={t} darkMode={darkMode} onOpenReviews={onOpenReviews} />

          {/* Horários — só hoje por padrão; click expande os outros dias */}
          <section>
            <h3 className={`text-xs font-bold uppercase tracking-wider ${t.textMuted} mb-2 flex items-center gap-1.5`}>
              <Clock className="w-3.5 h-3.5" /> Horários
            </h3>
            <div className={`rounded-xl border ${t.border} overflow-hidden`}>
              {/* Linha do dia atual sempre visível, com toggle */}
              <button
                type="button"
                onClick={() => setHoursExpanded((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2.5 bg-primary/10 hover:bg-primary/15 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="font-bold text-primary text-sm">
                    {DIAS[_todayIdx]} · hoje
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {(() => {
                    const { aberto, texto } = _hoursForDay(_todayIdx);
                    return (
                      <span className={`text-xs font-mono ${aberto ? t.text : "text-red-500"}`}>
                        {texto}
                      </span>
                    );
                  })()}
                  {hoursExpanded ? (
                    <ChevronUp className={`w-4 h-4 ${t.textMuted}`} />
                  ) : (
                    <ChevronDown className={`w-4 h-4 ${t.textMuted}`} />
                  )}
                </div>
              </button>
              {/* Outros dias só aparecem quando expandido */}
              {hoursExpanded && (
                <div className={`divide-y ${darkMode ? "divide-zinc-700" : "divide-gray-200"} border-t ${t.border}`}>
                  {DIAS.map((dia, idx) => {
                    if (idx === _todayIdx) return null;
                    const { aberto, texto } = _hoursForDay(idx);
                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between px-3 py-2 text-sm"
                      >
                        <span className={t.text}>{dia}</span>
                        <span className={`text-xs font-mono ${aberto ? t.text : "text-red-500"}`}>
                          {texto}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Endereço */}
          {address && (
            <section>
              <h3 className={`text-xs font-bold uppercase tracking-wider ${t.textMuted} mb-2 flex items-center gap-1.5`}>
                <MapPin className="w-3.5 h-3.5" /> Endereço
              </h3>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`block rounded-xl border ${t.border} px-3 py-2.5 hover:bg-primary/5 transition-colors`}
              >
                <p className={`text-sm ${t.text}`}>{address}</p>
                <p className="text-[11px] text-primary font-semibold mt-0.5">
                  Abrir no Google Maps →
                </p>
              </a>
            </section>
          )}

          {/* Contato */}
          {(phone || email) && (
            <section>
              <h3 className={`text-xs font-bold uppercase tracking-wider ${t.textMuted} mb-2 flex items-center gap-1.5`}>
                <Phone className="w-3.5 h-3.5" /> Contato
              </h3>
              <div className="space-y-2">
                {phone && (
                  <a
                    href={whatsappUrl || `tel:${phoneDigits}`}
                    target={whatsappUrl ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className={`flex items-center justify-between rounded-xl border ${t.border} px-3 py-2.5 hover:bg-green-500/10 transition-colors`}
                  >
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-green-600" />
                      <span className={`text-sm ${t.text} tabular-nums`}>{formatPhoneBR(phone)}</span>
                    </div>
                    {whatsappUrl && (
                      <span className="text-[11px] text-green-600 font-semibold">
                        WhatsApp →
                      </span>
                    )}
                  </a>
                )}
                {email && (
                  <a
                    href={`mailto:${email}`}
                    className={`flex items-center gap-2 rounded-xl border ${t.border} px-3 py-2.5 hover:bg-primary/5 transition-colors`}
                  >
                    <span className={`text-sm ${t.text}`}>📧 {email}</span>
                  </a>
                )}
              </div>
            </section>
          )}

          {/* Formas de pagamento */}
          {formasUnicas.length > 0 && (
            <section>
              <h3 className={`text-xs font-bold uppercase tracking-wider ${t.textMuted} mb-2 flex items-center gap-1.5`}>
                <CreditCard className="w-3.5 h-3.5" /> Formas de pagamento
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {formasUnicas.map((nome) => (
                  <span
                    key={nome}
                    className={`text-xs ${darkMode ? "bg-zinc-700 text-zinc-200" : "bg-gray-100 text-gray-700"} px-2.5 py-1 rounded-full font-medium`}
                  >
                    {nome}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Redes sociais */}
          {(instagramUrl || facebookUrl || tiktokUrl || kwaiUrl) && (
            <section>
              <h3 className={`text-xs font-bold uppercase tracking-wider ${t.textMuted} mb-2`}>
                Redes sociais
              </h3>
              <div className="flex gap-2 flex-wrap">
                {instagramUrl && (
                  <a
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 min-w-[100px] inline-flex items-center justify-center gap-2 rounded-xl border border-pink-500/30 bg-gradient-to-br from-pink-500/10 to-purple-500/10 px-3 py-2.5 text-sm font-semibold text-pink-600 dark:text-pink-400 hover:opacity-80 transition-opacity"
                  >
                    <InstagramIcon className="w-4 h-4" /> Instagram
                  </a>
                )}
                {facebookUrl && (
                  <a
                    href={facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 min-w-[100px] inline-flex items-center justify-center gap-2 rounded-xl border border-blue-500/30 bg-blue-500/10 px-3 py-2.5 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:opacity-80 transition-opacity"
                  >
                    <FacebookIcon className="w-4 h-4" /> Facebook
                  </a>
                )}
                {tiktokUrl && (
                  <a
                    href={tiktokUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex-1 min-w-[100px] inline-flex items-center justify-center gap-2 rounded-xl border ${darkMode ? "border-zinc-600 bg-zinc-800 text-zinc-200" : "border-gray-300 bg-gray-100 text-gray-800"} px-3 py-2.5 text-sm font-semibold hover:opacity-80 transition-opacity`}
                  >
                    <TikTokIcon className="w-4 h-4" /> TikTok
                  </a>
                )}
                {kwaiUrl && (
                  <a
                    href={kwaiUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 min-w-[100px] inline-flex items-center justify-center gap-2 rounded-xl border border-orange-500/30 bg-orange-500/10 px-3 py-2.5 text-sm font-semibold text-orange-600 dark:text-orange-400 hover:opacity-80 transition-opacity"
                  >
                    <KwaiIcon className="w-4 h-4" /> Kwai
                  </a>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CardapioPublico({ onAdminLogin }) {
  // Slug do produto vindo da URL /cardapio/p/<slug> (deep link de divulgacao).
  // Quando presente e os produtos ja carregaram, abre o popup automaticamente.
  const { productSlug, mesaToken } = useParams();
  const navigate = useNavigate();
  // Mesa QR: quando a URL é /cardapio/mesa/<token>, o cardápio entra em modo
  // mesa — sem entrega, sem endereço, com comanda e pagamento no fim.
  const mesaQr = useMesaQr(mesaToken);
  const modoMesa = mesaQr.ativo;
  // Cardápio online num celular que está numa mesa aberta: volta para a mesa.
  // Só na página pública (`onAdminLogin`), não na prévia de dentro do painel.
  useVoltarParaMesa({ ativo: !modoMesa && !!onAdminLogin });
  useEffect(() => { iniciarClarityCardapio(modoMesa ? 'mesa' : 'cardapio'); }, [modoMesa]);
  const [pagamentoMesaAberto, setPagamentoMesaAberto] = useState(false);
  // Tema "Modo Copa do Mundo" — quando settings.cardapio_theme == "copa-brasil",
  // anexa classe theme-copa-brasil no <html> pra CSS overrides do features/copa.
  const copa = useCopaTheme({ apply: true });
  const deepLinkAppliedRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [allProductsForPhotos, setAllProductsForPhotos] = useState([]); // Todos produtos para buscar fotos
  // Promoções cadastradas pela loja. `activePromotions` (mais abaixo) soma a
  // promoção sintética da Campanha Inteligente do cliente.
  const [promocoesFixas, setActivePromotions] = useState([]);
  // Exclusivas de tipos de cliente, sem preço: só para o aviso do visitante sem login (27/10/2026)
  const [promocoesExclusivas, setPromocoesExclusivas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [businessHours, setBusinessHours] = useState([]);
  const [storeStatus, setStoreStatus] = useState({ is_open: true, reason: "", reopen_at: "" });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  // Cupons visiveis/ativos do tenant (aparecem no cardapio publico mesmo sem login)
  const [visibleCoupons, setVisibleCoupons] = useState([]);
  const [couponsModalOpen, setCouponsModalOpen] = useState(false);
  // Teaser verde 'X ofertas disponiveis' — reseta a cada page load (useState),
  // clicar dispara animacao de explosao e revela a barra de progresso + modal.
  const [couponsUnlocked, setCouponsUnlocked] = useState(false);
  const [couponsExploding, setCouponsExploding] = useState(false);
  // Anim de absorcao: ao clicar "Continuar comprando", um pill com o valor recem
  // adicionado voa do centro ate a barra de progresso e a barra cresce visivelmente.
  // { value: number, key: number } | null
  const [coupAbsorbAnim, setCoupAbsorbAnim] = useState(null);
  const lastCheckpointSubtotalRef = useRef(0);
  // Scrollspy de categoria — slug da categoria atualmente em viewport, preenchido
  // pelo IntersectionObserver. O chip correspondente fica destacado.
  const [activeCategorySlug, setActiveCategorySlug] = useState(null);
  // Setas laterais da barra de chips (desktop): visibilidade depende da posicao
  // de scroll horizontal do container.
  const chipsScrollRef = useRef(null);
  const [chipsCanScrollLeft, setChipsCanScrollLeft] = useState(false);
  const [chipsCanScrollRight, setChipsCanScrollRight] = useState(false);
  // Sugestões Inteligentes: último produto que entrou pelo "+" (painel "Combina com")
  // e sugestão tocada que abriu o popup (o item ganha a marca quando for adicionado).
  const [ultimoAdicionado, setUltimoAdicionado] = useState(null);
  const sugestaoPendenteRef = useRef(null);
  // Cupom escolhido explicitamente pelo cliente via modal (tem prioridade sobre o auto-apply).
  // Null = sem escolha, auto-apply escolhe o melhor. Reseta ao carregar pagina.
  const [selectedCouponCode, setSelectedCouponCode] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem("cardapio_theme");
      if (saved === "light") return false;
      if (saved === "dark") return true;
    } catch (e) {}
    return document.documentElement.classList.contains("dark");
  });
  const [loggedClient, setLoggedClient] = useState(null);
  // Campanha Inteligente (features/ofertas-do-dia): ofertas sorteadas pra quem está
  // olhando. Entram como promoção sintética em `activePromotions`, então card,
  // popup, pizza, carrinho, cupom e cashback já sabem aplicar. Fora do Mesa QR.
  // No Mesa QR o cliente é quem se identificou por CPF na mesa (módulo MesaQR).
  const clienteDasOfertas = modoMesa ? (mesaQr.cliente || null) : loggedClient;
  // Cartão Fidelidade (23/10/2026): o cartão e os prêmios do cliente. O prêmio vira
  // um cupom guardado na conta e entra no checkout junto dos cupons visíveis.
  const cartaoFid = useCartaoFidelidade(clienteDasOfertas?.id, modoMesa ? "MesaQR" : "Cardapio");
  const [meuCartaoAberto, setMeuCartaoAberto] = useState(false);
  // Fotos extras e vídeo dos produtos (cardápio e Mesa QR; 23/10/2026)
  const midiasProdutos = useMidiasPublicas();
  const ofertasDia = useOfertasDoDia({
    habilitado: !modoMesa || !!mesaQr.cliente,
    cliente: clienteDasOfertas,
    modulo: modoMesa ? "MesaQR" : "Cardapio",
  });
  const activePromotions = useMemo(
    // `products`: linha por quilo/litro conta 1 unidade da oferta (30/09/2026)
    () => mesclarPromocoes(promocoesFixas, ofertasDia, cart, products),
    [promocoesFixas, ofertasDia, cart, products],
  );
  useOfertasExpiradasNoCarrinho({ cart, setCart, ofertasDia });
  // Recuperador de Vendas: link da conversa do WhatsApp avisa abertura e carrinho.
  // Se a CLEO estava anotando o pedido na conversa, o link traz os itens (mais abaixo vão pro carrinho).
  const { comandaDaConversa, esquecerComanda } = useLinkConversa(cart);

  useTelemetriaCardapio({ habilitado: !modoMesa, dia: ofertasDia.dia });
  const [editarCadastroAberto, setEditarCadastroAberto] = useState(false);
  // Estado do popup de produto
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productPopupOpen, setProductPopupOpen] = useState(false);
  // Pizza: hook usePizzaPopup() faz fetch product-aware automático
  // (source=product_steps) e gerencia o popup. addToCart vem via ref pra
  // contornar o fato dele ser declarado depois no componente (linha ~6478) —
  // a ref é sincronizada num useEffect logo abaixo, antes do popup ser usado.
  const _addToCartRef = useRef(null);
  // v1.5.365+ FIX: `allProductsForPhotos` (do endpoint /public/products/all)
  // vem sem `product_type` e sem `order_steps` pra pizzas, o que quebrava
  // herança de preços por tamanho no PizzaPopup. Prioriza `products` (que tem
  // metadata completa) e só usa `allProductsForPhotos` como fonte de IDs
  // exclusivos (insumos, fotos de adicionais).
  const allProductsForPizza = useMemo(() => {
    const byId = new Map((products || []).map(p => [String(p.id || ''), p]));
    return [
      ...(products || []),
      ...((allProductsForPhotos || []).filter(x => x?.id && !byId.has(String(x.id)))),
    ];
  }, [products, allProductsForPhotos]);
  // "Peça de novo": lista de venda × catálogo completo pra dizer se cada produto ou
  // complemento do pedido antigo está esgotado, escondido, apagado ou de categoria
  // desativada (features/cardapio-reorder).
  const aVendaPorId = useMemo(() => new Map((products || []).map(p => [String(p.id || ''), p])), [products]);
  const catalogoPorId = useMemo(() => new Map(allProductsForPizza.map(p => [String(p.id || ''), p])), [allProductsForPizza]);
  const { tryOpenPizza, popup: pizzaPopupNode, isOpen: pizzaAberta } = usePizzaPopup({
    onAddToCart: useCallback((item) => _addToCartRef.current?.(item), []),
    allProducts: allProductsForPizza,
    darkMode,
    // Campanha Inteligente: com o cliente, o popup recebe as ofertas de pizza dele por tamanho.
    clienteId: ofertasDia.estado === "ativo" ? (clienteDasOfertas?.id || null) : null,
    moduloOferta: modoMesa ? "MesaQR" : "Cardapio",
  });
  // Quando preenchido, abre o popup em modo "editar item do carrinho" — prefill
  // de quantidade/observacao/combo/step_selections, e salvar substitui o item.
  const [editingCartItem, setEditingCartItem] = useState(null);
  // Estado do checkout
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // State para aba ativa do menu inferior (declarada aqui, antes das sugestões,
  // porque a sacola lateral depende dela)
  const [activeTab, setActiveTab] = useState('cardapio');
  // Sacola lateral (features/cardapio-sacola): no computador, na aba Cardápio, a
  // sacola é um cartão ao lado dos produtos — saem o popup e as barras do rodapé.
  const telaLarga = useTelaLarga();
  const sacolaLateral = telaLarga && activeTab === 'cardapio';

  // Sugestões Inteligentes (features/sugestoes): sacola, logo após o "+" e antes de finalizar.
  const produtosPorId = useMemo(() => new Map((products || []).map(p => [String(p.id || ''), p])), [products]);
  const carrinhoIds = useMemo(() => Array.from(new Set((cart || []).map(i => String(i.id || '')).filter(Boolean))), [cart]);
  const ofertasIds = useMemo(() => (ofertasDia?.estado === 'ativo' && ofertasDia.porProduto ? Array.from(ofertasDia.porProduto.keys()) : []), [ofertasDia]);
  // Na lateral o "acabou de adicionar" vale até o próximo item; se ele saiu da
  // sacola, volta o "Combina com o seu pedido".
  const ultimoNaSacola = ultimoAdicionado && carrinhoIds.includes(ultimoAdicionado.id) ? ultimoAdicionado : null;
  const sugereSacolaToda = cartOpen ? !ultimoAdicionado : (sacolaLateral && !ultimoNaSacola);
  const sugestoesSacola = useSugestoes({ ativo: sugereSacolaToda && !modoMesa, lugar: 'sacola', carrinhoIds, ofertasIds, produtosPorId });
  const sugestoesCheckout = useSugestoes({ ativo: checkoutOpen && !modoMesa, lugar: 'checkout', carrinhoIds, ofertasIds, produtosPorId, somenteSimples: true });
  const sugestoesAdicionado = useSugestoes({ ativo: !!ultimoAdicionado && !modoMesa, lugar: 'adicionado', carrinhoIds, base: ultimoAdicionado?.id, ofertasIds, produtosPorId });
  const fecharCombina = useCallback(() => setUltimoAdicionado(null), []);
  // No computador a sacola já está na tela: "abrir a sacola" não abre o popup por
  // cima dela. Se a tela alarga com o popup aberto, ele fecha.
  const abrirSacola = () => { if (!sacolaLateral) setCartOpen(true); };
  useEffect(() => { if (sacolaLateral) setCartOpen(false); }, [sacolaLateral]);
  // Sacola fechada ou checkout aberto: o "acabou de adicionar" já passou.
  const sacolaAbertaAntes = useRef(false);
  useEffect(() => {
    if (checkoutOpen || (sacolaAbertaAntes.current && !cartOpen)) setUltimoAdicionado(null);
    sacolaAbertaAntes.current = cartOpen;
  }, [cartOpen, checkoutOpen]);
  useEffect(() => {
    // Popup fechado sem adicionar: a sugestão tocada não marca uma compra futura.
    if (!productPopupOpen && !pizzaAberta) {
      const timer = setTimeout(() => { sugestaoPendenteRef.current = null; }, 400);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [productPopupOpen, pizzaAberta]);
  const precoSugestao = (produto) => {
    if (produto.product_type === 'pizza') return { preco: getPizzaMinActivePrice(produto) || 0, cheio: null };
    const cheio = Number(produto.sale_price || 0);
    const promo = getPromoForProduct(produto.id, 'simples');
    const preco = promo ? calcPromoPrice(cheio, promo) : cheio;
    return { preco, cheio: preco < cheio ? cheio : null };
  };
  const escolherSugestao = (sugestao, lugar) => {
    const produto = sugestao.produto;
    if (lugar === 'adicionado') setUltimoAdicionado(null);
    if (ehSimples(produto)) {
      const { preco, cheio } = precoSugestao(produto);
      addToCart({
        ...produto, quantity: 1, sugestao_lugar: lugar, sugestao_qtd: 1,
        ...(cheio ? { final_price: Math.round(preco * 100) / 100 } : {}),
      }, { openDrawer: false });
      registrarAdicionada(lugar, produto.id);
      toast.success(`${produto.name} adicionado`);
      return;
    }
    sugestaoPendenteRef.current = { id: String(produto.id), lugar, em: Date.now() };
    if (cartOpen) {
      setCartOpen(false);
      setTimeout(() => openProductPopup(produto), 200);
    } else {
      openProductPopup(produto);
    }
  };
  // Estado da tela de acompanhamento
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  // Configurações da empresa
  const [companySettings, setCompanySettings] = useState({
    company_name: "Núcleo",
    slogan: "O Centro da sua Gestão",
    logo_url: null,
    fantasy_name: "",
    phone: ""
  });
  // Modal "Info da loja" — abre ao clicar na logo/status/tempo/frete do header do cardápio.
  // Mostra horários da semana, endereço, formas de pagamento, redes sociais e contato.
  const [showInfoLojaModal, setShowInfoLojaModal] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  // Config publica de agendamentos (ativo/inativo + limites). Fetch 1x no boot.
  const [schedulingConfig, setSchedulingConfig] = useState({
    ativo: false, janela_dias_max: 7, slot_minutos: 30,
    // null = dono nunca configurou (aceita tudo); [] = bloqueia tudo; lista = whitelist
    formas_pagamento: null,
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/scheduling/config`)
      .then((res) => {
        if (cancelled || !res.data) return;
        setSchedulingConfig({
          ativo: !!res.data.ativo,
          janela_dias_max: res.data.janela_dias_max || 7,
          slot_minutos: res.data.slot_minutos || 30,
          formas_pagamento: Array.isArray(res.data.formas_pagamento) ? res.data.formas_pagamento : null,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Config de Cashback (v1.5.304+) — fetch silencioso no boot.
  // Quando ativo && percentual > 0, cards renderizam badge "X% de cashback" no rodapé.
  // Cashback por tipo de cliente (27/10/2026): com o cliente logado, o
  // percentual é o DELE (0 se o tipo dele não ganha) — busca de novo ao logar/sair.
  const [cashbackConfig, setCashbackConfig] = useState({ ativo: false, percentual: 0 });
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/cashback/config`, { params: loggedClient?.id ? { cliente_id: loggedClient.id } : {} })
      .then((res) => {
        if (cancelled || !res.data) return;
        setCashbackConfig({
          ativo: !!res.data.ativo,
          percentual: Number(res.data.percentual || 0),
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [loggedClient?.id]);
  // Banners do topo (v1.5.444+) — fetch silencioso no boot. Lista vazia (ou
  // erro) mantem a faixa de cor solida de sempre, entao loja que nunca
  // cadastrar banner nao ve diferenca nenhuma.
  const [banners, setBanners] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/cardapio-banners`)
      .then((res) => {
        if (cancelled || !Array.isArray(res.data)) return;
        setBanners(res.data.filter((b) => b && b.image_url));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  // Saldo de cashback do cliente logado (v1.5.314+). Só faz sentido quando
  // cashbackConfig.ativo && loggedClient — refetch quando qualquer um muda.
  const [cashbackSaldo, setCashbackSaldo] = useState(0);
  const refetchCashbackSaldo = useCallback(async (clientId) => {
    if (!clientId) { setCashbackSaldo(0); return; }
    try {
      const res = await axios.get(`${API}/public/cashback/saldo/${clientId}`);
      setCashbackSaldo(Number(res?.data?.saldo || 0));
    } catch { /* silencioso */ }
  }, []);
  // Estatisticas publicas (tempo medio, taxa de entrega)
  const [cardapioStats, setCardapioStats] = useState({
    avg_order_time_min: null,
    delivery_fee_min: null,
    delivery_fee_max: null,
    delivery_fee_bairros_count: 0,
    top_product_ids: [],
    launch_product_ids: [],
    pedido_minimo: 0,
  });

  // State para controlar header visível em mobile
  const [headerVisible, setHeaderVisible] = useState(true);
  const [statusBarVisible, setStatusBarVisible] = useState(true);
  const lastScrollY = useRef(0);
  // State para busca expandida
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  // State para pedidos do cliente
  const [clientPedidos, setClientPedidos] = useState([]);
  const [loadingPedidos, setLoadingPedidos] = useState(false);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [pedidosSearch, setPedidosSearch] = useState("");
  const [pedidosPage, setPedidosPage] = useState(1);
  // Funil de conversao: registrar visita no mount (dedup por IP+janela 4h no backend)
  useEffect(() => {
    trackFunnel("visit");
  }, []);

  // Perfil ativo do cardápio (turno) — define quais categorias aparecem e em que ordem.
  // Re-busca a cada minuto pra cruzar a fronteira de turno automaticamente.
  const [activeCardapioProfile, setActiveCardapioProfile] = useState(null);
  const shiftKeyRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/public/cardapio-profile/active`);
        if (cancelled) return;
        const data = res.data || null;
        // Virada de turno: `shift_key` muda junto com o conjunto de turnos
        // vigentes, entao serve de gatilho exato sem comparar listas. A PRIMEIRA
        // resposta so' semeia o ref — senao toda loja com turno mostraria
        // "cardapio atualizado" ja no carregamento da pagina.
        const key = data?.shift_key || '__sem_turno__';
        if (shiftKeyRef.current !== null && shiftKeyRef.current !== key) {
          fetchProducts();
          toast('O cardápio deste horário foi atualizado.', { icon: '🕐', duration: 5000 });
        }
        shiftKeyRef.current = key;
        setActiveCardapioProfile(data);
      } catch (error) {
        // Falha de rede NAO mexe no ref: cair pra null aqui e' fail-open
        // (cardapio completo), e a proxima resposta boa nao deve ser lida como
        // virada de turno.
        if (!cancelled) setActiveCardapioProfile(null);
      }
    };
    fetchProfile();
    const interval = setInterval(fetchProfile, 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Turno vigente, na forma que o resto da tela consulta. `null` do backend =
  // nada filtrado (fail-open), entao `turnoAtivo` fica false e todo guard abaixo
  // vira no-op — que e' exatamente o comportamento de hoje.
  const turnoCategorias = useMemo(
    () => new Set(activeCardapioProfile?.categorias_nomes || []),
    [activeCardapioProfile]
  );
  const turnoAtivo = turnoCategorias.size > 0;
  const turnoVoltaEm = activeCardapioProfile?.volta_em || null;
  const foraDoTurno = useCallback((produto) => {
    if (!turnoAtivo || !produto) return false;
    // Produto explicitamente marcado pelo backend (/public/products/all) ou
    // deduzido pela categoria. Item sem categoria nunca e' barrado.
    if (produto.fora_do_turno === true) return true;
    const cat = String(produto.category || '').trim();
    if (!cat) return false;
    return !turnoCategorias.has(cat);
  }, [turnoAtivo, turnoCategorias]);
  const avisoForaDoTurno = useCallback((nome) => {
    const quando = turnoVoltaEm ? ` — ${turnoVoltaEm}` : '';
    toast.error(`${nome || 'Esse item'} não está disponível neste horário${quando}.`, { duration: 6000 });
  }, [turnoVoltaEm]);

  // Persistencia do carrinho (TTL 4h): restaura apos produtos carregarem, persiste nas mudancas
  const cartRestoredRef = useRef(false);
  useEffect(() => {
    if (cartRestoredRef.current) return;
    if (!products || products.length === 0) return;
    // Com turno ativo, espera o catálogo completo pra conseguir distinguir
    // "saiu por horário" de "foi descontinuado". A restauração roda uma vez só
    // (cartRestoredRef), então concluir cedo demais fixaria a mensagem errada.
    if (turnoAtivo && (allProductsForPhotos || []).length === 0) return;
    const draft = loadCartDraft();
    if (draft && draft.length > 0) {
      const { items, removed, removedItems } = reconcileCartWithProducts(draft, products);
      if (items.length > 0) setCart(items);
      if (removed > 0) {
        // TURNO: o carrinho salvo pode ser de ontem, ou de antes da virada.
        // Item que ainda EXISTE no catálogo completo mas sumiu do cardápio
        // recortado saiu por HORÁRIO, não por ter sido descontinuado — e essa
        // diferença importa pro cliente (um volta, o outro não).
        const idsCompletos = new Set((allProductsForPhotos || []).map(p => p.id));
        const porTurno = (removedItems || []).filter(it => idsCompletos.has(it.id));
        if (porTurno.length === removed && turnoVoltaEm) {
          toast.info(
            `${removed} item(ns) do seu carrinho não fazem parte do cardápio deste horário (${turnoVoltaEm}) e foram removidos`,
            { duration: 8000 }
          );
        } else {
          toast.info(`${removed} item(ns) do seu carrinho nao estao mais disponiveis e foram removidos`);
        }
      }
    }
    cartRestoredRef.current = true;
  }, [products, allProductsForPhotos, turnoAtivo, turnoVoltaEm]);

  useEffect(() => {
    if (!cartRestoredRef.current) return;
    saveCartDraft(cart);
  }, [cart]);

  // Deep link de produto: quando a URL eh /cardapio/p/<slug>, procura o produto
  // pelo slug derivado do nome e abre o popup automaticamente. Usa um ref pra
  // garantir que so dispara uma vez por slug (evita reabrir se o cliente fechar
  // o popup). Apos abrir, navega pra /cardapio limpo pra nao reaplicar em re-render.
  useEffect(() => {
    if (!productSlug) {
      deepLinkAppliedRef.current = null;
      return;
    }
    if (deepLinkAppliedRef.current === productSlug) return;
    if (loading || !Array.isArray(products) || products.length === 0) return;
    const target = productSlug.toLowerCase();
    const match = products.find(p => makeProductSlug(p.name) === target);
    if (match) {
      deepLinkAppliedRef.current = productSlug;
      setSelectedProduct(match);
      setEditingCartItem(null);
      setProductPopupOpen(true);
      trackFunnel("view", { product_id: match.id, source: "deep_link" });
      // Navega pra /cardapio limpo — evita que fechar o popup tente reabrir e
      // mantem historico do navegador limpo. replace:true pra nao empilhar.
      navigate("/cardapio", { replace: true });
      return;
    }
    // TURNO: `products` ja vem recortado, entao um item fora do turno some
    // daqui. Antes de concluir "nao encontrado", espera o catalogo completo
    // chegar — "volta as 18:00" e' muito melhor que "nao existe", e concluir
    // cedo demais queimaria o slug (o ref impede uma segunda tentativa).
    if (turnoAtivo && !(allProductsForPhotos || []).length) return;
    deepLinkAppliedRef.current = productSlug;
    const noCompleto = (allProductsForPhotos || []).find(
      p => makeProductSlug(p.name) === target
    );
    if (noCompleto && turnoAtivo) {
      avisoForaDoTurno(noCompleto.name);
    } else {
      toast.error("Produto não encontrado");
    }
    navigate("/cardapio", { replace: true });
  }, [productSlug, products, loading, navigate, turnoAtivo, allProductsForPhotos, avisoForaDoTurno]);

  // Abrir popup do produto
  const openProductPopup = (product) => {
    trackFunnel("view", { product_id: product?.id });
    // Pizza tem fluxo dedicado (hook usePizzaPopup faz fetch product-aware
    // e abre o popup automaticamente quando product_type === "pizza")
    if (tryOpenPizza(product)) return;
    setSelectedProduct(product);
    setEditingCartItem(null);
    setProductPopupOpen(true);
  };

  // Cartão Fidelidade: "Usar" separa o prêmio para o fechamento. No prêmio de
  // produto, abre o produto (o desconto só vale com ele na sacola).
  const cuponsDoCheckout = useMemo(() => [
    ...visibleCoupons,
    ...cartaoFid.premios.map((p) => premioComoCupom(p, cartaoFid.estado?.soma_cashback)),
  ], [visibleCoupons, cartaoFid.premios, cartaoFid.estado]);
  const usarPremio = (p) => {
    setMeuCartaoAberto(false);
    setSelectedCouponCode(p.codigo);
    if (p.tipo === "produto" || p.tipo === "pizza") {
      const prod = products.find((x) => x.id === p.product_id) || allProductsForPhotos.find((x) => x.id === p.product_id);
      toast.success(`Prêmio separado! Coloque ${p.texto.replace(/ grátis$/, "")} na sacola e o desconto entra no fechamento.`);
      if (prod) openProductPopup(prod);
      return;
    }
    toast.success("Prêmio separado! Ele entra no fechamento do pedido.");
    if (cart.length > 0) setCartOpen(true);
  };

  // Abrir popup em modo "editar" pra item ja no carrinho que tem combo/observacao/steps.
  // IMPORTANTE: precisa do produto completo com order_steps pra renderizar o resumo
  // corretamente. /api/public/products (-> products) retorna order_steps; ja
  // /api/public/products/all (-> allProductsForPhotos) so tem metadados basicos sem
  // order_steps. Por isso prefere products[] e usa allProductsForPhotos so como fallback.
  const openEditCartItem = (cartItem) => {
    if (!cartItem || !cartItem.id) {
      toast.error("Não foi possível editar este item");
      return;
    }
    const findById = (list) => (list || []).find(p => String(p.id || "") === String(cartItem.id || ""));
    const prod = findById(products) || findById(allProductsForPhotos);
    if (!prod) {
      toast.error("Esse produto não está mais disponível.");
      return;
    }
    if (prod.available === false) {
      toast.error("Esse produto está esgotado no momento.");
      return;
    }
    setSelectedProduct(prod);
    setEditingCartItem(cartItem);
    setCartOpen(false);
    setProductPopupOpen(true);
  };
  
  // Handler para fazer pedido
  const handleMakeOrder = () => {
    // Mesa QR: não existe entrega, endereço nem forma de pagamento aqui. O
    // cliente confere a rodada e manda direto pra cozinha; paga no fim.
    if (modoMesa) {
      if (!cart || cart.length === 0) return;
      setCartOpen(false);
      mesaQr.irPara('conferencia');
      return;
    }
    // Verifica se a loja esta aberta ANTES de abrir o checkout — evita o cliente
    // preencher tudo e so descobrir no final que a loja esta fechada.
    // EXCEÇÃO: quando `schedulingConfig.ativo` estiver true, o cliente entra
    // no checkout com um passo de "Agendar horario" (não bloqueia).
    // TURNO: carrinho montado antes da virada nao pode seguir pro checkout —
    // o backend barra com 409 (Fase 4) e o cliente so descobriria la'.
    const bloqueados = (cart || []).filter(foraDoTurno);
    if (bloqueados.length > 0) {
      const nomes = bloqueados.map(i => i.name).filter(Boolean).join(', ');
      const quando = turnoVoltaEm ? ` (${turnoVoltaEm})` : '';
      toast.error(
        `${bloqueados.length === 1 ? 'Este item saiu' : 'Estes itens saíram'} do cardápio deste horário${quando}: ${nomes}. Remova do carrinho para continuar.`,
        { duration: 8000 }
      );
      abrirSacola();
      return;
    }
    const status = getOpenStatus();
    if (!status.isOpen && !schedulingConfig.ativo) {
      if (status.manuallyClosed) {
        toast.error('A loja está temporariamente fechada. Tente novamente mais tarde.', { duration: 6000 });
      } else if (status.nextOpenTime) {
        toast.error(`Estamos fechados agora. Reabrimos às ${status.nextOpenTime}.`, { duration: 6000 });
      } else {
        toast.error('Estamos fechados no momento. Volte durante o horário de atendimento.', { duration: 6000 });
      }
      return;
    }
    if (!loggedClient) {
      // Fecha o carrinho antes de abrir o login para evitar sobreposicao
      setCartOpen(false);
      setTimeout(() => setShowLoginModal(true), 150);
    } else {
      setCheckoutOpen(true);
    }
  };

  // Callback quando o pedido é completado
  const handleOrderComplete = (orderData) => {
    console.log("Pedido realizado:", orderData);
    setTimeout(() => cartaoFid.recarregar(), 1500);
    setCart([]);
    clearCartDraft();
    setCartOpen(false);
    setCheckoutOpen(false);
    // Abrir tela de acompanhamento
    setCurrentOrder(orderData);
    setTrackingOpen(true);
    // Limpa a selecao de cupom — proximo pedido comeca do zero
    setSelectedCouponCode(null);
    // Atualizar lista de pedidos e cupons visiveis (cupom pode ter atingido limite)
    if (loggedClient) {
      fetchClientPedidos();
      const params = {};
      const phone = (loggedClient?.telefone || loggedClient?.phone || "").trim();
      if (phone) params.phone = phone;
      if (loggedClient?.id) params.customer_id = loggedClient.id;
      axios.get(`${API}/public/coupons/visible`, { params })
        .then(res => setVisibleCoupons(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    }
  };

  // Buscar pedidos do cliente
  const fetchClientPedidos = async () => {
    if (!loggedClient?.id) return;
    setLoadingPedidos(true);
    try {
      // Rota pública (sem JWT) — cliente do cardápio se autentica por telefone, não por token.
      // O tenant é resolvido pelo subdomínio. Antes usava /pedidos/cliente/{id} que exige
      // get_current_user (auth admin) e retornava lista vazia silenciosamente — bug do
      // "Você ainda não fez nenhum pedido" mesmo após pedido criado.
      const response = await axios.get(`${API}/public/pedidos/cliente/${loggedClient.id}`);
      const pedidosOrdenados = (response.data || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setClientPedidos(pedidosOrdenados);
    } catch (error) {
      console.error("Erro ao carregar pedidos:", error);
      setClientPedidos([]);
    } finally {
      setLoadingPedidos(false);
    }
  };

  // Buscar pedidos sempre que o cliente logar (para sugestao de Refazer)
  useEffect(() => {
    if (loggedClient) {
      fetchClientPedidos();
    } else {
      setClientPedidos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedClient?.id]);

  // Refetch cashback saldo quando cliente/config muda OU quando algum pedido
  // vira 'concluido' (o hook backend credita nesse momento — refletir no topbar).
  const concluidosCount = (clientPedidos || []).filter(p => p.status === 'concluido').length;
  useEffect(() => {
    if (!loggedClient?.id || !cashbackConfig.ativo) {
      setCashbackSaldo(0);
      return;
    }
    refetchCashbackSaldo(loggedClient.id);
  }, [loggedClient?.id, cashbackConfig.ativo, concluidosCount, refetchCashbackSaldo]);

  // Poll periódico de pedidos enquanto o cliente está logado — mantém o card de
  // pedido em andamento atualizado (status, tempo decorrido, ETA). Roda mais rápido
  // (8s) quando há pedido ativo pra sensação de tempo real.
  useEffect(() => {
    if (!loggedClient) return;
    const hasActive = (clientPedidos || []).some(p =>
      !['concluido', 'cancelado', 'entregue', 'retirado'].includes(p.status)
    );
    const intervalMs = hasActive ? 8000 : 60000;
    const id = setInterval(() => { fetchClientPedidos(); }, intervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedClient?.id, clientPedidos.length, clientPedidos.map(p => p.status).join(',')]);

  // Tick por minuto pra forçar re-render do "há X min" e da previsão
  const [, setMinuteTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setMinuteTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // Buscar pedidos quando mudar para aba de pedidos
  useEffect(() => {
    if (activeTab === 'pedidos' && loggedClient) {
      fetchClientPedidos();
    }
  }, [activeTab, loggedClient]);

  useEffect(() => {
    const client = localStorage.getItem("client");
    if (client) {
      // Sem a pulseira (sessão de antes de 27/10/2026, achados 92/114) o cliente
      // entra de novo uma vez: cartão, perfil e endereços passaram a exigi-la
      if (!lerPulseira()) {
        try { localStorage.removeItem("client"); } catch (e) {}
        return;
      }
      try { setLoggedClient(JSON.parse(client)); } catch (e) {}
    }
  }, []);

  // Mantem a classe 'dark' do html sincronizada com o estado darkMode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  const toggleTheme = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    try { localStorage.setItem("cardapio_theme", newMode ? "dark" : "light"); } catch (e) {}
  };

  // Buscar configurações da empresa
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const res = await axios.get(`${API}/company/settings`);
        if (res.data) {
          setCompanySettings(res.data);
          // Meta Pixel da loja (por tenant): injeta o pixel e dispara PageView.
          // Guardado contra dupla-injeção (o SPA pode remontar o componente).
          const pixelId = res.data.meta_pixel_id;
          if (pixelId && !window.__nucleoPixelId) {
            window.__nucleoPixelId = pixelId;
            /* eslint-disable */
            !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
            /* eslint-enable */
            window.fbq('init', pixelId);
            window.fbq('track', 'PageView');
          }
          // Atualizar título do documento
          document.title = res.data.fantasy_name || res.data.company_name || "Núcleo";
          // Aplicar cor primaria do estabelecimento como CSS var (usada por bg-primary, border-primary etc)
          if (res.data.primary_color) {
            const root = document.documentElement;
            root.style.setProperty("--primary", res.data.primary_color);
            root.style.setProperty("--ring", res.data.primary_color);
          }
        }
      } catch (error) {
        console.error("Erro ao buscar configurações da empresa:", error);
      }
    };
    fetchCompanySettings();
  }, []);

  // Buscar stats publicas (tempo medio e taxa de entrega)
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/public/cardapio-stats`);
        if (res.data) setCardapioStats(res.data);
      } catch (error) {
        console.error("Erro ao buscar stats do cardapio:", error);
      }
    };
    fetchStats();
  }, []);

  // Cupons publicos visiveis (bannerzinho acima do rodape). Passa phone/customer_id do
  // cliente logado para que o backend filtre cupons que o cliente ja atingiu o limite de uso.
  useEffect(() => {
    const params = {};
    const phone = (loggedClient?.telefone || loggedClient?.phone || "").trim();
    if (phone) params.phone = phone;
    if (loggedClient?.id) params.customer_id = loggedClient.id;
    axios.get(`${API}/public/coupons/visible`, { params })
      .then(res => setVisibleCoupons(Array.isArray(res.data) ? res.data : []))
      .catch(() => setVisibleCoupons([]));
  }, [loggedClient?.id, loggedClient?.telefone, loggedClient?.phone]);

  // Detectar scroll para esconder header e barra de status em mobile
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setHeaderVisible(false); // Rolando para baixo
        setStatusBarVisible(false); // Esconder barra de status quando aberto
      } else {
        setHeaderVisible(true); // Rolando para cima
        setStatusBarVisible(true); // Mostrar barra de status
      }
      lastScrollY.current = currentScrollY;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fechar busca ao clicar fora (só se não tiver texto)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchOpen && !searchTerm) {
        const searchContainer = document.getElementById('search-container');
        const searchButton = document.getElementById('search-button');
        if (searchContainer && !searchContainer.contains(e.target) && 
            searchButton && !searchButton.contains(e.target)) {
          setSearchOpen(false);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchOpen, searchTerm]);

  // fetchPizzaConfig removido em v1.5.210. usePizzaPopup() faz fetch
  // product-aware (`/api/public/pizza/config?product_id=X`) sob demanda
  // quando o cliente clica numa pizza específica.

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchBusinessHours();
    fetchStoreStatus();
    fetchActivePromotions();

    // Recarregar produtos e status quando a janela voltar ao foco (para pegar atualizações)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchProducts();
        fetchStoreStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Polling do status da loja a cada 30s para refletir fechamento/abertura manual em tempo real
    const statusInterval = setInterval(() => {
      fetchStoreStatus();
    }, 30000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(statusInterval);
    };
  }, []);

  const fetchProducts = async () => {
    try {
      // Modo mesa usa o canal Mesa QR (próprio desde 24/10/2026; a migração
      // copiou o de Mesas): produtos e preço marcados para a Mesa QR.
      const data = await fetchWithFallback(
        modoMesa ? '/public/products?modulo=MesaQR' : '/public/products');
      setProducts(Array.isArray(data) ? data : []);
      // Buscar TODOS os produtos para fotos (incluindo insumos)
      try {
        const allData = await fetchWithFallback('/public/products/all');
        setAllProductsForPhotos(Array.isArray(allData) ? allData : []);
      } catch (e) {
        // Fallback: usar os produtos normais
        setAllProductsForPhotos(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);
      setProducts([]);
      setAllProductsForPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivePromotions = async () => {
    try {
      // Modo mesa: as promoções do salão (marcadas em Mesas), as mesmas do
      // garçom — o servidor responde "MesaQR" com a lista de Mesas e confere a
      // rodada por ela. A pizza segue o popup (Cardápio), igual ao garçom.
      // Promoção exclusiva de tipos de cliente (27/10/2026): com o cliente logado,
      // o servidor inclui as do tipo dele; sem login, elas não vêm (só o aviso abaixo).
      const doCliente = loggedClient?.id ? `&cliente_id=${encodeURIComponent(loggedClient.id)}` : '';
      const data = await fetchWithFallback(`/promotions/active?module=${modoMesa ? 'MesaQR' : 'Cardapio'}${doCliente}`);
      setActivePromotions(Array.isArray(data) ? data : []);
    } catch { setActivePromotions([]); }
    if (modoMesa || loggedClient?.id) { setPromocoesExclusivas([]); return; }
    try {
      const ex = await fetchWithFallback('/promotions/exclusivas?module=Cardapio');
      setPromocoesExclusivas(Array.isArray(ex) ? ex : []);
    } catch { setPromocoesExclusivas([]); }
  };
  // Logou/saiu: as promoções exclusivas do tipo do cliente entram ou saem
  const promocoesDoClienteCarregadas = useRef(false);
  useEffect(() => {
    if (!promocoesDoClienteCarregadas.current) { promocoesDoClienteCarregadas.current = true; return; }
    fetchActivePromotions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedClient?.id]);

  // Helper: buscar promoção ativa para um produto
  const getPromoForProduct = (productId, comboType) => {
    for (const promo of activePromotions) {
      const pp = (promo.products || []).find(p => p.product_id === productId);
      if (pp) {
        if (comboType === 'combo' && pp.discount_value_combo) {
          return { promo, value: pp.discount_value_combo, type: promo.discount_type };
        }
        // Pizza (`pp.sizes`) precisa do tamanho selecionado pra calcular o
        // desconto — não temos isso aqui. Retorna null pra cair no fluxo
        // "abrir popup" (lá o cálculo é feito por tamanho). Sem isso, o
        // valor `pp.discount_value` é `undefined` e quebra `.toFixed` no
        // cartão promocional.
        if (pp.sizes && typeof pp.sizes === 'object' && pp.discount_value == null) {
          return null;
        }
        return { promo, value: pp.discount_value, type: promo.discount_type };
      }
    }
    return null;
  };

  /** Promoção de pizza por tamanho (formato novo desde v1.5.219).
   * Retorna `{promo, sizes: {[size_id]: discount}}` ou null se a pizza não tem promo.
   * Usado pra mostrar badge "PROMO" + "A partir de R$X" no card de pizza. */
  const getPizzaPromoForProduct = (productId) => {
    for (const promo of activePromotions) {
      const pp = (promo.products || []).find(p => p.product_id === productId);
      if (pp && pp.sizes && typeof pp.sizes === 'object') {
        return { promo, sizes: pp.sizes };
      }
    }
    return null;
  };

  /** Calcula os tamanhos com desconto efetivo. Retorna `null` se nenhum tamanho
   * teve desconto real; caso contrário retorna:
   *   {
   *     discounted: [{sizeId, sizeName, finalPrice, basePrice}, ...] (ordenado por finalPrice asc),
   *     priceFrom:  menor preço promocional,
   *     onlyOne:    true se exatamente 1 tamanho tem desconto,
   *   }
   * Pega `prices_by_size` do próprio sabor (step pizza_flavor.items[product_id])
   * e nomes via step pizza_size.items. */
  const calcPizzaFromPrice = (product, pizzaPromo) => {
    if (!product || !pizzaPromo) return null;
    const steps = product.order_steps || [];
    let pricesBySize = null;
    let sizesArr = [];
    for (const s of steps) {
      if (s?.kind === 'pizza_flavor') {
        const own = (s.items || []).find(it => it.product_id === product.id);
        if (own?.prices_by_size) pricesBySize = own.prices_by_size;
      }
      if (s?.kind === 'pizza_size') {
        sizesArr = Array.isArray(s.items) ? s.items : [];
      }
    }
    if (!pricesBySize) return null;
    const sizesPromo = pizzaPromo.sizes || {};
    const dtype = pizzaPromo.promo?.discount_type;
    const discounted = [];
    for (const [sid, base] of Object.entries(pricesBySize)) {
      const baseF = Number(base) || 0;
      const disc = Number(sizesPromo[sid]) || 0;
      if (baseF <= 0 || disc <= 0) continue;
      let final = baseF;
      if (dtype === 'percentage') final = baseF * (1 - disc / 100);
      else if (dtype === 'fixed_price') final = disc;
      else continue;
      if (final > 0 && final < baseF) {
        const sz = sizesArr.find(x => x.id === sid);
        discounted.push({ sizeId: sid, sizeName: sz?.name || '', finalPrice: final, basePrice: baseF });
      }
    }
    if (discounted.length === 0) return null;
    discounted.sort((a, b) => a.finalPrice - b.finalPrice);
    return { discounted, priceFrom: discounted[0].finalPrice, hadDiscount: true, onlyOne: discounted.length === 1 };
  };

  // Helper: calcular preço com promoção
  const calcPromoPrice = (originalPrice, promoInfo) => {
    if (!promoInfo) return null;
    if (promoInfo.type === 'percentage') return originalPrice * (1 - promoInfo.value / 100);
    if (promoInfo.type === 'fixed_price') return promoInfo.value;
    return null; // pontuação não altera preço
  };

  /** Recalcula preço de pizza a partir do produto-mãe + meta (v1.5.358+).
   * Espelha o algoritmo do PizzaPopup handleAdd e do backend `_calc_expected_pizza_price`.
   * Usado por handleRepetirPedido pra detectar preço desatualizado e alertar cliente.
   *
   * `meta` = { size_id, size_name, flavor_ids: [...], border_id?, border_name? }
   * Retorna { price, pricingRule } ou null quando não dá pra recalcular
   * (produto sem order_steps, size_id sumiu, nenhum flavor com preço). */
  const calcPizzaPriceFromMeta = (product, meta, allPizzasPool = []) => {
    if (!product || !meta || !meta.size_id || !Array.isArray(meta.flavor_ids) || meta.flavor_ids.length === 0) return null;
    const steps = Array.isArray(product.order_steps) ? product.order_steps
      : (typeof product.order_steps === 'string' ? (() => { try { return JSON.parse(product.order_steps); } catch { return []; } })() : []);
    const sizeStep = steps.find(s => s?.kind === 'pizza_size');
    const flavorStep = steps.find(s => s?.kind === 'pizza_flavor');
    const borderStep = steps.find(s => s?.kind === 'pizza_border');
    if (!sizeStep || !flavorStep) return null;
    const sizeItem = (sizeStep.items || []).find(s => s.id === meta.size_id);
    if (!sizeItem) return null;
    const pricingRule = (sizeStep.pricing_rule || 'highest').toLowerCase();
    const norm = (s) => String(s || '').trim().toLowerCase();
    const pool = allPizzasPool.length ? allPizzasPool : (products || []).filter(p => p.product_type === 'pizza');
    const prodById = new Map(pool.map(p => [p.id, p]));

    const priceFromSource = (fid, sizeName) => {
      const src = prodById.get(fid);
      if (!src) return 0;
      const srcSteps = Array.isArray(src.order_steps) ? src.order_steps
        : (typeof src.order_steps === 'string' ? (() => { try { return JSON.parse(src.order_steps); } catch { return []; } })() : []);
      const srcSize = srcSteps.find(s => s?.kind === 'pizza_size');
      const srcFlavor = srcSteps.find(s => s?.kind === 'pizza_flavor');
      if (!srcSize || !srcFlavor) return 0;
      const matchSize = (srcSize.items || []).find(x => norm(x.name) === norm(sizeName));
      if (!matchSize) return 0;
      const ownInSrc = (srcFlavor.items || []).find(f => f.product_id === fid);
      return Number((ownInSrc?.prices_by_size || {})[matchSize.id] || 0);
    };

    const flavorPrices = [];
    for (const fid of meta.flavor_ids) {
      const local = (flavorStep.items || []).find(f => f.product_id === fid);
      let p = Number((local?.prices_by_size || {})[meta.size_id] || 0);
      if (p <= 0) p = priceFromSource(fid, sizeItem.name);
      if (p > 0) flavorPrices.push(p);
    }
    if (flavorPrices.length === 0) return null;

    let flavorsPrice;
    if (pricingRule === 'average') flavorsPrice = flavorPrices.reduce((s, v) => s + v, 0) / flavorPrices.length;
    else if (pricingRule === 'lowest') flavorsPrice = Math.min(...flavorPrices);
    else flavorsPrice = Math.max(...flavorPrices);

    let borderPrice = 0;
    if (meta.border_id && borderStep) {
      const b = (borderStep.items || []).find(x => x.id === meta.border_id);
      if (b) borderPrice = Number((b.prices_by_size || {})[meta.size_id] || 0);
    }
    return { price: Math.round((flavorsPrice + borderPrice) * 100) / 100, pricingRule };
  };

  /** "A partir de" PARA PIZZAS (v1.5.321+):
   * Retorna o MENOR preço entre os tamanhos ATIVOS de pizza. Ignora tamanhos
   * marcados como `available: false` (esgotados via bulk-action do lojista).
   * - Fonte do preço: `pizza_flavor.items[self].prices_by_size[size_id]`
   * - Fonte dos tamanhos ativos: `pizza_size.items[]` (filtrando `available !== false`)
   * Retorna null se produto não é pizza (sem os 2 steps) OU nenhum tamanho ativo tem preço.
   *
   * Diferente de `getFromStepsPrice` (que soma o mais barato de cada step —
   * tamanho + sabor + borda), aqui pegamos APENAS o menor preço do step de tamanho,
   * que é o valor "de referência" que o dono quer mostrar no card. */
  const getPizzaMinActivePrice = (product) => {
    if (!product) return null;
    const steps = product.order_steps || [];
    let pricesBySize = null;
    let sizes = [];
    for (const s of steps) {
      if (s?.kind === 'pizza_flavor') {
        const own = (s.items || []).find(it => it.product_id === product.id);
        if (own?.prices_by_size) pricesBySize = own.prices_by_size;
      }
      if (s?.kind === 'pizza_size') {
        sizes = Array.isArray(s.items) ? s.items : [];
      }
    }
    if (!pricesBySize || sizes.length === 0) return null;
    const activePrices = [];
    for (const sz of sizes) {
      if (sz?.available === false) continue;
      const p = Number(pricesBySize[sz.id] || 0);
      if (p > 0) activePrices.push(p);
    }
    if (activePrices.length === 0) return null;
    return Math.min(...activePrices);
  };

  // "A partir de": quando o produto tem preço base 0 mas etapas obrigatórias,
  // calcula o MENOR valor possível somando os N items mais baratos de cada etapa
  // (N = min_selections da etapa). Retorna null se não se aplica.
  const getFromStepsPrice = (product) => {
    if (!product || !product.order_steps || product.order_steps.length === 0) return null;
    const basePrice = Number(product.sale_price || 0) + Number(product.simple_price || 0);
    if (basePrice > 0) return null;
    let total = 0;
    for (const step of product.order_steps) {
      const minSel = Number(step?.min_selections || 0);
      if (minSel <= 0) continue;
      const items = Array.isArray(step?.items) ? step.items : [];
      if (items.length === 0) continue;
      // Bug histórico (v1.0.1075): usava `allProducts` que não existe no escopo
      // do CardapioPublicoContent. Fix v1.5.280: usa `products` com fallback
      // pra `allProductsForPhotos` (padrão usado em getSubitemsFromStep,
      // renderProductCard, etc.). O crash só aparecia em produtos com sale_price=0
      // + step.min_selections>0 (ex.: Pizza de Banana do UAI PIZZA).
      const productsPool = (products && products.length ? products : allProductsForPhotos) || [];
      const prices = items.map(it => {
        if (step?.sync_prices !== false) {
          const full = productsPool.find(p => p.id === it.product_id);
          return Number(full?.sale_price ?? it?.price_override ?? 0);
        }
        return Number(it?.price_override ?? 0);
      }).sort((a, b) => a - b);
      const take = Math.min(minSel, prices.length);
      for (let i = 0; i < take; i++) total += prices[i] || 0;
    }
    return total > 0 ? total : null;
  };

  const fetchCategories = async () => {
    try {
      const data = await fetchWithFallback('/public/categories');
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
      setCategories([]);
    }
  };

  const fetchBusinessHours = async () => {
    try {
      const data = await fetchWithFallback('/public/business-hours');
      setBusinessHours(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao carregar horários:", error);
      setBusinessHours([]);
    }
  };

  const fetchStoreStatus = async () => {
    try {
      const data = await fetchWithFallback('/store/status');
      setStoreStatus({
        is_open: data?.is_open !== false,
        reason: data?.reason || "",
        reopen_at: data?.reopen_at || "",
      });
    } catch (error) {
      console.error("Erro ao carregar status da loja:", error);
    }
  };

  // Função para verificar se está aberto agora (com suporte a múltiplos períodos)
  const getOpenStatus = () => {
    // Prioridade: fechamento manual pelo painel admin sobrepõe o horário de funcionamento.
    if (!storeStatus.is_open) {
      return {
        isOpen: false,
        closingTime: null,
        nextOpenTime: null,
        todayHours: null,
        manuallyClosed: true,
        closeReason: storeStatus.reason || "",
      };
    }

    if (businessHours.length === 0) return { isOpen: true, closingTime: '23:59', nextOpenTime: null };
    
    const now = new Date();
    // JavaScript: 0=Domingo, 1=Segunda... precisamos converter para nosso formato (0=Segunda, 6=Domingo)
    let dayOfWeek = now.getDay() - 1;
    if (dayOfWeek < 0) dayOfWeek = 6; // Domingo
    
    const todayHours = businessHours.find(h => h.day_of_week === dayOfWeek);
    
    // Loja 24h nesse dia: aberta sempre, sem horário de fechamento a mostrar.
    if (todayHours && todayHours.is_open && todayHours.is_24h) {
      return { isOpen: true, closingTime: null, nextOpenTime: null, todayHours, is24h: true };
    }

    if (!todayHours || !todayHours.is_open) {
      return { isOpen: false, closingTime: null, nextOpenTime: null, todayHours };
    }
    
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    // Converter horários para minutos
    const parseTime = (timeStr) => {
      const [h, m] = timeStr.split(':').map(Number);
      return h * 60 + m;
    };
    
    // Períodos do dia em ordem (1º, 2º e 3º quando existirem). Percorre uma vez:
    // antes de um período = fechado e abre nele; dentro = aberto até o fim dele,
    // com o próximo como volta; passou de todos = já fechou por hoje.
    const periodos = [[todayHours.opening_time, todayHours.closing_time]];
    if (todayHours.has_second_period) periodos.push([todayHours.opening_time_2, todayHours.closing_time_2]);
    if (todayHours.has_third_period) periodos.push([todayHours.opening_time_3, todayHours.closing_time_3]);

    const validos = periodos.filter(([abre, fecha]) => abre && fecha);
    for (let i = 0; i < validos.length; i++) {
      const [abre, fecha] = validos[i];
      if (currentTime < parseTime(abre)) {
        return { isOpen: false, closingTime: null, nextOpenTime: abre, todayHours };
      }
      // 23:59 é o último instante do dia (dono, 25/10/2026): no minuto 23:59 a
      // loja continua aberta e, se o dia seguinte abre 00:00, atravessa a virada
      if (currentTime < (fecha === "23:59" ? 1440 : parseTime(fecha))) {
        const proximo = validos[i + 1] ? validos[i + 1][0] : null;
        return { isOpen: true, closingTime: fecha, nextOpenTime: proximo, todayHours };
      }
    }

    // Já fechou
    return { isOpen: false, closingTime: null, nextOpenTime: null, todayHours };
  };

  const { isOpen, closingTime, nextOpenTime, todayHours, manuallyClosed, closeReason } = getOpenStatus();

  const categoriesWithProducts = useMemo(() => {
    const categorySet = new Set(products.map(p => p.category).filter(Boolean));
    const baseList = categories.filter(cat => categorySet.has(cat.name));
    // Se há um perfil de cardápio ativo (turno), filtra e ordena por ele
    const profileCatIds = activeCardapioProfile?.categories || [];
    if (profileCatIds.length > 0) {
      const allowed = baseList.filter(cat => profileCatIds.includes(cat.id));
      // Reordena conforme a ordem do perfil
      allowed.sort((a, b) => profileCatIds.indexOf(a.id) - profileCatIds.indexOf(b.id));
      return allowed;
    }
    // Sem perfil ativo: ordem global do backend (display_order)
    return baseList;
  }, [categories, products, activeCardapioProfile]);

  const filteredProducts = useMemo(() => {
    let filtered = products;
    if (selectedCategory) filtered = filtered.filter(p => p.category === selectedCategory);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => p.name.toLowerCase().includes(term) || (p.description && p.description.toLowerCase().includes(term)));
    }
    return filtered;
  }, [products, selectedCategory, searchTerm]);

  const productsByCategory = useMemo(() => {
    const grouped = {};
    filteredProducts.forEach(product => {
      const cat = product.category || "Outros";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(product);
    });
    return grouped;
  }, [filteredProducts]);

  // "Categoria esgotada" olha a categoria inteira, não o resultado da busca:
  // calculado sobre a lista filtrada, buscar "coca" quando só a de 2 L está em
  // falta fechava a categoria e escondia as outras (achado 17).
  const categoriasEsgotadas = useMemo(() => {
    const porCategoria = {};
    products.forEach(product => {
      const cat = product.category || "Outros";
      if (!porCategoria[cat]) porCategoria[cat] = [];
      porCategoria[cat].push(product);
    });
    return new Set(Object.keys(porCategoria).filter(cat =>
      porCategoria[cat].every(p => p.available === false)));
  }, [products]);

  // ScrollSpy: observa cada secao de categoria e marca como ativa a que estiver
  // proxima do topo da viewport. rootMargin "-100px 0px -60% 0px" cria uma faixa
  // de deteccao logo abaixo do top bar+chips e ate 40% da viewport — a primeira
  // secao a entrar nessa faixa e considerada ativa.
  useEffect(() => {
    if (activeTab !== 'cardapio') return;
    const cats = Object.keys(productsByCategory);
    if (cats.length === 0) return;
    const elements = cats
      .map(c => document.getElementById(`cat-${makeProductSlug(c) || 'sem-categoria'}`))
      .filter(Boolean);
    if (elements.length === 0) return;
    // Mantem visiveis: setamos a 1a categoria como ativa por padrao no inicio
    setActiveCategorySlug(prev => prev || (elements[0].id.replace(/^cat-/, '')));
    const observer = new IntersectionObserver((entries) => {
      // Pega o primeiro entry intersectando (mais ao topo)
      const visible = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length > 0) {
        const slug = visible[0].target.id.replace(/^cat-/, '');
        setActiveCategorySlug(slug);
      }
    }, { rootMargin: '-100px 0px -60% 0px', threshold: [0, 0.1] });
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [productsByCategory, activeTab]);

  // Atualiza visibilidade das setas laterais conforme posicao de scroll horizontal
  // dos chips. Listener no container + chamada inicial pra setar estado correto.
  useEffect(() => {
    const el = chipsScrollRef.current;
    if (!el) return;
    const update = () => {
      const sl = el.scrollLeft;
      const max = el.scrollWidth - el.clientWidth;
      setChipsCanScrollLeft(sl > 4);
      setChipsCanScrollRight(sl < max - 4);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [productsByCategory, activeTab]);

  // Quando a categoria ativa muda, centraliza o chip correspondente na barra.
  // Antes so reagia quando o chip saia completamente do container — agora dispara
  // tambem quando o chip esta a < 64px de qualquer borda OU quando ainda ha chip
  // visivel adjacente que nao esta totalmente visivel (parcialmente cortado).
  // Resultado: o chip ativo fica sempre confortavelmente no centro da barra.
  useEffect(() => {
    if (!activeCategorySlug) return;
    const container = chipsScrollRef.current;
    if (!container) return;
    const chipEl = container.querySelector(`[data-cat-slug="${activeCategorySlug}"]`);
    if (!chipEl) return;
    const cl = container.getBoundingClientRect();
    const el = chipEl.getBoundingClientRect();
    const EDGE_THRESHOLD = 64; // px de margem confortavel das bordas
    const tooCloseLeft = el.left < cl.left + EDGE_THRESHOLD;
    const tooCloseRight = el.right > cl.right - EDGE_THRESHOLD;
    if (tooCloseLeft || tooCloseRight) {
      const offset = chipEl.offsetLeft - container.offsetWidth / 2 + chipEl.offsetWidth / 2;
      container.scrollTo({ left: Math.max(0, offset), behavior: 'smooth' });
    }
  }, [activeCategorySlug]);

  const addToCart = (product, { openDrawer = true } = {}) => {
    // TURNO: funil unico. Todo caminho que enche o carrinho (card, popup, pizza,
    // cross-sell, deep link, peca-de-novo) passa por aqui — barrar so aqui evita
    // a lista de guardas espalhadas que uma hora fica desatualizada.
    if (foraDoTurno(product)) {
      avisoForaDoTurno(product?.name);
      return;
    }
    trackFunnel("cart", { product_id: product?.id });
    // Sugestões Inteligentes: produto com opções tocado numa sugestão chega aqui pelo popup.
    const pendente = sugestaoPendenteRef.current;
    if (pendente && !product.sugestao_lugar && !product._editingCartItemId && pendente.id === String(product.id)
        && Date.now() - pendente.em < 10 * 60 * 1000) {
      product = { ...product, sugestao_lugar: pendente.lugar, sugestao_qtd: product.quantity || 1 };
      sugestaoPendenteRef.current = null;
      registrarAdicionada(pendente.lugar, product.id);
    }
    if (!product._editingCartItemId && !product.sugestao_lugar) {
      // "Combina com o seu X": painel flutuante no "+" (sacola fechada) ou topo das
      // sugestões da sacola quando o popup abre a sacola depois de adicionar.
      setUltimoAdicionado({ id: String(product.id), nome: product.name, em: Date.now() });
    }
    // Campanha Inteligente: marca o item com a oferta que o preço dele usou.
    if (!product._editingCartItemId) product = marcarOferta(product, ofertasDia, activePromotions);
    // (ref pizza sincronizado via useEffect abaixo)
    const qty = product.quantity || 1;
    // Modo edicao: substitui item existente identificado por _editingCartItemId
    // mantendo o mesmo cartItemId pra preservar posicao no carrinho.
    if (product._editingCartItemId) {
      const editId = product._editingCartItemId;
      setCart(prev => prev.map(it => (
        (it.cartItemId || it.id) === editId
          ? {
              ...it,
              quantity: qty,
              observation: product.observation || null,
              combo_type: product.combo_type || null,
              step_selections: product.step_selections || null,
              subitems: Array.isArray(product.subitems) ? product.subitems : it.subitems,
              etapas: Array.isArray(product.etapas) ? product.etapas : it.etapas,
              final_price: product.final_price || it.final_price,
              sale_price: product.final_price || it.sale_price,
            }
          : it
      )));
      setEditingCartItem(null);
      return;
    }
    setCart(prev => {
      // Item personalizado NUNCA agrupa. `subitems`/`etapas` entram na conta
      // porque a PIZZA guarda tamanho, sabores e borda ali — e em lugar nenhum
      // mais: ela nao seta observation/combo_type/step_selections. Sem isso,
      // uma Grande e uma Pequena do mesmo sabor viravam a MESMA linha (o `id`
      // da pizza e' o do sabor primario, nao o da combinacao) e a segunda sumia
      // com preco, tamanho e borda. Mesma regra que o PDV (Delivery.js) ja
      // aplicava corretamente.
      if (temPersonalizacao(product)) {
        return [...prev, { ...product, quantity: qty, cartItemId: Date.now() }];
      }
      // Sem personalização, agrupa por id (apenas itens simples sem combo_type)
      // Linha com oferta e linha no preço cheio do mesmo produto nunca se juntam.
      const existing = prev.find(item => item.id === product.id && !temPersonalizacao(item)
        && (item.oferta_chave || null) === (product.oferta_chave || null));
      if (existing) {
        // Casa pelo cartItemId do item ENCONTRADO — antes o `map` usava um
        // critério mais frouxo que o `find` e podia somar na linha errada.
        const alvo = existing.cartItemId || existing.id;
        return prev.map(item =>
          (item.cartItemId || item.id) === alvo
            ? { ...item, quantity: item.quantity + qty }
            : item
        );
      }
      return [...prev, { ...product, quantity: qty, cartItemId: Date.now() }];
    });
    if (openDrawer) abrirSacola();
  };

  // Sincroniza ref pro usePizzaPopup() conseguir chamar addToCart (declarado
  // depois do hook por restrição de ordenação)
  useEffect(() => { _addToCartRef.current = addToCart; });

  // Refazer um pedido anterior — adiciona todos os itens ao carrinho.
  // Filtra produtos descontinuados (nao existem mais) e esgotados (available=false)
  // e avisa o cliente quantos foram pulados em vez de falhar silenciosamente.
  const handleRepetirPedido = (pedido, { daConversa = false } = {}) => {
    const items = pedido?.items || [];
    if (items.length === 0) { toast.error("Pedido sem itens."); return; }
    // Card bloqueado (esgotado / indisponível) nunca vai pro carrinho, por qualquer caminho.
    const bloqueado = statusPedidoAntigo(pedido, { aVendaPorId, catalogoPorId });
    if (bloqueado) { toast.error(mensagemBloqueio(bloqueado), { duration: 6000 }); return; }
    // v1.5.365+ FIX (pedido #24076 UAI PIZZA): `/api/public/products/all` retorna
    // insumos + fotos mas SEM `product_type` e SEM `order_steps` (é lite). Isso
    // fazia `inferPizzaMeta` cair no guard `prod.product_type !== 'pizza'` e
    // retornar null → `pizza_meta` nulo no payload → backend caía no `continue`
    // (retrocompat sem meta) → cobrava o preço defasado do pedido antigo.
    // Solução: montar allProds priorizando `products` (endpoint /public/products
    // tem product_type + order_steps corretos), e só usar `allProductsForPhotos`
    // pra IDs QUE NÃO ESTÃO em `products` (insumos, fotos extras).
    const productsById = new Map((products || []).map(p => [String(p.id || ''), p]));
    const allProds = [
      ...(products || []),
      ...((allProductsForPhotos || []).filter(x => x?.id && !productsById.has(String(x.id)))),
    ];
    const allPizzasPool = allProds.filter(p => p.product_type === 'pizza');
    const now = Date.now();
    const skipped = []; // { name, reason: 'discontinued' | 'sold_out' | 'fora_do_turno' }
    const priceChanges = []; // { name, oldPrice, newPrice }

    // v1.5.358+: infere pizza_meta a partir dos subitems e do nome do item
    // ("Pizza Grande · 2 sabores" → sizeName = "Grande"). Necessário pra
    // recalcular preço da pizza pelo pricing_rule atual (o preço salvo no
    // pedido antigo pode estar defasado — bug do "peça de novo").
    const inferPizzaMeta = (it, prod) => {
      if (!prod || prod.product_type !== 'pizza') return null;
      const steps = Array.isArray(prod.order_steps) ? prod.order_steps : [];
      const sizeStep = steps.find(s => s?.kind === 'pizza_size');
      if (!sizeStep) return null;
      const rawName = String(it.nome || it.name || '');
      // Formato do headline: "Pizza {SizeName} · {N} sabores" ou "Pizza {SizeName} · {FlavorName}"
      const m = rawName.match(/^Pizza\s+([^·]+?)\s*·/i);
      const sizeName = m ? m[1].trim() : null;
      const sizeItem = sizeName
        ? (sizeStep.items || []).find(s => String(s.name || '').trim().toLowerCase() === sizeName.toLowerCase())
        : null;
      if (!sizeItem) return null;
      // flavor_ids: extrai dos subitems (step_name === "Sabores" ou fallback: qualquer subitem com product_id)
      const subs = Array.isArray(it.subitems) ? it.subitems : [];
      const flavorSubs = subs.filter(s => s && s.product_id && (String(s.step_name || '').toLowerCase() === 'sabores' || !s.step_name));
      const flavor_ids = flavorSubs.map(s => s.product_id).filter(Boolean);
      if (flavor_ids.length === 0) return null;
      // border: subitem com step_name === "Borda" (nome único) — não temos border_id salvo,
      // então tentamos casar por nome no pizza_border.items
      let border_id = null;
      let border_name = null;
      const borderSub = subs.find(s => String(s?.step_name || '').toLowerCase() === 'borda');
      if (borderSub?.nome) {
        const borderStep = steps.find(s => s?.kind === 'pizza_border');
        const match = (borderStep?.items || []).find(b => String(b.name || '').trim().toLowerCase() === String(borderSub.nome).trim().toLowerCase());
        if (match) { border_id = match.id; border_name = match.name; }
      }
      return {
        size_id: sizeItem.id,
        size_name: sizeItem.name,
        flavor_ids,
        border_id,
        border_name,
        pricing_rule: sizeStep.pricing_rule || 'highest',
      };
    };

    const cartItems = items.map((it, idx) => {
      const id = it.product_id || it.id;
      const name = it.nome || it.name || it.product_name || "";
      // Item principal só volta se estiver À VENDA agora: `products` é o
      // /public/products, que já exclui escondido, arquivado e categoria oculta.
      // Caso UAI #24992: o açaí de categoria escondida voltava pelo
      // `allProductsForPhotos` (lista tudo) e o pedido saía.
      const prod = id ? (productsById.get(String(id)) || null) : null;
      // Pula itens cujo produto nao existe mais ou esta esgotado
      if (id && !prod) {
        // /public/products também recorta por turno: se o produto existe no
        // catálogo completo e só está fora do horário, avisa o horário.
        const noCatalogo = allProds.find((p) => String(p.id || "") === String(id));
        const soTurno = !!noCatalogo && noCatalogo.hidden !== true && noCatalogo.available !== false && foraDoTurno(noCatalogo);
        skipped.push({ name, reason: soTurno ? 'fora_do_turno' : 'discontinued' });
        return null;
      }
      if (prod && prod.available === false) { skipped.push({ name, reason: 'sold_out' }); return null; }
      // Complemento (qualquer etapa) escondido/esgotado → pula o item inteiro em
      // vez de remontar sem ele: o preço salvo já inclui os adicionais.
      const complementoFora = (Array.isArray(it.subitems) ? it.subitems : []).find((s) => {
        const sp = s?.product_id ? allProds.find((p) => String(p.id || "") === String(s.product_id)) : null;
        return sp && (sp.hidden === true || sp.available === false);
      });
      if (complementoFora) {
        const sp = allProds.find((p) => String(p.id || "") === String(complementoFora.product_id));
        skipped.push({
          name: `${name} (${complementoFora.nome || complementoFora.name || sp?.name || 'complemento'})`,
          reason: sp?.available === false ? 'sold_out' : 'discontinued',
        });
        return null;
      }
      // TURNO: item do pedido antigo que nao pertence ao turno de agora.
      if (prod && foraDoTurno(prod)) { skipped.push({ name, reason: 'fora_do_turno' }); return null; }
      const qty = it.quantidade || it.quantity || 1;
      // pedido.items[].preco ja inclui o valor dos adicionais — usar direto (NAO somar
      // subitems.preco de novo, caso contrario dobra o valor)
      // Campanha Inteligente: "Peça de novo" não aplica oferta — parte do preço cheio.
      const oldPrice = Number(it.oferta_preco_cheio ?? it.preco ?? it.price ?? it.final_price ?? 0);
      let finalPrice = oldPrice;
      let pizzaMeta = null;
      let priceUpdated = null;

      // Pizza: recalcula preço usando pricing_rule atual do produto.
      if (prod?.product_type === 'pizza') {
        pizzaMeta = inferPizzaMeta(it, prod);
        if (pizzaMeta) {
          const calc = calcPizzaPriceFromMeta(prod, pizzaMeta, allPizzasPool);
          if (calc && calc.price > 0) {
            // Soma adicionais custom (etapas não-pizza_*) que estejam no pedido antigo.
            // O `oldPrice` inclui esses adicionais — calculamos o delta pra reaplicar.
            const subs = Array.isArray(it.subitems) ? it.subitems : [];
            const nonPizzaSubtotal = subs
              .filter(s => s && s.step_name && !['sabores', 'borda'].includes(String(s.step_name).toLowerCase()))
              .reduce((sum, s) => sum + Number(s.preco || s.price || 0), 0);
            const recalcTotal = Math.round((calc.price + nonPizzaSubtotal) * 100) / 100;
            if (Math.abs(recalcTotal - oldPrice) > 0.01) {
              priceUpdated = { oldPrice, newPrice: recalcTotal };
              priceChanges.push({ name, oldPrice, newPrice: recalcTotal });
            }
            finalPrice = recalcTotal;
          }
        }
      }

      const obs = it.observacao || it.observation || null;
      const subitems = Array.isArray(it.subitems) ? [...it.subitems] : [];
      const etapas = Array.isArray(it.etapas) ? [...it.etapas] : [];
      const stepSelections = it.step_selections && typeof it.step_selections === "object" ? { ...it.step_selections } : null;
      let orderSteps = null;
      if (prod && Array.isArray(prod.order_steps) && prod.order_steps.length) orderSteps = prod.order_steps;
      return {
        id,
        name,
        quantity: qty,
        sale_price: finalPrice,
        final_price: finalPrice,
        observation: obs,
        combo_type: it.combo_type || null,
        subitems,
        etapas,
        step_selections: stepSelections,
        order_steps: orderSteps,
        photo_url: it.photo_url || null,
        cartItemId: now + idx,
        pizza: pizzaMeta || undefined,
        _price_updated: priceUpdated || undefined,
      };
    }).filter(Boolean);

    if (cartItems.length === 0) {
      const soTurno = skipped.length > 0 && skipped.every(s => s.reason === 'fora_do_turno');
      toast.error(
        soTurno
          ? `Esse pedido não faz parte do cardápio deste horário${turnoVoltaEm ? ` — ${turnoVoltaEm}` : ''}.`
          : "Nenhum item desse pedido está disponível agora.",
        { duration: 6000 }
      );
      return;
    }
    setCart(cartItems);
    if (skipped.length > 0) {
      const discontinued = skipped.filter(s => s.reason === 'discontinued').length;
      const soldOut = skipped.filter(s => s.reason === 'sold_out').length;
      const foraTurno = skipped.filter(s => s.reason === 'fora_do_turno').length;
      const parts = [];
      if (discontinued) parts.push(`${discontinued} indisponível${discontinued > 1 ? 'is' : ''}`);
      if (soldOut) parts.push(`${soldOut} esgotado${soldOut > 1 ? 's' : ''}`);
      if (foraTurno) parts.push(`${foraTurno} fora do horário${turnoVoltaEm ? ` (${turnoVoltaEm})` : ''}`);
      toast.warning(`Adicionei ${cartItems.length} item${cartItems.length > 1 ? 's' : ''} — ${parts.join(' e ')} pulado${skipped.length > 1 ? 's' : ''}: ${skipped.map(s => s.name).filter(Boolean).join(', ')}`, { duration: 6000 });
    } else {
      toast.success(daConversa ? "Seu pedido da conversa já está no carrinho. É só conferir e finalizar ✅" : `Pedido ${pedido.codigo || ''} adicionado ao carrinho!`);
    }
    // v1.5.358+: aviso de preço atualizado (pizza recalculada pelo pricing_rule atual)
    if (priceChanges.length > 0) {
      const first = priceChanges[0];
      const restMsg = priceChanges.length > 1 ? ` (+ ${priceChanges.length - 1} outro${priceChanges.length > 2 ? 's' : ''})` : '';
      toast.warning(
        `Preço atualizado: ${first.name} passou de R$ ${first.oldPrice.toFixed(2).replace('.', ',')} pra R$ ${first.newPrice.toFixed(2).replace('.', ',')}${restMsg}. Confira antes de finalizar.`,
        { duration: 8000 }
      );
    }
    abrirSacola();
  };

  // Link da conversa com a CLEO (`?r=`): o que ela anotou entra no carrinho uma vez,
  // depois que o cardápio carregou. Carrinho já com itens não é mexido.
  useEffect(() => {
    // Espera as duas listas: a de venda e a completa (é nela que as escolhas dos itens são conferidas).
    if (!comandaDaConversa || !products.length || !allProductsForPhotos.length) return;
    esquecerComanda();
    if (cart.length === 0) handleRepetirPedido({ items: comandaDaConversa }, { daConversa: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comandaDaConversa, products.length, allProductsForPhotos.length]);

  const removeFromCart = (cartItemId) => setCart(prev => prev.filter(item => (item.cartItemId || item.id) !== cartItemId));

  const updateQuantity = (cartItemId, delta) => {
    const alvo = cart.find(item => (item.cartItemId || item.id) === cartItemId);
    // Quilo/litro (30/09/2026): o − / + anda 0,1 kg (ou L) e para no mínimo de
    // 0,1 — o ±1 levava 0,350 kg a 1,350 kg. A linha conta 1 unidade da oferta,
    // então mudar o peso não esbarra no limite.
    const medida = medidaDoItem(alvo, products);
    if (delta > 0 && alvo?.oferta_chave && !medida) {
      const limite = ofertasDia.config?.limite_unidades || 1;
      if ((unidadesPorChave(cart)[alvo.oferta_chave] || 0) + delta > limite) {
        toast.info(`A oferta vale para até ${limite} ${limite === 1 ? "unidade" : "unidades"} por dia. Pra levar mais, adicione de novo pelo cardápio no preço normal.`, { duration: 6000 });
        return;
      }
    }
    setCart(prev => prev.map(item => {
      if ((item.cartItemId || item.id) === cartItemId) {
        if (medida) {
          const passo = delta > 0 ? PASSO_PESO : -PASSO_PESO;
          return { ...item, quantity: Math.max(PASSO_PESO, parseFloat((Number(item.quantity) + passo).toFixed(3))) };
        }
        const newQty = item.quantity + delta;
        return newQty <= 0 ? null : { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const cartTotal = useMemo(() => cart.reduce((sum, item) => sum + ((item.final_price || item.sale_price) * item.quantity), 0), [cart]);
  const cartItemsCount = useMemo(() => contarItens(cart), [cart]); // item do carrinho traz sale_unit/is_divisible

  // Helper: id do produto em cada item (cobre variantes de naming usados na adicao ao carrinho)
  const itemProductId = (item) => item?.product_id || item?.id;
  // Retorna a promocao ativa do produto se ela NAO aceita cupom (accepts_coupon=0).
  // Usado para flagar o produto como 'nao couponavel' no card e excluir do calculo do cupom.
  const getNonCouponablePromo = (productId) => {
    if (!productId) return null;
    for (const promo of activePromotions || []) {
      const pp = (promo.products || []).find(p => p.product_id === productId);
      if (!pp) continue;
      const accepts = promo.accepts_coupon === 1 || promo.accepts_coupon === true;
      if (!accepts) return promo;
    }
    return null;
  };
  // Subtotal apenas dos itens que aceitam cupom (exclui itens em promo com accepts_coupon=0)
  const couponableSubtotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      const pid = itemProductId(item);
      if (item.oferta_chave && !item.oferta_acumula_cupom) return sum;
      if (getNonCouponablePromo(pid)) return sum;
      return sum + ((item.final_price || item.sale_price) * item.quantity);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, activePromotions]);

  const scrollCategories = (direction) => {
    const container = document.getElementById('categories-scroll');
    if (container) container.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  const handleLoginSuccess = (type, userData) => {
    if (type === "admin" && onAdminLogin) {
      onAdminLogin(userData);
    } else {
      setLoggedClient(userData);
      // Se tem itens no carrinho, abrir checkout automaticamente
      if (cart.length > 0) {
        setShowLoginModal(false);
        setTimeout(() => setCheckoutOpen(true), 300);
      }
    }
  };

  const handleLogout = () => setLoggedClient(null);
  const handleClientUpdate = (updatedClient) => setLoggedClient(updatedClient);

  // Classes de tema
  const t = {
    bg: darkMode ? 'bg-zinc-900' : 'bg-gray-50',
    bgCard: darkMode ? 'bg-zinc-800' : 'bg-white',
    bgHeader: darkMode ? 'bg-zinc-950' : 'bg-white border-b border-gray-100',
    bgInput: darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-300',
    bgMuted: darkMode ? 'bg-zinc-700' : 'bg-gray-100',
    bgCart: darkMode ? 'bg-zinc-800' : 'bg-white',
    bgCartItem: darkMode ? 'bg-zinc-900' : 'bg-gray-50',
    border: darkMode ? 'border-zinc-700' : 'border-gray-200',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textMuted: darkMode ? 'text-zinc-400' : 'text-gray-500',
    textMuted2: darkMode ? 'text-zinc-500' : 'text-gray-400',
    btnCat: darkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
  };

  // Faixa verde "X ofertas para desbloquear": explode, desbloqueia e abre os cupons.
  // Mesma ação no rodapé (celular) e dentro da sacola lateral (computador).
  const desbloquearCupons = () => {
    setCouponsExploding(true);
    setTimeout(() => {
      setCouponsUnlocked(true);
      setCouponsExploding(false);
      setCouponsModalOpen(true);
    }, 700);
  };
  const rotuloFazerPedido = modoMesa
    ? "Enviar para a cozinha"
    : !getOpenStatus().isOpen && schedulingConfig.ativo ? "Agendar Pedido" : "Fazer Pedido";
  const produtosFotoSacola = (allProductsForPhotos && allProductsForPhotos.length ? allProductsForPhotos : products) || [];
  const itensDaSacola = cart.map(item => (
    <ItemSacola
      key={item.cartItemId || item.id}
      item={item}
      produtosFoto={produtosFotoSacola}
      produtosVenda={products}
      t={t}
      darkMode={darkMode}
      grande={sacolaLateral}
      fotoDe={getImageUrl}
      semCupom={!itemAceitaCupom(item, activePromotions)}
      passoPeso={PASSO_PESO}
      onRemover={() => removeFromCart(item.cartItemId || item.id)}
      onAlterarQtd={(delta) => updateQuantity(item.cartItemId || item.id, delta)}
      onEditar={() => openEditCartItem(item)}
    />
  ));

  // Barra de categorias - chips horizontais sticky abaixo do top bar.
  // Click leva o cliente direto pra secao da categoria via scrollIntoView,
  // com scroll-mt-[100px] no anchor pra compensar a altura do top + chips.
  // Chip ativo (categoria atualmente em viewport) destacado em laranja
  // via scrollspy IntersectionObserver. Setas laterais aparecem em
  // desktop quando ha conteudo a rolar antes/depois.
  // No computador com a sacola lateral ela sai do <main> e fica acima da grade,
  // na largura toda (pedido do dono, 02/10/2026); no celular continua no lugar.
  const renderBarraCategorias = () => {
    const orderedCats = categoriesWithProducts
      .filter(cat => productsByCategory[cat.name])
      .map(cat => cat.name);
    // Perfil ativo: só categorias do perfil aparecem nos chips
    const profileActive = !!(activeCardapioProfile && (activeCardapioProfile.categories || []).length > 0);
    const otherCats = profileActive ? [] : Object.keys(productsByCategory)
      .filter(catName => !categoriesWithProducts.some(cat => cat.name === catName));
    const allCats = [...orderedCats, ...otherCats];
    if (allCats.length < 2) return null;
    const scrollChips = (delta) => {
      const el = chipsScrollRef.current;
      if (el) el.scrollBy({ left: delta, behavior: 'smooth' });
    };
    return (
      <div className={`sticky ${searchOpen ? 'top-[96px]' : 'top-[44px]'} z-[19] ${t.bg} border-b ${t.border} ${sacolaLateral ? 'mt-2' : '-mt-2'} mb-3`} data-e2e="barra-categorias">
        <div className="relative">
          {/* Seta esquerda - apenas desktop, visivel quando ha scroll a esquerda */}
          {chipsCanScrollLeft && (
            <button
              type="button"
              onClick={() => scrollChips(-220)}
              className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full items-center justify-center shadow-md transition-colors ${darkMode ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200 ring-1 ring-zinc-700' : 'bg-white hover:bg-gray-50 text-gray-700 ring-1 ring-gray-200'}`}
              aria-label="Categorias anteriores"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {/* Seta direita - apenas desktop, visivel quando ha scroll a direita */}
          {chipsCanScrollRight && (
            <button
              type="button"
              onClick={() => scrollChips(220)}
              className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full items-center justify-center shadow-md transition-colors ${darkMode ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200 ring-1 ring-zinc-700' : 'bg-white hover:bg-gray-50 text-gray-700 ring-1 ring-gray-200'}`}
              aria-label="Próximas categorias"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
          <div
            ref={chipsScrollRef}
            className="flex gap-2 overflow-x-auto px-3 py-2 md:px-10 lg:[justify-content:safe_center]"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {allCats.map(catName => {
              const slug = makeProductSlug(catName) || 'sem-categoria';
              const isActive = activeCategorySlug === slug;
              const catProducts = productsByCategory[catName] || [];
              const allUnavailable = catProducts.length > 0 && categoriasEsgotadas.has(catName);
              return (
                <button
                  key={catName}
                  type="button"
                  data-cat-slug={slug}
                  title={allUnavailable ? `${catName} (categoria esgotada)` : undefined}
                  onClick={() => {
                    setActiveCategorySlug(slug);
                    const el = document.getElementById(`cat-${slug}`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all active:scale-95 inline-flex items-center gap-1.5 ${
                    isActive
                      ? 'bg-orange-500 border-orange-500 text-white shadow-md shadow-orange-500/30'
                      : allUnavailable
                        ? (darkMode
                            ? 'border-red-500/30 bg-red-500/5 text-zinc-400 hover:border-red-500/50'
                            : 'border-red-200 bg-red-50/50 text-gray-500 hover:border-red-300')
                        : (darkMode
                            ? 'border-zinc-700 bg-zinc-800 text-zinc-200 hover:border-orange-500 hover:bg-orange-500/10 hover:text-orange-400'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-orange-500 hover:bg-orange-50 hover:text-orange-600')
                  }`}
                >
                  {allUnavailable && (
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${isActive ? 'bg-white/80' : 'bg-red-500'}`} />
                  )}
                  <span className={allUnavailable && !isActive ? 'line-through' : ''}>{catName}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <CardapioCarregando escuro={darkMode} />;
  }

  // Computador com sacola lateral: barra de categorias acima da grade (largura toda)
  // e a sacola gruda logo abaixo dela — topo 44 px (96 com a busca aberta) + barra + 12.
  const barraCategoriasNoTopo = sacolaLateral ? renderBarraCategorias() : null;
  const topoDaSacola = (searchOpen ? 96 : 44) + (barraCategoriasNoTopo ? ALTURA_BARRA_CATEGORIAS : 0) + 12;

  return (
    <div className={`min-h-screen ${t.bg} ${t.text}`}>
      {/* Mesa QR: cliente que escaneou o código da mesa. Enquanto ele está no
          cardápio isto desenha só a faixa do topo; nas outras etapas (entrar,
          CPF, conferir, comanda) cobre a tela inteira. */}
      <MesaQrFluxo
        mesaQr={mesaQr}
        cart={cart}
        produtos={allProductsForPhotos}
        fotoDe={getImageUrl}
        darkMode={darkMode}
        nomeLoja={companySettings?.fantasy_name || companySettings?.company_name || ""}
        logoLoja={companySettings?.logo_url ? getImageUrl(companySettings.logo_url) : null}
        cashbackPct={cashbackConfig?.ativo ? Number(cashbackConfig.percentual || 0) : 0}
        onRodadaEnviada={() => { setCart([]); clearCartDraft(); setCartOpen(false); }}
        onPagar={() => setPagamentoMesaAberto(true)}
      />
      {modoMesa && pagamentoMesaAberto && (
        <PagamentoMesa
          token={mesaToken}
          quem={mesaQr.quem}
          mesa={mesaQr.mesa}
          eu={mesaQr.participantes?.eu || mesaQr.cliente}
          fotoDe={getImageUrl}
          darkMode={darkMode}
          onFechar={() => { setPagamentoMesaAberto(false); mesaQr.recarregarComanda(); }}
          onPago={() => { setPagamentoMesaAberto(false); abrirComprovante(mesaQr.codigoSaida); }}
        />
      )}
      {/* MODO COPA — confetes (fundo) + bunting + bolas/bandeiras flutuando.
          Ribbon "É HEXA" fica DEPOIS do info-card (mais embaixo no JSX). */}
      {copa.active && <CopaOverlay />}
      <div className={`max-w-[480px] ${sacolaLateral ? 'lg:max-w-[1280px]' : 'lg:max-w-[1120px]'} mx-auto relative copa-content`}>
        {/* Top Bar compacta - sticky, aparece ao rolar - cor do estabelecimento */}
        <div className="sticky top-0 z-30 bg-primary px-3 py-2 flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              if (activeTab !== 'cardapio') {
                setActiveTab('cardapio');
              } else {
                try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) {}
              }
            }}
            className="flex items-center gap-2 min-w-0 hover:opacity-90 transition-opacity"
            aria-label="Ir para o cardápio"
          >
            {activeTab !== 'cardapio' ? (
              <ChevronLeft className="w-4 h-4 text-white flex-shrink-0" />
            ) : companySettings?.logo_url ? (
              <img
                src={getImageUrl(companySettings.logo_url)}
                alt=""
                className="w-7 h-7 rounded-full object-cover bg-white flex-shrink-0"
              />
            ) : null}
            <span data-clarity-mask="true" className="text-white text-xs font-semibold truncate" data-e2e={modoMesa ? 'mesa-faixa' : undefined}>
              {modoMesa
                ? `${(mesaQr.mesa?.nome || 'Mesa').toUpperCase()}${mesaQr.cliente?.nome ? ` · Olá, ${mesaQr.cliente.nome}` : ''}`
                : activeTab === 'pedidos' ? 'Meus Pedidos' : `Oi, ${loggedClient?.nome?.split(' ')[0] || 'Visitante'}!`}
            </span>
            {activeTab === 'cardapio' && loggedClient && cashbackConfig.ativo && (
              <div
                className="flex items-center gap-1 bg-emerald-500/90 px-1.5 py-0.5 rounded-full flex-shrink-0"
                title={`Sua carteira de cashback: R$ ${cashbackSaldo.toFixed(2).replace('.', ',')}`}
              >
                <Coins className="w-3 h-3 text-white" />
                <span className="text-[10px] font-bold text-white tabular-nums">
                  R$ {cashbackSaldo.toFixed(2).replace('.', ',')}
                </span>
              </div>
            )}
          </button>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              id="search-button"
              onClick={() => {
                setSearchOpen(!searchOpen);
                if (!searchOpen) {
                  setTimeout(() => searchInputRef.current?.focus(), 100);
                }
              }}
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${searchOpen ? 'bg-white text-primary' : 'bg-white/20 text-white hover:bg-white/30'}`}
            >
              <Search className="w-3.5 h-3.5" />
            </button>
            {/* Na mesa não existe login de cliente nem "Meus Pedidos": a
                identificação é o CPF e o histórico é a comanda da mesa. */}
            {modoMesa ? (
              <Button
                onClick={() => mesaQr.irPara('comanda')}
                data-e2e="mesa-abrir-comanda"
                className="bg-white hover:bg-gray-100 text-primary font-bold px-3 h-7 text-xs gap-1"
              >
                <Receipt className="w-3.5 h-3.5" />
                Comanda
                {(mesaQr.comanda?.rodadas || []).length > 0 ? (
                  <span className="ml-0.5 rounded-full bg-primary text-primary-foreground px-1.5 text-[10px] font-black">
                    {(mesaQr.comanda?.rodadas || []).length}
                  </span>
                ) : null}
              </Button>
            ) : loggedClient ? (
              <ProfileMenu
                client={loggedClient}
                onLogout={handleLogout}
                onClientUpdate={handleClientUpdate}
                darkMode={darkMode}
                onToggleTheme={toggleTheme}
                onNavigateToPedidos={() => setActiveTab('pedidos')}
              />
            ) : (
              <Button onClick={() => setShowLoginModal(true)} className="bg-white hover:bg-gray-100 text-primary font-semibold px-4 h-7 text-xs">
                ENTRAR
              </Button>
            )}
          </div>
        </div>

        {/* Campo de Busca Expandível */}
        {searchOpen && (
          <div id="search-container" className={`px-3 py-2 ${t.bg} border-b ${t.border} sticky top-[44px] z-20`}>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${t.textMuted2}`} />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Buscar produto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-9 pr-9 ${t.bgInput} ${t.text} h-9 rounded-lg text-sm`}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} className={`absolute right-3 top-1/2 -translate-y-1/2 ${t.textMuted2}`}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* HERO — logo circular + nome + slogan + status - cor do estabelecimento */}
        {activeTab === 'cardapio' && (
          <div className="relative">
            {/* Banner: encostado nas bordas no celular; no desktop ganha respiro
                e cantos arredondados, como no cardapio de referencia. */}
            <div className="lg:px-4 lg:pt-4">
              <div className="lg:rounded-2xl lg:overflow-hidden lg:shadow-sm">
                <CardapioBanner banners={banners} darkMode={darkMode} />
              </div>
            </div>

            {/* Cabecalho unico (celular e computador) — logo a esquerda +
                cartao alinhado a esquerda, redimensionado por breakpoint. */}
            <CardapioHero
              companySettings={companySettings}
              logoUrl={companySettings?.logo_url ? getImageUrl(companySettings.logo_url) : null}
              stats={cardapioStats}
              t={t}
              darkMode={darkMode}
              businessHours={businessHours}
              isOpen={isOpen}
              closingTime={closingTime}
              manuallyClosed={manuallyClosed}
              closeReason={closeReason}
              nextOpenTime={nextOpenTime}
              schedulingActive={schedulingConfig.ativo}
              onOpenInfo={() => setShowInfoLojaModal(true)}
              onOpenReviews={() => setShowReviewsModal(true)}
              onShare={() => shareStore(companySettings?.fantasy_name || companySettings?.company_name)}
            />

            {/* Cartão Fidelidade: a tira com o progresso e o prêmio a usar */}
            {(cartaoFid.estado?.cartao?.participa || cartaoFid.premios.length > 0) && (
              <div className="px-4 lg:px-4 pt-3 space-y-2">
                <TiraCartao estado={cartaoFid.estado} onAbrir={() => setMeuCartaoAberto(true)} darkMode={darkMode} />
                <FaixaPremio premios={cartaoFid.premios} onUsar={usarPremio} onVer={() => setMeuCartaoAberto(true)} darkMode={darkMode} />
              </div>
            )}

          </div>
        )}

      {/* MODO COPA — ribbon "É HEXA DE SABOR!" logo abaixo do card da empresa */}
      {copa.active && (
        <div className="copa-ribbon-wrap">
          <div className="copa-ribbon">
            <img className="copa-ribbon-img" src={COPA_FLAG_BRASIL_URL} alt="" aria-hidden />
            <span>ESPECIAL COPA 2026 · É HEXA DE SABOR!</span>
            <span className="copa-ribbon-emoji" aria-hidden>⚽</span>
          </div>
        </div>
      )}

      {barraCategoriasNoTopo}

      {/* Conteúdo Principal — no computador (aba Cardápio) divide a largura com a sacola */}
      <div className={sacolaLateral ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px] lg:gap-6 lg:pr-4' : ''}>
      <main className={`min-w-0 ${sacolaLateral ? 'pb-8' : cartItemsCount > 0 ? (activeTab === 'cardapio' && visibleCoupons.length > 0 ? 'pb-24' : 'pb-16') : (activeTab === 'cardapio' && visibleCoupons.length > 0 ? 'pb-14' : 'pb-4')} ${activeTab === 'cardapio' ? (sacolaLateral ? 'pt-0' : 'pt-4') : 'pt-2'}`}>
        {/* Aba Cardápio */}
        {activeTab === 'cardapio' && (
          <>
            {!sacolaLateral && renderBarraCategorias()}

            {/* Produtos por Categoria */}
            <div className="px-4 space-y-6 mt-2">
              {Object.keys(productsByCategory).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className={`w-16 h-16 ${t.bgCard} rounded-full flex items-center justify-center mb-4`}>
                    <Search className={`w-6 h-6 ${t.textMuted2}`} />
                  </div>
                  <h3 className={`text-base font-semibold ${t.textMuted} mb-2`}>Nenhum produto encontrado</h3>
                  <p className={`${t.textMuted2} text-center text-sm`}>
                    {searchTerm ? `Não encontramos "${searchTerm}"` : "Cardápio vazio"}
                  </p>
                </div>
              ) : (
                <>
                {/* Card de pedido em andamento — só aparece se o cliente logado tem ao menos um pedido ativo */}
                {loggedClient && (() => {
                  const activos = clientPedidos.filter(p =>
                    !['concluido', 'cancelado', 'entregue', 'retirado'].includes(p.status)
                  );
                  if (activos.length === 0) return null;
                  const pedido = activos[0]; // mais recente (clientPedidos já vem ordenado desc)
                  const isPickup = pedido.tipo_entrega === 'pickup';

                  // Pedido AGENDADO — layout próprio (sem timeline de "Recebido → Em produção"
                  // porque o pedido nem começou; só está esperando aprovação do restaurante).
                  if (pedido.status === 'agendado' && pedido.scheduled_at) {
                    // Formata scheduled_at como janela +30min (consistente com Meus Pedidos/admin).
                    const fmtScheduled = (iso) => {
                      if (!iso) return "";
                      try {
                        const [d, hm] = String(iso).split("T");
                        const [y, mo, da] = d.split("-").map((n) => parseInt(n, 10));
                        const [hh, mm] = (hm || "00:00").split(":");
                        const dt = new Date(y, mo - 1, da);
                        const now = new Date();
                        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                        const diff = Math.round((dt - today) / (1000 * 60 * 60 * 24));
                        let dayLbl;
                        if (diff === 0) dayLbl = "Hoje";
                        else if (diff === 1) dayLbl = "Amanhã";
                        else {
                          const wd = dt.toLocaleDateString("pt-BR", { weekday: "short" }).replace(/\.$/, "");
                          dayLbl = `${wd.charAt(0).toUpperCase() + wd.slice(1)} ${da}`;
                        }
                        const startMin = parseInt(hh, 10) * 60 + parseInt(mm, 10);
                        const endMin = startMin + 30;
                        const eh = String(Math.floor(endMin / 60) % 24).padStart(2, "0");
                        const em = String(endMin % 60).padStart(2, "0");
                        return `${dayLbl} · ${hh}:${mm}–${eh}:${em}`;
                      } catch { return iso; }
                    };
                    const cleanPhone2 = (companySettings?.phone || '').replace(/\D/g, '');
                    const phoneWithCountry2 = cleanPhone2.startsWith('55') ? cleanPhone2 : `55${cleanPhone2}`;
                    const codigoPed2 = (pedido.codigo || '').replace(/^#/, '');
                    const waMsg2 = encodeURIComponent(`Gostaria de acompanhar o pedido #${codigoPed2}`);
                    const waLink2 = phoneWithCountry2 ? `https://wa.me/${phoneWithCountry2}?text=${waMsg2}` : null;
                    return (
                      <div
                        onClick={() => { setCurrentOrder(pedido); setTrackingOpen(true); }}
                        className={`rounded-2xl p-4 border-2 border-indigo-500/60 bg-gradient-to-br from-indigo-500/[0.08] to-indigo-500/[0.02] cursor-pointer shadow-lg shadow-indigo-500/10 hover:border-indigo-500 transition-colors`}
                      >
                        {/* Header: código + pill agendado */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse flex-shrink-0" />
                            <span className={`text-sm font-bold ${t.text} truncate`}>{pedido.codigo}</span>
                            <span className="text-[10px] uppercase font-bold text-indigo-600 dark:text-indigo-300 bg-indigo-500/15 px-2 py-0.5 rounded flex-shrink-0 inline-flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> Agendado
                            </span>
                          </div>
                        </div>

                        {/* Bloco central destacado com horário previsto */}
                        <div className="rounded-xl border border-indigo-500/30 bg-white/40 dark:bg-zinc-900/40 p-3 mb-3 text-center">
                          <p className="text-[10px] uppercase tracking-wider font-bold text-indigo-600 dark:text-indigo-300">
                            {isPickup ? "Retirada prevista" : "Entrega prevista"}
                          </p>
                          <p className={`text-lg font-black leading-tight tabular-nums ${t.text} mt-0.5`}>
                            {fmtScheduled(pedido.scheduled_at)}
                          </p>
                        </div>

                        {/* Etapa visual: agendado (feito) → aguardando aprovação (atual) → em preparo (futuro) → entregue (futuro) */}
                        <div className="flex items-start mb-3">
                          {[
                            { id: 'agendado', label: 'Agendado', icon: Calendar, done: true },
                            { id: 'aprovacao', label: 'Aguardando aprovação', icon: Clock, done: false, current: true },
                            { id: 'preparo', label: 'Em preparo', icon: ChefHat, done: false },
                            { id: isPickup ? 'pronto' : 'entregue', label: isPickup ? 'Pronto' : 'Entregue', icon: isPickup ? Package : CheckCircle, done: false },
                          ].map((step, idx, arr) => {
                            const Icon = step.icon;
                            return (
                              <div key={step.id} className="flex-1 flex flex-col items-stretch min-w-0">
                                <div className="flex items-center">
                                  <div className={`flex-1 h-0.5 ${idx === 0 ? 'opacity-0' : ''} ${step.done ? 'bg-indigo-500' : 'bg-muted/40'}`} />
                                  <div className={`relative w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                                    step.done ? 'bg-indigo-500' : step.current ? 'bg-amber-500' : 'bg-muted/30 border border-muted-foreground/30'
                                  }`}>
                                    {step.current && (
                                      <span className="absolute inset-0 rounded-full bg-amber-500 opacity-60 animate-ping" />
                                    )}
                                    {step.done ? <Check className="w-3.5 h-3.5 text-white relative" /> : (
                                      <Icon className={`w-3.5 h-3.5 relative ${step.current ? 'text-white' : 'text-muted-foreground/60'}`} />
                                    )}
                                  </div>
                                  <div className={`flex-1 h-0.5 ${idx === arr.length - 1 ? 'opacity-0' : ''} bg-muted/40`} />
                                </div>
                                <p className={`text-center text-[9px] leading-tight font-semibold mt-1 px-0.5 ${
                                  step.done ? t.text : step.current ? 'text-amber-600 dark:text-amber-400' : t.textMuted
                                }`}>
                                  {step.label}
                                </p>
                              </div>
                            );
                          })}
                        </div>

                        <p className={`text-[11px] text-center ${t.textMuted} mb-3 leading-relaxed px-1`}>
                          O restaurante vai aprovar seu agendamento e começar o preparo próximo do horário previsto.
                        </p>

                        {waLink2 && (
                          <a
                            href={waLink2}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
                          >
                            <MessageCircle className="w-4 h-4" />
                            Acompanhar pelo WhatsApp
                          </a>
                        )}
                      </div>
                    );
                  }
                  // Definição das etapas: cada uma tem id (pra mapear status), label, ícone e
                  // qual timestamp usar do banco.
                  const fmtTime = (iso) => {
                    if (!iso) return null;
                    try { return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); }
                    catch { return null; }
                  };
                  const steps = isPickup
                    ? [
                        { id: 'recebido', label: 'Recebido', icon: Send, time: fmtTime(pedido.created_at) },
                        { id: 'producao', label: 'Em produção', icon: ChefHat, time: fmtTime(pedido.ts_producao) },
                        { id: 'pronto', label: 'Pronto', icon: Package, time: fmtTime(pedido.ts_pronto) },
                      ]
                    : [
                        { id: 'recebido', label: 'Recebido', icon: Send, time: fmtTime(pedido.created_at) },
                        { id: 'producao', label: 'Em produção', icon: ChefHat, time: fmtTime(pedido.ts_producao) },
                        { id: 'pronto', label: 'Pronto', icon: Package, time: fmtTime(pedido.ts_pronto) },
                        { id: 'em_rota', label: 'Em rota', icon: Bike, time: fmtTime(pedido.ts_em_rota) },
                      ];
                  // Mapeia status atual do banco -> índice da etapa
                  const statusToStepIdx = {
                    aguardando_aceite: 0,
                    aceito: 1,
                    producao: 1,
                    pronto: 2,
                    na_bag: 2,
                    em_rota: isPickup ? 2 : 3,
                  };
                  const currentIdx = statusToStepIdx[pedido.status] ?? 0;
                  const statusLabel = steps[currentIdx]?.label || pedido.status;
                  // Tempo decorrido
                  const elapsedMin = Math.max(0, Math.floor((Date.now() - new Date(pedido.created_at).getTime()) / 60000));
                  const elapsedLabel = elapsedMin < 60
                    ? `${elapsedMin} min`
                    : `${Math.floor(elapsedMin / 60)}h ${elapsedMin % 60}min`;
                  // Previsão: created_at + tempo médio do cardápio (fallback 30min)
                  const avgMin = Math.max(1, Number(cardapioStats?.avg_order_time_min) || 30);
                  const eta = new Date(new Date(pedido.created_at).getTime() + avgMin * 60000);
                  const etaLabel = eta.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                  // WhatsApp link
                  const cleanPhone = (companySettings?.phone || '').replace(/\D/g, '');
                  const phoneWithCountry = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
                  const codigoPedido = (pedido.codigo || '').replace(/^#/, '');
                  const waMsg = encodeURIComponent(`Gostaria de acompanhar o pedido #${codigoPedido}`);
                  const waLink = phoneWithCountry ? `https://wa.me/${phoneWithCountry}?text=${waMsg}` : null;
                  return (
                    <div
                      onClick={() => { setCurrentOrder(pedido); setTrackingOpen(true); }}
                      className={`${t.bgCard} rounded-2xl p-4 border-2 border-orange-500/60 cursor-pointer shadow-lg shadow-orange-500/10 hover:border-orange-500 transition-colors`}
                    >
                      {/* Header: codigo + badge + status atual */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                          <span className={`text-sm font-bold ${t.text} truncate`}>{pedido.codigo}</span>
                          <span className="text-[10px] uppercase font-bold text-orange-500 bg-orange-500/15 px-2 py-0.5 rounded flex-shrink-0">
                            Em andamento
                          </span>
                        </div>
                        <span className={`text-xs font-semibold text-orange-500 flex-shrink-0`}>{statusLabel}</span>
                      </div>

                      {/* Timeline com nome + horário por etapa + animação de progressão */}
                      <div className="flex items-start mb-4">
                        {steps.map((step, idx) => {
                          const Icon = step.icon;
                          const isDone = idx < currentIdx;
                          const isCurrent = idx === currentIdx;
                          const isPending = idx > currentIdx;
                          return (
                            <div key={step.id} className="flex-1 flex flex-col items-stretch min-w-0">
                              {/* Linha do círculo (com conector à esquerda e direita pra alinhar) */}
                              <div className="flex items-center">
                                {/* Conector esquerdo: invisível no primeiro */}
                                <div className={`flex-1 h-0.5 ${idx === 0 ? 'opacity-0' : ''} ${isDone || isCurrent ? 'bg-green-500' : 'bg-muted/40'}`} />
                                {/* Círculo da etapa */}
                                <div className={`relative w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-500 ${
                                  isDone ? 'bg-green-500' :
                                  isCurrent ? 'bg-orange-500' :
                                  'bg-muted/30 border border-muted-foreground/30'
                                }`}>
                                  {isCurrent && (
                                    <span className="absolute inset-0 rounded-full bg-orange-500 opacity-60 animate-ping" />
                                  )}
                                  {isDone
                                    ? <Check className="w-3.5 h-3.5 text-white relative" />
                                    : <Icon className={`w-3.5 h-3.5 relative ${isCurrent ? 'text-white' : 'text-muted-foreground/60'}`} />}
                                </div>
                                {/* Conector direito: invisível no último; animado quando aproxima do current */}
                                <div className={`flex-1 h-0.5 ${idx === steps.length - 1 ? 'opacity-0' : ''} relative overflow-hidden ${isDone ? 'bg-green-500' : 'bg-muted/40'}`}>
                                  {isCurrent && idx < steps.length - 1 && (
                                    <span className="absolute inset-0 bg-gradient-to-r from-orange-500 to-orange-500/0 animate-pulse" />
                                  )}
                                </div>
                              </div>
                              {/* Label + horário */}
                              <div className="text-center mt-1.5 px-0.5">
                                <p className={`text-[10px] leading-tight font-semibold ${
                                  isDone ? t.text : isCurrent ? 'text-orange-500' : t.textMuted
                                }`}>
                                  {step.label}
                                </p>
                                <p className={`text-[9px] tabular-nums mt-0.5 ${isPending ? 'text-muted-foreground/40' : t.textMuted}`}>
                                  {step.time || '—'}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Tempo decorrido + previsão */}
                      <div className="flex items-center justify-between text-xs mb-3 px-1">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className={t.textMuted}>há {elapsedLabel}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={t.textMuted}>previsão</span>
                          <span className={`font-semibold ${t.text}`}>{etaLabel}</span>
                        </div>
                      </div>

                      {waLink && (
                        <a
                          href={waLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
                        >
                          <MessageCircle className="w-4 h-4" />
                          Acompanhar pelo WhatsApp
                        </a>
                      )}
                    </div>
                  );
                })()}
                {/* Refazer pedido — carrossel horizontal compacto dos 4 ultimos pedidos.
                    Mobile: arrasta pro lado pra ver os demais. Bem mais clean que stack vertical. */}
                {loggedClient && clientPedidos.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-2 px-0.5">
                      <RotateCw className="w-3.5 h-3.5 text-primary" />
                      <h3 className={`text-xs font-bold uppercase tracking-wider ${t.text}`}>Peça de novo</h3>
                    </div>
                    <div
                      className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-2 snap-x snap-mandatory"
                      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                    >
                      {clientPedidos.slice(0, 5).map(pedido => {
                        const items = pedido.items || [];
                        const itemCount = contarItens(items, products);
                        // Coleta TODAS as fotos visiveis: itens principais + subitens
                        // (combos) + adicionais (etapas). Cada um se torna candidato pra
                        // celula do mosaico, ordenado por preco desc.
                        const allProdsForLookup = (allProductsForPhotos && allProductsForPhotos.length ? allProductsForPhotos : products) || [];
                        const findProdByName = (n) => allProdsForLookup.find(p => (p.name || '').toLowerCase() === String(n || '').toLowerCase());
                        const collected = [];
                        const seenNames = new Set(); // evita repetir mesmo nome 2x no mosaico
                        const pushIfNew = (entry) => {
                          const key = (entry.name || '').toLowerCase();
                          if (!key || seenNames.has(key)) return;
                          seenNames.add(key);
                          collected.push(entry);
                        };
                        for (const it of items) {
                          pushIfNew({
                            name: it.nome || it.name || '',
                            photo_url: it.photo_url,
                            preco: Number(it.preco || it.price || 0),
                          });
                          if (Array.isArray(it.subitems)) {
                            for (const sub of it.subitems) {
                              const subName = typeof sub === 'string' ? sub : (sub?.nome || sub?.name);
                              if (!subName) continue;
                              const prod = findProdByName(subName);
                              pushIfNew({
                                name: subName,
                                photo_url: (sub && sub.photo_url) || prod?.photo_url || null,
                                preco: Number((sub && (sub.preco || sub.price)) || prod?.sale_price || 0),
                              });
                            }
                          }
                          if (Array.isArray(it.etapas)) {
                            for (const et of it.etapas) {
                              const itensList = Array.isArray(et.itens) ? et.itens : (Array.isArray(et.items) ? et.items : []);
                              for (const ad of itensList) {
                                const adName = typeof ad === 'string' ? ad : (ad?.nome || ad?.name);
                                if (!adName) continue;
                                const prod = findProdByName(adName);
                                pushIfNew({
                                  name: adName,
                                  photo_url: prod?.photo_url || null,
                                  preco: Number(prod?.sale_price || 0),
                                });
                              }
                            }
                          }
                        }
                        const topItems = [...collected].sort((a, b) => b.preco - a.preco).slice(0, 4);
                        const n = topItems.length;
                        const remaining = Math.max(0, collected.length - n);
                        // Data relativa do pedido (badge no canto da foto): "hoje", "ontem",
                        // "5d" (poucos dias), "DD/MM" (mais de 7 dias). Compacto pra encaixar
                        // num badge pequeno sem quebrar o layout.
                        const formatRelativeDate = (iso) => {
                          if (!iso) return '';
                          try {
                            const dt = new Date(iso);
                            if (isNaN(dt.getTime())) return '';
                            const now = new Date();
                            const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
                            const diffDays = Math.round((startOf(now) - startOf(dt)) / 86400000);
                            if (diffDays <= 0) return 'hoje';
                            if (diffDays === 1) return 'ontem';
                            if (diffDays < 7) return `${diffDays}d`;
                            const dd = String(dt.getDate()).padStart(2, '0');
                            const mm = String(dt.getMonth() + 1).padStart(2, '0');
                            return `${dd}/${mm}`;
                          } catch { return ''; }
                        };
                        const dataLabel = formatRelativeDate(pedido.created_at);
                        // Item principal (mais caro) pra titulo do card — se o item original
                        // tinha combo_type, mantemos esse atributo no titulo
                        const firstOriginal = items[0] || {};
                        const firstItemName = (topItems[0]?.name) || firstOriginal.nome || firstOriginal.name || pedido.codigo || '';
                        const firstItemCombo = firstOriginal.combo_type;
                        // Helper de fallback dentro de cada celula
                        const cellFallback = (
                          <div className={`w-full h-full flex items-center justify-center ${t.bgMuted} ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                            <ImageOff className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </div>
                        );
                        const renderCell = (it, key) => (
                          it && it.photo_url
                            ? <img key={key} src={getImageUrl(it.photo_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                            : <div key={key} className="w-full h-full">{cellFallback}</div>
                        );
                        // Esgotado (produto ou complemento) / indisponível (escondido, apagado,
                        // categoria desativada): selo no card e clique só explica o motivo.
                        const bloqueio = statusPedidoAntigo(pedido, { aVendaPorId, catalogoPorId });
                        return (
                          <button
                            key={pedido.id}
                            onClick={() => (bloqueio ? toast.error(mensagemBloqueio(bloqueio), { duration: 6000 }) : handleRepetirPedido(pedido))}
                            aria-disabled={!!bloqueio}
                            data-e2e="peca-de-novo-card"
                            data-bloqueio={bloqueio ? bloqueio.bloqueio : ''}
                            className={`relative flex-shrink-0 snap-start rounded-lg border ${t.border} ${darkMode ? 'bg-zinc-900 active:bg-zinc-800' : 'bg-gray-50 active:bg-white'} overflow-hidden text-left transition-colors ${bloqueio ? 'cursor-not-allowed' : ''}`}
                            style={{ width: 'clamp(84px, 22vw, 100px)' }}
                            aria-label={bloqueio ? `${firstItemName}: ${rotuloBloqueio(bloqueio)}` : `Refazer pedido com ${firstItemName}`}
                          >
                            {/* Mosaico de fotos quadrado — layout adaptativo por qtde de itens */}
                            {/* Só as fotos ficam em cinza — o selo por cima precisa manter a cor (vermelho do ESGOTADO). */}
                            <div className={`relative w-full aspect-square ${t.bgMuted} overflow-hidden ${bloqueio ? '[&_img]:grayscale' : ''}`}>
                              {n === 0 ? (
                                cellFallback
                              ) : n === 1 ? (
                                renderCell(topItems[0], 0)
                              ) : n === 2 ? (
                                /* 2 colunas */
                                <div className="grid grid-cols-2 w-full h-full" style={{ gap: '1px' }}>
                                  {topItems.map((it, i) => renderCell(it, i))}
                                </div>
                              ) : n === 3 ? (
                                /* 1 grande à esquerda + 2 pequenos à direita empilhados */
                                <div className="grid w-full h-full" style={{ gap: '1px', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' }}>
                                  <div style={{ gridRow: '1 / 3' }}>{renderCell(topItems[0], 0)}</div>
                                  {renderCell(topItems[1], 1)}
                                  {renderCell(topItems[2], 2)}
                                </div>
                              ) : (
                                /* 2x2 — 4 itens */
                                <div className="grid grid-cols-2 grid-rows-2 w-full h-full" style={{ gap: '1px' }}>
                                  {topItems.map((it, i) => renderCell(it, i))}
                                </div>
                              )}
                              {/* Badge "+N" — itens nao visiveis no mosaico */}
                              {remaining > 0 && (
                                <div className={`absolute top-1 left-1 px-1 py-px rounded-full bg-black/65 backdrop-blur-sm`}>
                                  <span className="text-[8px] font-bold text-white leading-none">+{remaining}</span>
                                </div>
                              )}
                              {/* Botao Refazer flutuante no canto sup-direito */}
                              {!bloqueio && (
                                <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow-sm">
                                  <RotateCw className="w-2.5 h-2.5 text-primary-foreground" />
                                </div>
                              )}
                              {/* Badge de data no canto inferior direito */}
                              {dataLabel && (
                                <div className="absolute bottom-1 right-1 px-1 py-px rounded-full bg-black/65 backdrop-blur-sm">
                                  <span className="text-[8px] font-bold text-white tracking-wide leading-none">{dataLabel}</span>
                                </div>
                              )}
                              {bloqueio && <SeloStatusPedido status={bloqueio} />}
                            </div>
                            {/* Body compacto */}
                            <div className="p-1.5 space-y-0">
                              <div className="flex items-center gap-1">
                                <span className={`text-[10px] font-bold ${t.text} truncate flex-1 leading-tight`}>{firstItemName}</span>
                                {firstItemCombo && (
                                  <span className={`text-[7px] font-bold uppercase px-0.5 rounded ${darkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-gray-200 text-gray-600'} flex-shrink-0`}>
                                    {firstItemCombo === 'combo' ? 'C' : 'S'}
                                  </span>
                                )}
                              </div>
                              <div className={`text-[9px] ${t.textMuted} truncate leading-tight`}>
                                {itemCount} {itemCount === 1 ? 'item' : 'itens'}
                              </div>
                              <div className="text-[10px] font-bold text-primary leading-tight">
                                R$ {(pedido.total || 0).toFixed(2).replace('.', ',')}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Campanha Inteligente: faixa de entrada. Avisa, não entrega — a caça é pelo cardápio. */}
                <FaixaOfertas
                  ofertasDia={ofertasDia}
                  darkMode={darkMode}
                  onEntrar={modoMesa ? undefined : () => setShowLoginModal(true)}
                  onCompletarCadastro={modoMesa ? undefined : () => setEditarCadastroAberto(true)}
                />

                {/* Promoção exclusiva de tipos de cliente (27/10/2026): o visitante sem
                    login vê só este aviso; entrando, o preço aparece se for do tipo. */}
                {!loggedClient && !modoMesa && promocoesExclusivas.length > 0 && (
                  <button type="button" onClick={() => setShowLoginModal(true)} data-e2e="aviso-promo-exclusiva"
                    className={`w-full mb-3 flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
                      darkMode ? 'bg-violet-500/10 border-violet-500/40 hover:bg-violet-500/15' : 'bg-violet-50 border-violet-200 hover:bg-violet-100'}`}>
                    <span className="text-xl">🔒</span>
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm font-bold ${darkMode ? 'text-violet-200' : 'text-violet-800'}`}>
                        Preço especial para clientes {nomesTipos([...new Set(promocoesExclusivas.flatMap((p) => p.segmentos || []))], '', 'curto')}
                      </span>
                      <span className={`block text-xs ${darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        Entre com seu telefone para ver se é para você.
                      </span>
                    </span>
                    <span className="shrink-0 text-xs font-bold text-white bg-violet-600 rounded-full px-3 py-1.5">ENTRAR</span>
                  </button>
                )}

                {/* Seção de Promoções Ativas no Topo — moderada e centralizada.
                    Só as FIXAS: a oferta pessoal aqui viraria a prateleira que o dono descartou. */}
                {promocoesFixas.length > 0 && promocoesFixas.map(promo => {
                  const promoProductsList = (promo.products || [])
                    .map(pp => products.find(p => p.id === pp.product_id))
                    .filter(Boolean);
                  if (promoProductsList.length === 0) return null;
                  const manyProducts = promoProductsList.length > 1;
                  return (
                    <div
                      key={promo.id}
                      className="rounded-2xl border-2 border-dashed border-primary/60 bg-primary/5 p-3"
                    >
                      {/* Header centralizado */}
                      <div className="text-center mb-3">
                        <div className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wide shadow">
                          🔥 {promo.name}
                        </div>
                        {promo.description && (
                          <p className={`text-xs ${t.textMuted} mt-1.5 px-2`}>{promo.description}</p>
                        )}
                      </div>

                      {/* Container dos cards — centralizado se 1 unico, carrossel se varios */}
                      <div
                        className={`${
                          manyProducts
                            ? 'flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-1 -mx-1 px-1'
                            : 'flex justify-center'
                        }`}
                      >
                        {promoProductsList.map(product => {
                          const isUnavailable = product.available === false;
                          const promoSimple = getPromoForProduct(product.id, 'simples');
                          const promoCombo = getPromoForProduct(product.id, 'combo');
                          const pi = promoSimple || promoCombo;
                          // Guarda contra `pi.value == null` (pizza com sizes-only retornaria undefined antes da fix; mantida como defesa em profundidade).
                          let discountLabel = pi && pi.value != null
                            ? (pi.type === 'percentage' ? `${Math.round(pi.value)}% OFF` : `-R$ ${Number(pi.value).toFixed(2).replace('.', ',')}`)
                            : null;
                          // Pizza com promo por tamanho: badge "PROMO" + preço calculado
                          // entra como "A partir de R$X". Sem badge específico do desconto,
                          // já que cada tamanho tem o seu — detalhe aparece no popup.
                          const pizzaPromo = product.product_type === 'pizza' ? getPizzaPromoForProduct(product.id) : null;
                          const pizzaCalc = pizzaPromo ? calcPizzaFromPrice(product, pizzaPromo) : null;
                          if (pizzaPromo && pizzaCalc?.hadDiscount && !discountLabel) {
                            discountLabel = 'PROMO';
                          }
                          const isNew = !isUnavailable && (cardapioStats.launch_product_ids || []).includes(product.id);
                          const simpleOrig = product.simple_price || Math.round((product.sale_price || 0) * 0.7 * 100) / 100;
                          const comboOrig = product.sale_price || 0;
                          const simpleFinal = calcPromoPrice(simpleOrig, promoSimple);
                          const comboFinal = calcPromoPrice(comboOrig, promoCombo);
                          return (
                            <div
                              key={product.id}
                              onClick={() => !isUnavailable && openProductPopup(product)}
                              className={`${
                                manyProducts
                                  ? 'snap-center flex-shrink-0 w-[85%] sm:w-[60%] lg:w-[47%]'
                                  : 'w-full max-w-[92%] sm:max-w-sm'
                              } ${t.bgCard} rounded-xl border ${t.border} shadow-md flex flex-row-reverse gap-3 p-3 transition-all ${
                                isUnavailable ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-lg active:scale-[0.99]'
                              }`}
                            >
                              {/* Conteudo texto à direita (v1.5.322+ — layout invertido via flex-row-reverse) */}
                              <div className="flex-1 min-w-0 flex flex-col">
                                {(discountLabel || isNew || getNonCouponablePromo(product.id)) && !isUnavailable && (
                                  <div className="mb-1 flex items-center gap-1 flex-wrap">
                                    {isNew && (
                                      <span className="inline-flex items-center gap-1 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-black text-[10px] px-2 py-0.5 rounded-full shadow-sm">
                                        ✨ LANÇAMENTO
                                      </span>
                                    )}
                                    {discountLabel && (
                                      <span className="inline-flex items-center gap-1 bg-primary text-primary-foreground font-black text-[10px] px-2 py-0.5 rounded-full shadow-sm">
                                        {discountLabel}
                                      </span>
                                    )}
                                    {getNonCouponablePromo(product.id) && (
                                      <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full ${darkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-gray-200 text-gray-600'}`}>
                                        NÃO ACEITA CUPOM
                                      </span>
                                    )}
                                  </div>
                                )}
                                <h3 className={`font-bold ${t.text} text-sm leading-snug line-clamp-2`}>
                                  <span className="align-middle">{product.name}</span>
                                  {!isUnavailable && (
                                    <button
                                      type="button"
                                      onClick={(e) => shareProduct(product, e)}
                                      className={`inline-flex align-middle items-center justify-center shrink-0 ml-1.5 w-5 h-5 rounded-full transition-colors active:scale-90 ${darkMode ? 'text-zinc-400 hover:text-orange-400' : 'text-gray-500 hover:text-orange-500'}`}
                                      aria-label={`Compartilhar ${product.name}`}
                                      title="Compartilhar produto"
                                    >
                                      <Share2 className="w-3 h-3" strokeWidth={2.2} />
                                    </button>
                                  )}
                                </h3>
                                {product.description && (
                                  <p className={`${t.textMuted} text-xs mt-0.5 leading-relaxed break-words`}>{product.description}</p>
                                )}
                                {/* Preço centralizado */}
                                <div className={`mt-auto pt-1.5 flex justify-center text-center ${isUnavailable ? 'opacity-50' : ''}`}>
                                  {product.product_type === 'combo' ? (
                                    <div className="flex items-baseline justify-center gap-3 flex-wrap">
                                      <div className="flex flex-col items-center">
                                        <span className={`text-[9px] font-medium ${t.textMuted} uppercase tracking-wide`}>Individual</span>
                                        {simpleFinal != null && simpleFinal !== simpleOrig ? (
                                          <div className="flex items-baseline gap-1">
                                            <span className={`text-[10px] line-through ${t.textMuted}`}>R$ {simpleOrig.toFixed(2).replace('.', ',')}</span>
                                            <span className="font-bold text-sm text-green-500">R$ {simpleFinal.toFixed(2).replace('.', ',')}</span>
                                          </div>
                                        ) : (
                                          <span className="font-bold text-sm text-orange-500">R$ {simpleOrig.toFixed(2).replace('.', ',')}</span>
                                        )}
                                      </div>
                                      <div className="flex flex-col items-center">
                                        <span className={`text-[9px] font-medium ${t.textMuted} uppercase tracking-wide`}>Combo</span>
                                        {comboFinal != null && comboFinal !== comboOrig ? (
                                          <div className="flex items-baseline gap-1">
                                            <span className={`text-[10px] line-through ${t.textMuted}`}>R$ {comboOrig.toFixed(2).replace('.', ',')}</span>
                                            <span className="font-bold text-sm text-green-500">R$ {comboFinal.toFixed(2).replace('.', ',')}</span>
                                          </div>
                                        ) : (
                                          <span className="font-bold text-sm text-orange-500">R$ {comboOrig.toFixed(2).replace('.', ',')}</span>
                                        )}
                                      </div>
                                    </div>
                                  ) : (() => {
                                    const origPrice = product.sale_price || 0;
                                    // Pizza com promo por tamanho:
                                    //  - 1 tamanho promovido → mostra o NOME do tamanho + preço promo + preço base riscado.
                                    //  - 2+ tamanhos → "A partir de R$X" (menor preço promocional).
                                    if (pizzaPromo && pizzaCalc?.hadDiscount) {
                                      if (pizzaCalc.onlyOne) {
                                        const it = pizzaCalc.discounted[0];
                                        return (
                                          <div className="flex flex-col items-center">
                                            <span className={`text-[10px] ${t.textMuted} uppercase tracking-wide`}>
                                              {it.sizeName || 'Tamanho promocional'}
                                            </span>
                                            <div className="flex items-baseline gap-1.5">
                                              <span className={`text-[10px] line-through ${t.textMuted} tabular-nums`}>R$ {it.basePrice.toFixed(2).replace('.', ',')}</span>
                                              <span className="font-black text-base text-green-500 tabular-nums">R$ {it.finalPrice.toFixed(2).replace('.', ',')}</span>
                                            </div>
                                          </div>
                                        );
                                      }
                                      return (
                                        <div className="flex flex-col items-center">
                                          <span className={`text-[10px] ${t.textMuted} uppercase tracking-wide`}>A partir de</span>
                                          <span className="font-black text-base text-green-500 tabular-nums">R$ {pizzaCalc.priceFrom.toFixed(2).replace('.', ',')}</span>
                                        </div>
                                      );
                                    }
                                    // Pizza sem promo: mostra o menor tamanho ATIVO (v1.5.321+).
                                    // Fallback pra getFromStepsPrice pra outros produtos com preço base 0.
                                    const pizzaMin = getPizzaMinActivePrice(product);
                                    const fromSteps = pizzaMin != null ? pizzaMin
                                      : (origPrice === 0 ? getFromStepsPrice(product) : null);
                                    if (fromSteps != null) {
                                      return (
                                        <div className="flex flex-col items-center">
                                          <span className={`text-[10px] ${t.textMuted} uppercase tracking-wide`}>A partir de</span>
                                          <span className="font-bold text-base text-orange-500">R$ {fromSteps.toFixed(2).replace('.', ',')}</span>
                                        </div>
                                      );
                                    }
                                    const finalPrice = calcPromoPrice(origPrice, promoSimple);
                                    return finalPrice != null && finalPrice !== origPrice ? (
                                      <div className="flex items-baseline justify-center gap-2">
                                        <span className={`text-xs line-through ${t.textMuted}`}>R$ {origPrice.toFixed(2).replace('.', ',')}</span>
                                        <span className="font-black text-lg text-green-500">R$ {finalPrice.toFixed(2).replace('.', ',')}</span>
                                      </div>
                                    ) : (
                                      <span className="font-bold text-base text-orange-500">R$ {origPrice.toFixed(2).replace('.', ',')}</span>
                                    );
                                  })()}
                                </div>
                              </div>

                              {/* Thumbnail maior que o convencional (112px) */}
                              <div className={`relative w-28 h-28 rounded-xl overflow-hidden flex-shrink-0 ${
                                product.photo_url && isPngImage(product.photo_url)
                                  ? darkMode ? 'bg-gradient-to-b from-orange-900/20 to-orange-800/30' : 'bg-gradient-to-b from-orange-50 to-orange-100'
                                  : t.bgMuted
                              }`}>
                                {product.photo_url ? (
                                  <img
                                    src={getImageUrl(product.photo_url)}
                                    alt={product.name}
                                    className={`w-full h-full ${isPngImage(product.photo_url) ? 'object-contain p-1' : 'object-cover'} ${isUnavailable ? 'grayscale' : ''}`}
                                    loading="lazy"
                                    decoding="async"
                                  />
                                ) : (
                                  <div className={`w-full h-full flex items-center justify-center ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                                    <ImageOff className="w-9 h-9" strokeWidth={1.5} />
                                  </div>
                                )}
                                {!isUnavailable && <VideoDoCartao midias={midiasProdutos[product.id]} />}
                                {isUnavailable && (
                                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                    <span className="bg-red-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase">
                                      ESGOTADO
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Indicador de carrossel quando tem varios */}
                      {manyProducts && (
                        <div className={`text-center text-[10px] ${t.textMuted} mt-2 flex items-center justify-center gap-1`}>
                          <ChevronLeft className="w-3 h-3" />
                          <span>Deslize para ver mais {promoProductsList.length} ofertas</span>
                          <ChevronRight className="w-3 h-3" />
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Categorias regulares - lista vertical estilo Brendi/iFood */}
                {(() => {
                  const orderedCategories = categoriesWithProducts
                    .filter(cat => productsByCategory[cat.name])
                    .map(cat => [cat.name, productsByCategory[cat.name]]);
                  // Quando um perfil (turno) está ativo, ignora 'otherCategories' — só
                  // as categorias do perfil devem aparecer.
                  const profileActive = !!(activeCardapioProfile && (activeCardapioProfile.categories || []).length > 0);
                  if (profileActive) return orderedCategories;
                  const otherCategories = Object.entries(productsByCategory)
                    .filter(([catName]) => !categoriesWithProducts.some(cat => cat.name === catName));
                  return [...orderedCategories, ...otherCategories];
                })().map(([category, categoryProducts]) => {
                  // Categoria toda esgotada: renderizar header compacto sem listar produtos (economiza espaço)
                  const allUnavailable = categoryProducts.length > 0 && categoriasEsgotadas.has(category);
                  if (allUnavailable) {
                    return (
                      <div
                        key={category}
                        id={`cat-${makeProductSlug(category) || 'sem-categoria'}`}
                        className="scroll-mt-[100px]"
                      >
                        <div className="flex items-center justify-center gap-2 py-1.5 opacity-70">
                          <h2 className={`text-sm font-semibold ${t.textMuted} line-through`}>{category}</h2>
                          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-500/15 text-red-500 border border-red-500/30">
                            Categoria esgotada
                          </span>
                          <span className={`text-[10px] ${t.textMuted}`}>· {categoryProducts.length} {categoryProducts.length === 1 ? 'item' : 'itens'}</span>
                        </div>
                      </div>
                    );
                  }
                  return (
                  <div
                    key={category}
                    id={`cat-${makeProductSlug(category) || 'sem-categoria'}`}
                    className="scroll-mt-[100px]"
                  >
                    {/* Título da Categoria - centralizado em uma unica linha */}
                    <div className="flex items-baseline justify-center lg:justify-start gap-2 mb-3">
                      <h2 className={`text-lg lg:text-xl font-bold ${t.text}`}>{category}</h2>
                      <span className={`text-xs ${t.textMuted}`}>· {categoryProducts.length} {categoryProducts.length === 1 ? 'item' : 'itens'}</span>
                    </div>

                    {/* Mercearia: scroll horizontal com cards verticais compactos.
                        Restaurante: lista vertical (mobile) ou grade 2 colunas (desktop lg+).
                        Esgotados são empurrados para o fim — sort estável preserva a ordem. */}
                    {((companySettings?.categoria || 'restaurante') === 'mercearia') ? (
                      <HorizontalScroller darkMode={darkMode} ariaLabel={`Produtos de ${category}`}>
                        {[...categoryProducts]
                          .sort((a, b) => (a.available === false ? 1 : 0) - (b.available === false ? 1 : 0))
                          .map(product => {
                            const isUnavailable = product.available === false;
                            const promoSimple = getPromoForProduct(product.id, 'simples');
                            const promoCombo = getPromoForProduct(product.id, 'combo');
                            const hasPromo = !isUnavailable && (promoSimple || promoCombo);
                            const isTopSeller = !isUnavailable && (cardapioStats.top_product_ids || []).includes(product.id);
                            const isNew = !isUnavailable && (cardapioStats.launch_product_ids || []).includes(product.id);
                            const origPrice = product.sale_price || 0;
                            // Pizza sem promo: menor tamanho ATIVO (v1.5.321+). Fallback pra getFromStepsPrice.
                            const pizzaMin = getPizzaMinActivePrice(product);
                            const fromSteps = pizzaMin != null ? pizzaMin
                              : (origPrice === 0 ? getFromStepsPrice(product) : null);
                            const finalPrice = calcPromoPrice(origPrice, promoSimple);
                            const pi = promoSimple || promoCombo;
                            const promoLabel = pi ? (pi.type === 'percentage' ? `-${Math.round(pi.value)}%` : `R$ ${pi.value.toFixed(2).replace('.', ',')}`) : null;
                            // "/kg" só no produto vendido por peso (kg/L + divisível), não no "Guaraná 2 L"
                            const unitSuffix = medidaDoProduto(product) ? `/${medidaDoProduto(product)}` : '';
                            // "de/por" DECORATIVO (v1.5.282+) — sem badge PROMO. Só aparece
                            // se não houver promoção global (finalPrice) e original_price > sale_price.
                            const decoOrigPrice = (!finalPrice || finalPrice === origPrice) && Number(product.original_price || 0) > origPrice && origPrice > 0
                              ? Number(product.original_price) : null;
                            // Produto simples: sem combo e sem etapas/complementos — pode ir direto pro carrinho via botao +
                            const isSimpleProduct = !isUnavailable
                              && product.product_type !== 'combo'
                              && (!product.order_steps || product.order_steps.length === 0);
                            const oferta = isUnavailable ? null : ofertaDoProduto(ofertasDia, product.id);
                            const cardOferta = propsCardOferta(oferta);
                            const mostraCashback = cashbackConfig.ativo && cashbackConfig.percentual > 0 && !isUnavailable
                              && beneficiosDoProduto(product, activePromotions).ganhaCashback;
                            return (
                              <div
                                key={product.id}
                                onClick={() => !isUnavailable && openProductPopup(product)}
                                {...cardOferta.atributos}
                                style={cardOferta.style}
                                className={`snap-start flex-shrink-0 w-40 sm:w-44 md:w-48 rounded-2xl border overflow-hidden transition-all ${
                                  isTopSeller
                                    ? (darkMode ? 'bg-zinc-800 border-amber-500/60' : 'bg-white border-amber-400')
                                    : isNew
                                      ? (darkMode ? 'bg-zinc-800 border-sky-500/60' : 'bg-white border-sky-400')
                                      : (darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200')
                                } ${
                                  isUnavailable
                                    ? 'opacity-70 cursor-not-allowed'
                                    : `cursor-pointer ${darkMode ? 'hover:bg-zinc-800/80' : 'hover:bg-gray-50'} hover:shadow-md`
                                } ${cardOferta.className}`}
                              >
                                <div className={`relative w-full aspect-square ${
                                  product.photo_url && isPngImage(product.photo_url)
                                    ? darkMode ? 'bg-gradient-to-b from-orange-900/20 to-orange-800/30' : 'bg-gradient-to-b from-orange-50 to-orange-100'
                                    : t.bgMuted
                                }`}>
                                  {product.photo_url ? (
                                    <img
                                      src={getImageUrl(product.photo_url)}
                                      alt={product.name}
                                      className={`w-full h-full ${isPngImage(product.photo_url) ? 'object-contain p-2' : 'object-cover'} ${isUnavailable ? 'grayscale opacity-60' : ''}`}
                                      loading="lazy"
                                      decoding="async"
                                    />
                                  ) : (
                                    <div className={`w-full h-full flex items-center justify-center ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                                      <ImageOff className="w-8 h-8" strokeWidth={1.5} />
                                    </div>
                                  )}
                                  {!isUnavailable && <VideoDoCartao midias={midiasProdutos[product.id]} />}
                                  {!isUnavailable && (hasPromo || isTopSeller || isNew || oferta) && (
                                    <div className="absolute top-1.5 left-1.5 flex flex-col gap-1">
                                      {oferta && <SeloOferta oferta={oferta} />}
                                      {isNew && (
                                        <span className="text-[9px] font-bold text-white bg-gradient-to-r from-sky-500 to-blue-600 px-1.5 py-0.5 rounded-full shadow-sm">✨ NOVO</span>
                                      )}
                                      {isTopSeller && (
                                        <span className="text-[9px] font-bold text-amber-900 bg-gradient-to-r from-amber-300 to-amber-400 px-1.5 py-0.5 rounded-full shadow-sm">⭐ TOP</span>
                                      )}
                                      {hasPromo && !oferta && (
                                        <span className="text-[9px] font-bold text-white bg-green-500 px-1.5 py-0.5 rounded-full shadow-sm">{promoLabel}</span>
                                      )}
                                    </div>
                                  )}
                                  {isUnavailable && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                      <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full bg-red-500 text-white shadow-sm flex items-center gap-1">
                                        <XCircle className="w-3 h-3" /> Esgotado
                                      </span>
                                    </div>
                                  )}
                                  {isSimpleProduct && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Mesmo preço que o card mostra: antes o "+" mandava o
                                        // sale_price cru e a promoção exibida não chegava no carrinho.
                                        const precoPromo = promoSimple ? calcPromoPrice(product.sale_price || 0, promoSimple) : null;
                                        addToCart({
                                          ...product, quantity: 1,
                                          ...(precoPromo != null ? { final_price: Math.round(precoPromo * 100) / 100 } : {}),
                                        }, { openDrawer: false });
                                      }}
                                      aria-label={`Adicionar ${product.name} ao carrinho`}
                                      title="Adicionar ao carrinho"
                                      className="absolute bottom-1.5 right-1.5 w-9 h-9 rounded-full flex items-center justify-center bg-orange-500 hover:bg-orange-600 active:scale-90 text-white shadow-lg ring-2 ring-white/80 transition-all"
                                    >
                                      <Plus className="w-5 h-5" strokeWidth={2.5} />
                                    </button>
                                  )}
                                </div>
                                {/* Tarja de cashback LOGO ABAIXO da foto (v1.5.306+).
                                    Colada — sem sobrepor a imagem (evita cortar comida na foto). */}
                                {mostraCashback && (
                                  <div className="bg-emerald-600 text-white text-[10px] font-bold py-1 px-2 flex items-center justify-center gap-1">
                                    <Coins className="w-3 h-3" />
                                    {Number(cashbackConfig.percentual).toFixed(0)}% de cashback
                                  </div>
                                )}
                                <div className="p-2.5 flex flex-col gap-1">
                                  <h3 className={`font-semibold text-xs leading-snug line-clamp-2 min-h-[2rem] ${t.text}`}>{product.name}</h3>
                                  {!isUnavailable && (
                                    <div className="mt-0.5">
                                      {fromSteps != null ? (
                                        <div className="flex flex-col">
                                          <span className={`text-[9px] ${t.textMuted} uppercase tracking-wide`}>A partir de</span>
                                          <span className="font-bold text-sm text-orange-500">R$ {fromSteps.toFixed(2).replace('.', ',')}{unitSuffix}</span>
                                        </div>
                                      ) : finalPrice != null && finalPrice !== origPrice ? (
                                        <div className="flex items-baseline gap-1.5">
                                          <span className={`text-[10px] line-through ${t.textMuted}`}>R$ {origPrice.toFixed(2).replace('.', ',')}</span>
                                          <span className="font-bold text-sm text-green-500">R$ {finalPrice.toFixed(2).replace('.', ',')}{unitSuffix}</span>
                                        </div>
                                      ) : decoOrigPrice != null ? (
                                        <div className="flex items-baseline gap-1.5">
                                          <span className={`text-[10px] line-through ${t.textMuted}`}>R$ {decoOrigPrice.toFixed(2).replace('.', ',')}</span>
                                          <span className="font-bold text-sm text-orange-500">R$ {origPrice.toFixed(2).replace('.', ',')}{unitSuffix}</span>
                                        </div>
                                      ) : (
                                        <span className="font-bold text-sm text-orange-500">R$ {origPrice.toFixed(2).replace('.', ',')}{unitSuffix}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </HorizontalScroller>
                    ) : (
                    <div className={`flex flex-col gap-2.5 lg:grid ${sacolaLateral ? 'lg:grid-cols-1 xl:grid-cols-2' : 'lg:grid-cols-2'} lg:gap-4`}>
                      {[...categoryProducts]
                        .sort((a, b) => (a.available === false ? 1 : 0) - (b.available === false ? 1 : 0))
                        .map(product => {
                        const isUnavailable = product.available === false;
                        const promoSimple = getPromoForProduct(product.id, 'simples');
                        const promoCombo = getPromoForProduct(product.id, 'combo');
                        const hasPromo = !isUnavailable && (promoSimple || promoCombo);
                        const isTopSeller = !isUnavailable && (cardapioStats.top_product_ids || []).includes(product.id);
                        // LANCAMENTO: backend filtra pra evitar marcar cadastros em massa
                        const isNew = !isUnavailable && (cardapioStats.launch_product_ids || []).includes(product.id);
                        const simpleOrig = product.simple_price || Math.round((product.sale_price || 0) * 0.7 * 100) / 100;
                        const comboOrig = product.sale_price || 0;
                        const simpleFinal = calcPromoPrice(simpleOrig, promoSimple);
                        const comboFinal = calcPromoPrice(comboOrig, promoCombo);
                        const pi = promoSimple || promoCombo;
                        const promoLabel = pi ? (pi.type === 'percentage' ? `-${Math.round(pi.value)}%` : `R$ ${pi.value.toFixed(2).replace('.', ',')}`) : null;
                        const oferta = isUnavailable ? null : ofertaDoProduto(ofertasDia, product.id);
                        const cardOferta = propsCardOferta(oferta);
                        // Tarja de cashback só em produto que gera cashback (promoção, "de/por"
                        // e oferta da Campanha Inteligente que não acumula ficam sem).
                        const mostraCashback = cashbackConfig.ativo && cashbackConfig.percentual > 0 && !isUnavailable
                          && beneficiosDoProduto(product, activePromotions).ganhaCashback;

                        return (
                          <div
                            key={product.id}
                            onClick={() => !isUnavailable && openProductPopup(product)}
                            {...cardOferta.atributos}
                            style={cardOferta.style}
                            className={`flex flex-row-reverse transition-colors rounded-2xl border ${
                              isUnavailable ? 'gap-2 p-2 lg:p-2' : 'gap-3 p-3 lg:gap-4 lg:p-4'
                            } ${
                              isTopSeller
                                ? (darkMode ? 'bg-zinc-800 border-amber-500/60 shadow-amber-500/10' : 'bg-white border-amber-400')
                                : isNew
                                  ? (darkMode ? 'bg-zinc-800 border-sky-500/60 shadow-sky-500/10' : 'bg-white border-sky-400')
                                  : (darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200')
                            } ${
                              isUnavailable
                                ? `opacity-70 cursor-not-allowed ${darkMode ? 'bg-zinc-900/50' : 'bg-gray-100/50'}`
                                : `cursor-pointer ${darkMode ? 'hover:bg-zinc-800/80' : 'hover:bg-gray-50'} hover:shadow-md`
                            } ${cardOferta.className}`}
                          >
                            {/* Conteúdo à direita (v1.5.322+ — layout invertido via flex-row-reverse; JSX preserva ordem original) */}
                            <div className="flex-1 min-w-0 flex flex-col justify-center">
                              {!isUnavailable && (hasPromo || isTopSeller || isNew || getNonCouponablePromo(product.id) || oferta) && (
                                <div className="mb-1 flex items-center gap-1 flex-wrap">
                                  {oferta && <SeloOferta oferta={oferta} />}
                                  {isNew && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-gradient-to-r from-sky-500 to-blue-600 px-2 py-0.5 rounded-full shadow-sm">
                                      ✨ LANÇAMENTO
                                    </span>
                                  )}
                                  {isTopSeller && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-900 bg-gradient-to-r from-amber-300 to-amber-400 px-2 py-0.5 rounded-full shadow-sm">
                                      ⭐ MAIS VENDIDO
                                    </span>
                                  )}
                                  {getNonCouponablePromo(product.id) && (
                                    <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-full ${darkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-gray-200 text-gray-600'}`}>
                                      NÃO ACEITA CUPOM
                                    </span>
                                  )}
                                  {hasPromo && !oferta && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-600 dark:text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">
                                      🔥 PROMO {promoLabel}
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className={`font-bold ${t.text} ${isUnavailable ? 'text-base line-clamp-1' : 'text-base lg:text-lg leading-snug line-clamp-2'}`}>
                                  {product.name}
                                </h3>
                                {isUnavailable && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500 text-white shadow-sm">
                                    <XCircle className="w-3 h-3" />
                                    Esgotado
                                  </span>
                                )}
                              </div>
                              {!isUnavailable && product.description && (
                                <p className={`${t.textMuted} text-xs lg:text-sm mt-0.5 lg:mt-1 leading-relaxed break-words`}>{product.description}</p>
                              )}
                              {/* Preços centralizados - escondidos quando esgotado */}
                              {!isUnavailable && (
                              <div className="mt-auto pt-1.5 flex justify-center text-center lg:justify-start lg:text-left">
                                {product.product_type === 'combo' ? (
                                  <div className={`flex items-baseline justify-center gap-3 flex-wrap ${isUnavailable ? 'opacity-50' : ''}`}>
                                    <div className="flex flex-col items-center">
                                      <span className={`text-[9px] font-medium ${t.textMuted} uppercase tracking-wide`}>Individual</span>
                                      {simpleFinal != null && simpleFinal !== simpleOrig ? (
                                        <div className="flex items-baseline gap-1">
                                          <span className={`text-[10px] line-through ${t.textMuted}`}>R$ {simpleOrig.toFixed(2).replace('.', ',')}</span>
                                          <span className="font-bold text-sm text-green-500">R$ {simpleFinal.toFixed(2).replace('.', ',')}</span>
                                        </div>
                                      ) : (
                                        <span className={`font-bold text-sm ${isUnavailable ? 'text-gray-400 line-through' : 'text-orange-500'}`}>
                                          R$ {simpleOrig.toFixed(2).replace('.', ',')}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex flex-col items-center">
                                      <span className={`text-[9px] font-medium ${t.textMuted} uppercase tracking-wide`}>Combo</span>
                                      {comboFinal != null && comboFinal !== comboOrig ? (
                                        <div className="flex items-baseline gap-1">
                                          <span className={`text-[10px] line-through ${t.textMuted}`}>R$ {comboOrig.toFixed(2).replace('.', ',')}</span>
                                          <span className="font-bold text-sm text-green-500">R$ {comboFinal.toFixed(2).replace('.', ',')}</span>
                                        </div>
                                      ) : (
                                        <span className={`font-bold text-sm ${isUnavailable ? 'text-gray-400 line-through' : 'text-orange-500'}`}>
                                          R$ {comboOrig.toFixed(2).replace('.', ',')}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : (() => {
                                  const origPrice = product.sale_price || 0;
                                  // Pizza sem promo: menor tamanho ATIVO (v1.5.321+). Fallback pra getFromStepsPrice.
                                  const pizzaMin = getPizzaMinActivePrice(product);
                                  const fromSteps = pizzaMin != null ? pizzaMin
                                    : (origPrice === 0 ? getFromStepsPrice(product) : null);
                                  // "/kg" só no produto vendido por peso (kg/L + divisível)
                                  const unitSuffix = medidaDoProduto(product)
                                    ? <span className={`text-[10px] font-normal ${t.textMuted} ml-0.5`}>/{medidaDoProduto(product)}</span>
                                    : null;
                                  if (fromSteps != null) {
                                    return (
                                      <div className="flex flex-col items-center">
                                        <span className={`text-[10px] ${t.textMuted} uppercase tracking-wide`}>A partir de</span>
                                        <span className={`font-bold text-base ${isUnavailable ? 'text-gray-400 line-through' : 'text-orange-500'}`}>R$ {fromSteps.toFixed(2).replace('.', ',')}{unitSuffix}</span>
                                      </div>
                                    );
                                  }
                                  const finalPrice = calcPromoPrice(origPrice, promoSimple);
                                  const decoOrig = (!finalPrice || finalPrice === origPrice) && Number(product.original_price || 0) > origPrice && origPrice > 0
                                    ? Number(product.original_price) : null;
                                  if (finalPrice != null && finalPrice !== origPrice) {
                                    return (
                                      <div className="flex items-baseline justify-center gap-2">
                                        <span className={`text-xs line-through ${t.textMuted}`}>R$ {origPrice.toFixed(2).replace('.', ',')}</span>
                                        <span className="font-bold text-base text-green-500">R$ {finalPrice.toFixed(2).replace('.', ',')}{unitSuffix}</span>
                                      </div>
                                    );
                                  }
                                  if (decoOrig != null) {
                                    return (
                                      <div className="flex items-baseline justify-center gap-2">
                                        <span className={`text-xs line-through ${t.textMuted}`}>R$ {decoOrig.toFixed(2).replace('.', ',')}</span>
                                        <span className={`font-bold text-base ${isUnavailable ? 'text-gray-400 line-through' : 'text-orange-500'}`}>R$ {origPrice.toFixed(2).replace('.', ',')}{unitSuffix}</span>
                                      </div>
                                    );
                                  }
                                  return (
                                    <span className={`font-bold text-base ${isUnavailable ? 'text-gray-400 line-through' : 'text-orange-500'}`}>
                                      R$ {origPrice.toFixed(2).replace('.', ',')}{unitSuffix}
                                    </span>
                                  );
                                })()}
                              </div>
                              )}
                            </div>

                            {/* Thumbnail à esquerda (v1.5.322 flex-row-reverse) — foto ligeiramente
                                maior (v1.5.323+ w-28) + botão de compartilhar sobreposto no canto
                                superior esquerdo. Wrapper `flex-col` pra encaixar tarja de cashback
                                logo abaixo (colada) sem sobrepor a imagem. */}
                            <div className="flex flex-col flex-shrink-0">
                              <div className={`relative ${isUnavailable ? 'w-12 h-12' : 'w-28 h-28 lg:w-48 lg:h-48'} rounded-xl overflow-hidden ${
                                mostraCashback ? 'rounded-b-none' : ''
                              } ${
                                product.photo_url && isPngImage(product.photo_url)
                                  ? darkMode ? 'bg-gradient-to-b from-orange-900/20 to-orange-800/30' : 'bg-gradient-to-b from-orange-50 to-orange-100'
                                  : t.bgMuted
                              }`}>
                                {product.photo_url ? (
                                  <img
                                    src={getImageUrl(product.photo_url)}
                                    alt={product.name}
                                    className={`w-full h-full ${isPngImage(product.photo_url) ? 'object-contain p-1' : 'object-cover'} ${isUnavailable ? 'grayscale opacity-60' : ''}`}
                                    loading="lazy"
                                    decoding="async"
                                  />
                                ) : (
                                  <div className={`w-full h-full flex items-center justify-center ${darkMode ? 'text-zinc-500' : 'text-zinc-400'} ${isUnavailable ? 'opacity-60' : ''}`}>
                                    <ImageOff className={isUnavailable ? 'w-4 h-4' : 'w-7 h-7'} strokeWidth={1.5} />
                                  </div>
                                )}
                                {!isUnavailable && <VideoDoCartao midias={midiasProdutos[product.id]} />}
                                {/* Botão compartilhar sobreposto no canto superior esquerdo — sem
                                    fundo, ícone branco com drop-shadow pra ficar legível sobre foto
                                    clara ou escura. Só quando disponível (esgotado esconde). */}
                                {!isUnavailable && (
                                  <button
                                    type="button"
                                    onClick={(e) => shareProduct(product, e)}
                                    className="absolute top-1 left-1 z-10 w-6 h-6 flex items-center justify-center text-white transition-transform active:scale-90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]"
                                    aria-label={`Compartilhar ${product.name}`}
                                    title="Compartilhar produto"
                                  >
                                    <Share2 className="w-3.5 h-3.5" strokeWidth={2.4} />
                                  </button>
                                )}
                              </div>
                              {/* Tarja de cashback LOGO ABAIXO da thumbnail (v1.5.306+).
                                  Só na thumb normal (w-28). Esgotado (w-12) fica sem — texto ilegível. */}
                              {mostraCashback && (
                                <div className="w-28 lg:w-48 bg-emerald-600 text-white text-[9px] lg:text-xs font-bold py-0.5 lg:py-1 px-1 flex items-center justify-center gap-0.5 rounded-b-xl">
                                  <Coins className="w-2.5 h-2.5 lg:w-3.5 lg:h-3.5" />
                                  {Number(cashbackConfig.percentual).toFixed(0)}% cashback
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                  );
                })}
                </>
              )}
            </div>
          </>
        )}

        {/* Aba Ofertas - oculta */}

        {/* Aba Pedidos - Histórico Completo */}
        {activeTab === 'pedidos' && (
          <div data-clarity-mask="true" className="px-4 py-4">
            <h2 className={`text-lg font-bold ${t.text} mb-4 flex items-center gap-2`}>
              <ClipboardList className="w-5 h-5 text-orange-500" />
              Meus Pedidos
            </h2>
            {loggedClient && cartaoFid.estado?.cartao?.participa && (
              <button type="button" onClick={() => setMeuCartaoAberto(true)} className="block w-full mb-5 text-left" data-e2e="meu-cartao-pedidos">
                <p className={`text-sm font-bold ${t.text} mb-2`}>Meu cartão fidelidade</p>
                <CartaoFidelidade loja={cartaoFid.estado.loja} cartao={cartaoFid.estado.cartao} />
              </button>
            )}
            
            {!loggedClient ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className={`w-16 h-16 ${t.bgCard} rounded-full flex items-center justify-center mb-4 border ${t.border}`}>
                  <User className="w-8 h-8 text-orange-500" />
                </div>
                <p className={`${t.textMuted} text-center text-sm mb-4`}>Faça login para ver seus pedidos</p>
                <Button onClick={() => setShowLoginModal(true)} className="bg-orange-500 hover:bg-orange-600 text-white">
                  Entrar
                </Button>
              </div>
            ) : loadingPedidos ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mb-4"></div>
                <p className={t.textMuted}>Carregando pedidos...</p>
              </div>
            ) : clientPedidos.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className={`w-16 h-16 ${t.bgCard} rounded-full flex items-center justify-center mb-4 border ${t.border}`}>
                  <ClipboardList className="w-8 h-8 text-orange-500/50" />
                </div>
                <p className={`${t.textMuted} text-center text-sm`}>Você ainda não fez nenhum pedido</p>
              </div>
            ) : selectedPedido ? (
              /* Detalhes do Pedido — resumo, fotos, etapas, observações, tempos, entregador */
              <div className={`${t.bgCard} rounded-xl border ${t.border} overflow-hidden`}>
                {/* Header com botão voltar */}
                <div className={`p-3 border-b ${t.border} flex items-center gap-3 bg-orange-500`}>
                  <button 
                    onClick={() => setSelectedPedido(null)}
                    className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center"
                  >
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </button>
                  <div>
                    <h3 className="font-bold text-white">{selectedPedido.codigo}</h3>
                    <p className="text-xs text-white/80">
                      {new Date(selectedPedido.created_at).toLocaleDateString('pt-BR')} às {new Date(selectedPedido.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>

                {/* Resumo geral */}
                <div className={`p-3 border-b ${t.border} space-y-2`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white text-xs font-medium ${
                      selectedPedido.status === 'concluido' ? 'bg-green-500' :
                      selectedPedido.status === 'cancelado' ? 'bg-red-500' :
                      selectedPedido.status === 'em_rota' || selectedPedido.status === 'transito' ? 'bg-purple-500' :
                      selectedPedido.status === 'producao' ? 'bg-orange-500' :
                      selectedPedido.status === 'pronto' || selectedPedido.status === 'na_bag' ? 'bg-blue-500' :
                      'bg-yellow-500'
                    }`}>
                      {selectedPedido.status === 'aguardando_aceite' ? '⏳ Aguardando' :
                       selectedPedido.status === 'producao' ? '🍳 Em Produção' :
                       selectedPedido.status === 'pronto' ? '✅ Pronto' :
                       selectedPedido.status === 'na_bag' ? '📦 Na Bag' :
                       selectedPedido.status === 'em_rota' || selectedPedido.status === 'transito' ? '🛵 Em Rota' :
                       selectedPedido.status === 'concluido' ? '🎉 Entregue' :
                       selectedPedido.status === 'cancelado' ? '❌ Cancelado' :
                       selectedPedido.status}
                    </span>
                    <span className={`text-xs ${t.textMuted}`}>
                      {selectedPedido.tipo_entrega === 'delivery' ? '🛵 Entrega' : '🏪 Retirada'}
                    </span>
                    <span className={`text-sm font-bold text-orange-500`}>
                      R$ {(selectedPedido.total || 0).toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                  {/* Entregador (se entrega) */}
                  {selectedPedido.tipo_entrega === 'delivery' && (selectedPedido.entregador_nome || selectedPedido.entregador_id) && (
                    <div className="flex items-center gap-2">
                      <Bike className="w-4 h-4 text-orange-500 shrink-0" />
                      <span className={`text-sm ${t.text}`}>
                        Entregador: <strong>{selectedPedido.entregador_nome || 'Definido'}</strong>
                      </span>
                    </div>
                  )}
                </div>

                {/* Tempos das etapas — Delivery */}
                {(selectedPedido.tipo_entrega === 'delivery' || selectedPedido.modulo === 'Delivery') &&
                 [selectedPedido.tempo_aceite_seg, selectedPedido.tempo_producao_seg, selectedPedido.tempo_pronto_seg, selectedPedido.tempo_na_bag_seg, selectedPedido.tempo_em_rota_seg, selectedPedido.tempo_total_seg].some(v => v != null && v !== undefined) && (
                  <div className={`p-3 border-b ${t.border}`}>
                    <h4 className={`text-xs font-semibold ${t.textMuted} uppercase mb-2 flex items-center gap-1.5`}>
                      <Clock className="w-3.5 h-3.5" /> Tempos (Entrega)
                    </h4>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <div className="flex justify-between"><span className={t.textMuted}>Até aceitar:</span><span className={`font-medium ${t.text}`}>{selectedPedido.tempo_aceite_seg != null ? formatTempoEtapa(selectedPedido.tempo_aceite_seg) : (selectedPedido.tempo_aceite_min != null ? `${selectedPedido.tempo_aceite_min} min` : '—')}</span></div>
                      <div className="flex justify-between"><span className={t.textMuted}>Produção:</span><span className={`font-medium ${t.text}`}>{selectedPedido.tempo_producao_seg != null ? formatTempoEtapa(selectedPedido.tempo_producao_seg) : (selectedPedido.tempo_producao_min != null ? `${selectedPedido.tempo_producao_min} min` : '—')}</span></div>
                      <div className="flex justify-between"><span className={t.textMuted}>Pronto:</span><span className={`font-medium ${t.text}`}>{selectedPedido.tempo_pronto_seg != null ? formatTempoEtapa(selectedPedido.tempo_pronto_seg) : (selectedPedido.tempo_pronto_min != null ? `${selectedPedido.tempo_pronto_min} min` : '—')}</span></div>
                      <div className="flex justify-between"><span className={t.textMuted}>Na bag:</span><span className={`font-medium ${t.text}`}>{selectedPedido.tempo_na_bag_seg != null ? formatTempoEtapa(selectedPedido.tempo_na_bag_seg) : (selectedPedido.tempo_na_bag_min != null ? `${selectedPedido.tempo_na_bag_min} min` : '—')}</span></div>
                      <div className="flex justify-between"><span className={t.textMuted}>Em rota:</span><span className={`font-medium ${t.text}`}>{selectedPedido.tempo_em_rota_seg != null ? formatTempoEtapa(selectedPedido.tempo_em_rota_seg) : (selectedPedido.tempo_em_rota_min != null ? `${selectedPedido.tempo_em_rota_min} min` : '—')}</span></div>
                      <div className="flex justify-between col-span-2 border-t border-dashed pt-1 mt-1"><span className={`font-medium ${t.textMuted}`}>Total:</span><span className={`font-bold ${t.text}`}>{selectedPedido.tempo_total_seg != null ? formatTempoEtapa(selectedPedido.tempo_total_seg) : (selectedPedido.tempo_total_min != null ? `${selectedPedido.tempo_total_min} min` : '—')}</span></div>
                    </div>
                  </div>
                )}

                {/* Tempos das etapas — Retirada */}
                {selectedPedido.tipo_entrega === 'pickup' &&
                 [selectedPedido.tempo_aceite_seg, selectedPedido.tempo_producao_seg, selectedPedido.tempo_pronto_seg, selectedPedido.tempo_ate_retirar_seg, selectedPedido.tempo_total_seg].some(v => v != null && v !== undefined) && (
                  <div className={`p-3 border-b ${t.border}`}>
                    <h4 className={`text-xs font-semibold ${t.textMuted} uppercase mb-2 flex items-center gap-1.5`}>
                      <Clock className="w-3.5 h-3.5" /> Tempos (Retirada)
                    </h4>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <div className="flex justify-between"><span className={t.textMuted}>Até aceitar:</span><span className={`font-medium ${t.text}`}>{selectedPedido.tempo_aceite_seg != null ? formatTempoEtapa(selectedPedido.tempo_aceite_seg) : (selectedPedido.tempo_aceite_min != null ? `${selectedPedido.tempo_aceite_min} min` : '—')}</span></div>
                      <div className="flex justify-between"><span className={t.textMuted}>Produção:</span><span className={`font-medium ${t.text}`}>{selectedPedido.tempo_producao_seg != null ? formatTempoEtapa(selectedPedido.tempo_producao_seg) : (selectedPedido.tempo_producao_min != null ? `${selectedPedido.tempo_producao_min} min` : '—')}</span></div>
                      <div className="flex justify-between"><span className={t.textMuted}>Pronto:</span><span className={`font-medium ${t.text}`}>{selectedPedido.tempo_pronto_seg != null ? formatTempoEtapa(selectedPedido.tempo_pronto_seg) : (selectedPedido.tempo_pronto_min != null ? `${selectedPedido.tempo_pronto_min} min` : '—')}</span></div>
                      <div className="flex justify-between"><span className={t.textMuted}>Até retirar:</span><span className={`font-medium ${t.text}`}>{selectedPedido.tempo_ate_retirar_seg != null ? formatTempoEtapa(selectedPedido.tempo_ate_retirar_seg) : (selectedPedido.tempo_ate_retirar_min != null ? `${selectedPedido.tempo_ate_retirar_min} min` : '—')}</span></div>
                      <div className="flex justify-between col-span-2 border-t border-dashed pt-1 mt-1"><span className={`font-medium ${t.textMuted}`}>Total:</span><span className={`font-bold ${t.text}`}>{selectedPedido.tempo_total_seg != null ? formatTempoEtapa(selectedPedido.tempo_total_seg) : (selectedPedido.tempo_total_min != null ? `${selectedPedido.tempo_total_min} min` : '—')}</span></div>
                    </div>
                  </div>
                )}

                {/* Itens com foto, etapas e observação */}
                <div className="p-3 space-y-3">
                  <h4 className={`text-xs font-semibold ${t.textMuted} uppercase`}>Itens do Pedido</h4>
                  {selectedPedido.items?.map((item, idx) => (
                    <div key={idx} className={`flex gap-3 p-3 ${t.bgMuted} rounded-xl border ${t.border}`}>
                      {/* Foto do produto */}
                      <div className="w-14 h-14 rounded-lg bg-muted flex-shrink-0 overflow-hidden">
                        {(item.photo_url || item.foto_url) ? (
                          <img
                            src={getImageUrl(item.photo_url || item.foto_url)}
                            alt={item.nome || item.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling?.classList.remove('hidden'); }}
                          />
                        ) : null}
                        <div className={`w-full h-full flex items-center justify-center ${(item.photo_url || item.foto_url) ? 'hidden' : ''} ${t.textMuted}`}>
                          <UtensilsCrossed className="w-6 h-6" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          {/* Quilo/litro (30/09/2026): "0,350 kg" numa pílula, não "0.35" */}
                          {medidaDoItem(item, products) ? (
                            <span className="h-6 px-2 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0 whitespace-nowrap">
                              {formatarQtd(item.quantidade, medidaDoItem(item, products))}
                            </span>
                          ) : (
                            <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">
                              {item.quantidade}
                            </span>
                          )}
                          <span className={`text-sm font-semibold text-orange-500 shrink-0`}>
                            R$ {((item.preco_unitario || item.preco || 0) * (item.quantidade || 1)).toFixed(2).replace('.', ',')}
                          </span>
                        </div>
                        <p className={`text-sm font-medium ${t.text} mt-0.5`}>{item.nome || item.name}</p>
                        {item.combo_type && <span className={`text-[10px] ${t.textMuted}`}>({item.combo_type === 'combo' ? 'Combo' : 'Simples'})</span>}
                        {/* Observação do item */}
                        {(item.observacao || item.observation) && (
                          <p className={`text-xs ${t.textMuted} mt-0.5`}>📝 {item.observacao || item.observation}</p>
                        )}
                        {/* Etapas do item (ex.: "Ponto: Mal passado; Acompanhamento: Batata") */}
                        {item.etapas && Array.isArray(item.etapas) && item.etapas.length > 0 && (
                          <div className={`mt-1.5 space-y-0.5`}>
                            {item.etapas.map((et, i) => (
                              <p key={i} className={`text-[11px] ${t.textMuted}`}>
                                <span className="font-medium">{et.etapa}:</span> {Array.isArray(et.itens) ? et.itens.join(', ') : (et.itens || '')}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Observação geral do pedido */}
                {selectedPedido.observacao && (
                  <div className={`p-3 border-t ${t.border}`}>
                    <p className={`text-xs font-medium ${t.textMuted}`}>Observação do pedido</p>
                    <p className={`text-sm ${t.text} mt-0.5`}>📝 {selectedPedido.observacao}</p>
                  </div>
                )}
                
                {/* Tipo Entrega e Endereço */}
                <div className={`p-3 border-t ${t.border}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {selectedPedido.tipo_entrega === 'delivery' ? (
                      <Truck className="w-4 h-4 text-orange-500" />
                    ) : (
                      <Store className="w-4 h-4 text-orange-500" />
                    )}
                    <span className={`text-sm font-medium ${t.text}`}>
                      {selectedPedido.tipo_entrega === 'delivery' ? 'Entrega' : 'Retirada no Local'}
                    </span>
                  </div>
                  {selectedPedido.tipo_entrega === 'delivery' && (
                    <p className={`text-xs ${t.textMuted} ml-6`}>
                      {selectedPedido.endereco_entrega || [selectedPedido.endereco_rua, selectedPedido.endereco_numero, selectedPedido.endereco_complemento, selectedPedido.endereco_bairro].filter(Boolean).join(', ') || '—'}
                    </p>
                  )}
                </div>
                
                {/* Pagamento e Total */}
                <div className={`p-3 border-t ${t.border} flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const fp = selectedPedido.forma_pagamento || '';
                      const isOnline = fp === 'pix_online' || fp === 'cartao_online';
                      const iconStyle = isOnline ? { color: 'hsl(var(--primary))' } : {};
                      const iconClass = isOnline ? 'w-4 h-4' : 'w-4 h-4 text-muted-foreground';
                      if (fp.includes('pix')) return <QrCode className={iconClass} style={iconStyle} />;
                      if (fp.includes('cartao') || fp.includes('credito') || fp.includes('debito')) return <CreditCard className={iconClass} style={iconStyle} />;
                      return <Banknote className={iconClass} style={iconStyle} />;
                    })()}
                    <span className={`text-sm ${t.text}`}>
                      {(() => {
                        const fp = selectedPedido.forma_pagamento || '';
                        const labelMap = { pix_online: 'PIX Online', cartao_online: 'Cartao Online', pix: 'PIX', dinheiro: 'Dinheiro', credito: 'Credito', debito: 'Debito', cartao: 'Cartao' };
                        return labelMap[fp] || rotuloPagamento(fp); // código "dinheiro_<loja>" nunca aparece
                      })()}
                    </span>
                  </div>
                  <span className="font-bold text-lg" style={{ color: 'hsl(var(--primary))' }}>R$ {(selectedPedido.total || 0).toFixed(2).replace('.', ',')}</span>
                </div>
              </div>
            ) : (() => {
              /* Lista de pedidos com busca, paginação e agrupamento Em andamento / Histórico */
              const ACTIVE = new Set(['aguardando_aceite', 'aceito', 'producao', 'pronto', 'na_bag', 'em_rota', 'transito']);
              const searchTermNorm = pedidosSearch.trim().toLowerCase().replace(/^#/, '');
              const filtered = searchTermNorm
                ? clientPedidos.filter(p => String(p.codigo || '').toLowerCase().replace(/^#/, '').includes(searchTermNorm))
                : clientPedidos;
              const inProgressAll = filtered.filter(p => ACTIVE.has(p.status));
              const doneAll = filtered.filter(p => !ACTIVE.has(p.status));
              // Paginação SÓ no histórico (em andamento mostra tudo, geralmente 0-3 pedidos)
              const PAGE_SIZE = 10;
              const totalPages = Math.max(1, Math.ceil(doneAll.length / PAGE_SIZE));
              const currentPage = Math.min(pedidosPage, totalPages);
              const doneSlice = doneAll.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
              // Stats do cliente: total de pedidos + total gasto (só não-cancelados)
              const totalGasto = clientPedidos
                .filter(p => p.status !== 'cancelado')
                .reduce((s, p) => s + (Number(p.total) || 0), 0);
              const allProductsLookup = (allProductsForPhotos && allProductsForPhotos.length ? allProductsForPhotos : products) || [];
              const findItemPhoto = (item) => {
                if (item?.photo_url) return getImageUrl(item.photo_url);
                const name = (item?.nome || item?.name || '').toLowerCase().trim();
                if (!name) return null;
                const p = allProductsLookup.find(pr => (pr.name || pr.nome || '').toLowerCase().trim() === name);
                return p ? getImageUrl(p.photo_url || p.imagem || p.image) : null;
              };

              const relTime = (iso) => {
                if (!iso) return '';
                const d = new Date(iso);
                const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
                if (diffMin < 1) return 'agora';
                if (diffMin < 60) return `há ${diffMin} min`;
                const diffH = Math.floor(diffMin / 60);
                if (diffH < 24) return `há ${diffH}h`;
                const diffD = Math.floor(diffH / 24);
                if (diffD === 1) return 'ontem';
                if (diffD < 7) return `há ${diffD} dias`;
                return d.toLocaleDateString('pt-BR');
              };

              const statusMeta = (status) => {
                switch (status) {
                  case 'aguardando_aceite': return { label: 'Aguardando', icon: Timer, fg: 'text-yellow-500', bg: 'bg-yellow-500', soft: 'bg-yellow-500/10' };
                  case 'aceito':            return { label: 'Aceito', icon: Check, fg: 'text-blue-500', bg: 'bg-blue-500', soft: 'bg-blue-500/10' };
                  case 'producao':          return { label: 'Em produção', icon: ChefHat, fg: 'text-orange-500', bg: 'bg-orange-500', soft: 'bg-orange-500/10' };
                  case 'pronto':
                  case 'na_bag':            return { label: 'Pronto', icon: Package, fg: 'text-sky-500', bg: 'bg-sky-500', soft: 'bg-sky-500/10' };
                  case 'em_rota':
                  case 'transito':          return { label: 'Em rota', icon: Bike, fg: 'text-purple-500', bg: 'bg-purple-500', soft: 'bg-purple-500/10' };
                  case 'concluido':
                  case 'entregue':          return { label: 'Entregue', icon: CheckCircle, fg: 'text-emerald-500', bg: 'bg-emerald-500', soft: 'bg-emerald-500/10' };
                  case 'retirado':          return { label: 'Retirado', icon: CheckCircle, fg: 'text-emerald-500', bg: 'bg-emerald-500', soft: 'bg-emerald-500/10' };
                  case 'cancelado':         return { label: 'Cancelado', icon: XCircle, fg: 'text-red-500', bg: 'bg-red-500', soft: 'bg-red-500/10' };
                  default:                  return { label: status, icon: Clock, fg: 'text-zinc-400', bg: 'bg-zinc-500', soft: 'bg-zinc-500/10' };
                }
              };

              const renderCard = (pedido) => {
                const m = statusMeta(pedido.status);
                const Icon = m.icon;
                const isActive = ACTIVE.has(pedido.status);
                const items = pedido.items || [];
                // Mosaico de até 3 fotos dos itens principais
                const photos = items
                  .map(findItemPhoto)
                  .filter(Boolean)
                  .slice(0, 3);
                const itemsPreview = items.slice(0, 2).map(it => `${qtdDoItem(it.quantidade || 1, medidaDoItem(it, products))} ${it.nome || it.name}`).join(', ');
                const moreItems = items.length - 2;
                return (
                  <div
                    key={pedido.id}
                    onClick={() => { setCurrentOrder(pedido); setTrackingOpen(true); }}
                    className={`relative ${t.bgCard} rounded-2xl overflow-hidden cursor-pointer transition-all hover:scale-[1.01] active:scale-[0.99] ${
                      isActive
                        ? 'border-2 border-orange-500/70 shadow-lg shadow-orange-500/20'
                        : `border ${t.border} hover:border-orange-500/40`
                    }`}
                  >
                    {/* Faixa lateral colorida indicando status */}
                    <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${m.bg}`} />
                    {/* Glow sutil no fundo pra pedidos ativos */}
                    {isActive && <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-transparent pointer-events-none" />}

                    <div className="relative p-3.5 pl-5">
                      {/* Linha de cabeçalho — codigo, status, total */}
                      <div className="flex items-start gap-2.5 mb-2">
                        <div className={`w-11 h-11 rounded-xl ${m.soft} ${m.fg} flex items-center justify-center flex-shrink-0 ${isActive ? 'ring-2 ring-current/30' : ''}`}>
                          <Icon className="w-5 h-5" strokeWidth={2.2} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 min-w-0 flex-wrap">
                            <span className={`font-bold text-base ${t.text}`}>{pedido.codigo}</span>
                            <div className={`text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${m.soft} ${m.fg}`}>
                              {m.label}
                            </div>
                          </div>
                          <div className={`flex items-center gap-2 text-[11px] ${t.textMuted} mt-0.5`}>
                            <span>{relTime(pedido.created_at)}</span>
                            <span className="opacity-50">·</span>
                            <span className="inline-flex items-center gap-1">
                              {pedido.tipo_entrega === 'delivery' ? <Truck className="w-3 h-3" /> : <Store className="w-3 h-3" />}
                              {pedido.tipo_entrega === 'delivery' ? 'Entrega' : 'Retirada'}
                            </span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`font-bold text-base ${isActive ? 'text-orange-500' : t.text}`}>
                            R$ {(pedido.total || 0).toFixed(2).replace('.', ',')}
                          </p>
                          <p className={`text-[10px] ${t.textMuted} mt-0.5`}>{items.length} {items.length === 1 ? 'item' : 'itens'}</p>
                        </div>
                      </div>

                      {/* Mosaico de fotos + descrição */}
                      <div className="flex items-center gap-2.5">
                        {photos.length > 0 && (
                          <div className="flex -space-x-2 flex-shrink-0">
                            {photos.map((src, i) => (
                              <img
                                key={i}
                                src={src}
                                alt=""
                                className={`w-9 h-9 rounded-lg object-cover ring-2 ${darkMode ? 'ring-zinc-900' : 'ring-white'} ${darkMode ? 'bg-zinc-800' : 'bg-gray-100'}`}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            ))}
                            {items.length > 3 && (
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-[10px] font-bold ring-2 ${darkMode ? 'ring-zinc-900 bg-zinc-800 text-zinc-400' : 'ring-white bg-gray-100 text-gray-500'}`}>
                                +{items.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                        <p className={`text-xs ${t.textMuted} truncate flex-1`}>
                          {itemsPreview || 'Sem itens'}
                          {moreItems > 0 && <span className="text-orange-500 font-semibold"> +{moreItems}</span>}
                        </p>
                        <ChevronRight className={`w-4 h-4 ${t.textMuted} flex-shrink-0`} />
                      </div>

                      {/* Live tag pra pedidos ativos */}
                      {isActive && (
                        <div className="mt-2.5 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-500">
                            Acompanhar ao vivo
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              };

              return (
                <div className="space-y-4">
                  {/* Stats + busca */}
                  <div className={`${t.bgCard} rounded-2xl border ${t.border} p-3 space-y-3`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className={`text-[10px] uppercase tracking-wider font-bold ${t.textMuted}`}>Total de pedidos</p>
                        <p className={`text-xl font-bold ${t.text}`}>{clientPedidos.length}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-[10px] uppercase tracking-wider font-bold ${t.textMuted}`}>Total gasto</p>
                        <p className="text-xl font-bold text-orange-500">R$ {totalGasto.toFixed(2).replace('.', ',')}</p>
                      </div>
                    </div>
                    <div className="relative">
                      <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${t.textMuted}`} />
                      <input
                        type="text"
                        value={pedidosSearch}
                        onChange={(e) => { setPedidosSearch(e.target.value); setPedidosPage(1); }}
                        placeholder="Buscar pelo código do pedido..."
                        className={`w-full pl-9 pr-9 py-2.5 text-sm rounded-xl ${darkMode ? 'bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'} border focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500/40`}
                      />
                      {pedidosSearch && (
                        <button
                          onClick={() => { setPedidosSearch(""); setPedidosPage(1); }}
                          className={`absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-md ${t.textMuted} hover:bg-muted`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Em andamento */}
                  {inProgressAll.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1.5 mb-2 px-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <h3 className={`text-xs font-bold uppercase tracking-wider ${t.text}`}>Em andamento</h3>
                        <span className={`text-[10px] ${t.textMuted}`}>· {inProgressAll.length}</span>
                      </div>
                      <div className="space-y-3">{inProgressAll.map(renderCard)}</div>
                    </div>
                  )}

                  {/* Histórico */}
                  {doneAll.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2 px-1">
                        <div className="flex items-center gap-1.5">
                          <History className={`w-3.5 h-3.5 ${t.textMuted}`} />
                          <h3 className={`text-xs font-bold uppercase tracking-wider ${t.textMuted}`}>Histórico</h3>
                          <span className={`text-[10px] ${t.textMuted}`}>· {doneAll.length}</span>
                        </div>
                        {totalPages > 1 && (
                          <span className={`text-[10px] ${t.textMuted}`}>Pág {currentPage}/{totalPages}</span>
                        )}
                      </div>
                      <div className="space-y-2.5">{doneSlice.map(renderCard)}</div>
                      {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-3">
                          <button
                            onClick={() => setPedidosPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 ${currentPage === 1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-orange-500/10'} ${t.text}`}
                          >
                            <ChevronLeft className="w-3.5 h-3.5" /> Anterior
                          </button>
                          {Array.from({ length: totalPages }).slice(0, 7).map((_, i) => {
                            const pageNum = i + 1;
                            const isCurrent = pageNum === currentPage;
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setPedidosPage(pageNum)}
                                className={`w-7 h-7 rounded-lg text-xs font-bold ${isCurrent ? 'bg-orange-500 text-white' : `${t.text} hover:bg-orange-500/10`}`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => setPedidosPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 ${currentPage === totalPages ? 'opacity-40 cursor-not-allowed' : 'hover:bg-orange-500/10'} ${t.text}`}
                          >
                            Próxima <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Estado vazio quando busca não retorna nada */}
                  {searchTermNorm && inProgressAll.length === 0 && doneAll.length === 0 && (
                    <div className={`${t.bgCard} rounded-2xl border ${t.border} p-8 text-center`}>
                      <Search className={`w-8 h-8 mx-auto mb-2 ${t.textMuted}`} />
                      <p className={`text-sm ${t.text}`}>Nenhum pedido encontrado</p>
                      <p className={`text-xs ${t.textMuted} mt-1`}>Tente outro código</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </main>
      {sacolaLateral && (
        <SacolaLateral
          t={t}
          quantidade={cartItemsCount}
          total={cartTotal}
          rotuloTotal={modoMesa ? 'Total desta rodada' : 'Total'}
          mostrarEntrega={!modoMesa}
          desconto={modoMesa ? null : (() => {
            // Cupom ou prêmio do cartão escolhido (o prêmio só vale se couber na sacola)
            const escolhido = cuponsDoCheckout.find(c => c.code === selectedCouponCode);
            return escolhido && premioCabeNaSacola(escolhido, cart) ? descontoDoCupom(escolhido, couponableSubtotal) : null;
          })()}
          cupom={!modoMesa && couponsUnlocked && visibleCoupons.length > 0 ? (
            <CupomDaSacola t={t} codigo={selectedCouponCode} quantidade={visibleCoupons.length} onClick={() => setCouponsModalOpen(true)} />
          ) : null}
          rotuloBotao={rotuloFazerPedido}
          onFinalizar={handleMakeOrder}
          distanciaTopo={topoDaSacola}
          topo={(
            <>
              {/* Mesa QR: a rodada não leva cupom (ele entra no Fechar mesa, pelo caixa) */}
              {!modoMesa && visibleCoupons.length > 0 && (couponsUnlocked ? (
                <CouponMiniBar
                  visibleCoupons={visibleCoupons}
                  selectedCouponCode={selectedCouponCode}
                  couponableSubtotal={couponableSubtotal}
                  darkMode={darkMode}
                  onClick={() => setCouponsModalOpen(true)}
                />
              ) : (
                <OfertasParaDesbloquear quantidade={visibleCoupons.length} explodindo={couponsExploding} onClick={desbloquearCupons} />
              ))}
              {cashbackConfig.ativo && loggedClient && cashbackSaldo > 0 && (
                <CarteiraCashback saldo={cashbackSaldo} darkMode={darkMode} />
              )}
              <FaixaPremio premios={cartaoFid.premios} onUsar={usarPremio} onVer={() => setMeuCartaoAberto(true)} darkMode={darkMode} />
            </>
          )}
          sugestoes={modoMesa ? null : ultimoNaSacola ? (sugestoesAdicionado.length > 0 && (
            <ListaSugestoes lugar="adicionado" titulo={`💡 Combina com o seu ${ultimoNaSacola.nome}`} itens={sugestoesAdicionado}
              onEscolher={escolherSugestao} precoDe={precoSugestao} fotoDe={getImageUrl} darkMode={darkMode} compacto />
          )) : (sugestoesSacola.length > 0 && (
            <ListaSugestoes lugar="sacola" titulo="💡 Combina com o seu pedido" itens={sugestoesSacola}
              onEscolher={escolherSugestao} precoDe={precoSugestao} fotoDe={getImageUrl} darkMode={darkMode} compacto />
          ))}
        >
          {itensDaSacola}
        </SacolaLateral>
      )}
      </div>
      </div>
      {/* /max-w-[480px] column */}

      {/* Rodape da Sacola — clean, fundo neutro (estilo ifood). No computador a sacola é a lateral. */}
      {cartItemsCount > 0 && !sacolaLateral && (
        <div className={`fixed bottom-0 left-0 right-0 z-30 ${t.bgCard} border-t ${t.border}`}>
          <div className="max-w-[480px] lg:max-w-[1120px] mx-auto px-3 py-1.5 flex items-center gap-2">
            <div className="flex-shrink min-w-0">
              <p className={`text-[11px] ${t.textMuted} leading-tight`}>
                {modoMesa ? 'Total desta rodada' : 'Total sem a entrega'}
              </p>
              <p className={`text-base font-black ${t.text} leading-tight`}>
                R$ {cartTotal.toFixed(2).replace('.', ',')}
                <span className={`text-xs font-normal ${t.textMuted} ml-1`}>/ {cartItemsCount} {cartItemsCount === 1 ? 'item' : 'itens'}</span>
              </p>
            </div>
            <button
              onClick={() => setCartOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-bold rounded-xl h-10 text-base transition-colors flex-1 ml-auto"
            >
              Ver sacola
            </button>
          </div>
        </div>
      )}

      {/* Carrinho Popup Centralizado */}
      <Dialog open={cartOpen && !sacolaLateral} onOpenChange={setCartOpen}>
        <DialogContent hideClose className={`p-0 gap-0 overflow-hidden ${t.bgCart} ${t.text} border-0 sm:max-w-md w-[92vw] max-h-[85vh] rounded-2xl flex flex-col`}>
          <DialogHeader className="sr-only">
            <DialogTitle>Meu Carrinho</DialogTitle>
          </DialogHeader>
          {/* Header do Carrinho */}
          <div className={`p-3 border-b ${t.border} flex items-center justify-between bg-orange-500 flex-shrink-0`}>
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-white" />
              <div>
                <h3 className="font-bold text-white text-sm">Meu Carrinho</h3>
                <p className="text-[10px] text-white/80">{cartItemsCount} {cartItemsCount === 1 ? 'item' : 'itens'}</p>
              </div>
            </div>
            <button
              onClick={() => setCartOpen(false)}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>

          {/* Mini barra de cupom — slim, sempre visivel acima dos itens (fora da Mesa QR) */}
          {cart.length > 0 && !modoMesa && (
            <CouponMiniBar
              visibleCoupons={visibleCoupons}
              selectedCouponCode={selectedCouponCode}
              couponableSubtotal={couponableSubtotal}
              darkMode={darkMode}
              onClick={() => setCouponsModalOpen(true)}
            />
          )}

          {/* Carteira do cliente (v1.5.316+): saldo de cashback; o switch de USAR fica no pagamento. */}
          {cart.length > 0 && cashbackConfig.ativo && loggedClient && cashbackSaldo > 0 && (
            <CarteiraCashback saldo={cashbackSaldo} darkMode={darkMode} />
          )}

          {/* Itens do Carrinho — mesmo cartão da sacola lateral (features/cardapio-sacola) */}
          <div className="flex-1 overflow-y-auto p-2 min-h-0">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <ShoppingCart className={`w-10 h-10 ${t.textMuted2} mb-3`} />
                <p className={`text-sm ${t.textMuted}`}>Carrinho vazio</p>
              </div>
            ) : (
              <div className="space-y-2">{itensDaSacola}</div>
            )}
          </div>

          {/* Footer do Carrinho */}
          {cart.length > 0 && (
            <div className={`p-3 border-t ${t.border} ${t.bgCartItem} flex-shrink-0 space-y-2`}>
              {/* Sugestões Inteligentes na sacola: logo depois de adicionar, o que combina com
                  o produto que entrou; depois, com o carrinho inteiro. */}
              {ultimoAdicionado ? (sugestoesAdicionado.length > 0 && (
                <div className="-mx-3 px-3 pb-2">
                  <ListaSugestoes lugar="adicionado" titulo={`💡 Combina com o seu ${ultimoAdicionado.nome}`} itens={sugestoesAdicionado}
                    onEscolher={escolherSugestao} precoDe={precoSugestao} fotoDe={getImageUrl} darkMode={darkMode} />
                </div>
              )) : (sugestoesSacola.length > 0 && (
                <div className="-mx-3 px-3 pb-2">
                  <ListaSugestoes lugar="sacola" titulo="💡 Combina com o seu pedido" itens={sugestoesSacola}
                    onEscolher={escolherSugestao} precoDe={precoSugestao} fotoDe={getImageUrl} darkMode={darkMode} />
                </div>
              ))}

              <div className="flex items-center justify-between">
                <span className={`text-xs ${t.textMuted}`}>Total</span>
                <span className="text-lg font-bold text-orange-500">R$ {cartTotal.toFixed(2).replace('.', ',')}</span>
              </div>
              <Button
                onClick={() => {
                  // Calcula o quanto foi adicionado desde o ultimo "Continuar comprando".
                  // Se >0 e a barra de progresso ja foi desbloqueada, dispara animacao de
                  // absorcao: pill com o valor voa pra barra e a barra cresce.
                  const delta = couponableSubtotal - lastCheckpointSubtotalRef.current;
                  lastCheckpointSubtotalRef.current = couponableSubtotal;
                  setCartOpen(false);
                  if (couponsUnlocked && visibleCoupons.length > 0 && delta > 0) {
                    setCoupAbsorbAnim({ value: delta, key: Date.now() });
                    setTimeout(() => setCoupAbsorbAnim(null), 1100);
                  }
                }}
                variant="outline"
                className={`w-full font-semibold h-10 text-sm rounded-xl ${darkMode ? 'border-zinc-600 bg-transparent text-zinc-200 hover:bg-zinc-700' : 'border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100'}`}
              >
                Continuar comprando
              </Button>
              <Button
                onClick={handleMakeOrder}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-11 text-sm rounded-xl"
              >
                {rotuloFazerPedido}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Barrinha de cupom — teaser verde 'X ofertas disponiveis' ate o usuario clicar e
          desbloquear (com animacao de explosao); depois vira barra de progresso. */}
      {activeTab === 'cardapio' && !sacolaLateral && visibleCoupons.length > 0 && !couponsUnlocked && (
        <button
          type="button"
          onClick={desbloquearCupons}
          className={`fixed ${cartItemsCount > 0 ? 'bottom-[52px]' : 'bottom-0'} left-0 right-0 z-30 bg-gradient-to-r from-green-500 to-green-600 border-t border-green-700 text-white active:opacity-90 overflow-hidden ${couponsExploding ? 'animate-coupon-burst' : 'animate-coupon-attract coupon-shine-overlay'}`}
          aria-label="Desbloquear ofertas"
        >
          <div className="px-3 py-2.5 flex items-center justify-center gap-2 relative">
            <Gift className={`w-4 h-4 shrink-0 ${couponsExploding ? 'animate-coupon-spin' : 'animate-coupon-wiggle'}`} />
            <span className="text-[13px] font-bold">
              {visibleCoupons.length} {visibleCoupons.length === 1 ? 'oferta disponível' : 'ofertas disponíveis'} para desbloquear
            </span>
            <span className="text-[11px]">→</span>
            {couponsExploding && (
              <span className="absolute inset-0 pointer-events-none flex items-center justify-center">
                {[...Array(12)].map((_, i) => (
                  <span
                    key={i}
                    className="absolute w-1.5 h-1.5 rounded-full bg-white animate-coupon-particle"
                    style={{ '--a': `${i * 30}deg`, animationDelay: `${i * 10}ms` }}
                  />
                ))}
              </span>
            )}
          </div>
        </button>
      )}

      {/* Pill voador "+R$ X,XX" que voa do centro ate a barra de cupom e e absorvido */}
      {coupAbsorbAnim && (
        <div
          key={coupAbsorbAnim.key}
          className="fixed left-1/2 z-40 pointer-events-none animate-coupon-absorb"
          style={{ bottom: cartItemsCount > 0 ? '100px' : '50px' }}
        >
          <div className="bg-orange-500 text-white text-sm font-extrabold px-4 py-1.5 rounded-full shadow-2xl shadow-orange-500/50 ring-2 ring-orange-300/60 whitespace-nowrap">
            +R$ {Number(coupAbsorbAnim.value).toFixed(2).replace('.', ',')}
          </div>
        </div>
      )}

      {activeTab === 'cardapio' && !sacolaLateral && visibleCoupons.length > 0 && couponsUnlocked && (() => {
        // Se o cliente ja selecionou um cupom no modal, mostra ele na barra.
        // Caso contrario, escolhe o mais proximo de ser atingido.
        const selected = selectedCouponCode ? visibleCoupons.find(c => c.code === selectedCouponCode) : null;
        const best = selected || [...visibleCoupons].sort((a, b) => {
          const minA = Number(a.min_order_value || 0);
          const minB = Number(b.min_order_value || 0);
          const faltaA = couponableSubtotal >= minA ? 0 : minA - couponableSubtotal;
          const faltaB = couponableSubtotal >= minB ? 0 : minB - couponableSubtotal;
          if (faltaA === 0 && faltaB > 0) return -1;
          if (faltaB === 0 && faltaA > 0) return 1;
          return faltaA - faltaB;
        })[0];
        const min = Number(best.min_order_value || 0);
        const reached = min === 0 || couponableSubtotal >= min;
        const progress = min === 0 ? 100 : Math.min(100, (couponableSubtotal / min) * 100);
        const falta = Math.max(0, min - couponableSubtotal);
        const isFreeDelivery = best.discount_type === 'free_delivery';
        const descontoLabel = isFreeDelivery
          ? 'frete grátis'
          : best.discount_type === 'percentage'
            ? `${Number(best.discount_value).toFixed(0)}%`
            : `R$ ${Number(best.discount_value).toFixed(2).replace('.', ',')}`;
        const BarIcon = isFreeDelivery ? MotoHelmet : Ticket;
        return (
          <button
            type="button"
            onClick={() => setCouponsModalOpen(true)}
            className={`fixed ${cartItemsCount > 0 ? 'bottom-[52px]' : 'bottom-0'} left-0 right-0 z-30 text-left active:opacity-90 animate-coupon-reveal ${reached ? 'bg-green-600' : t.bgCard} border-t ${reached ? 'border-green-700' : t.border}`}
            aria-label="Ver cupons disponíveis"
          >
            {min > 0 && !reached && (
              <div className={`absolute top-0 left-0 right-0 h-1 ${darkMode ? 'bg-zinc-800' : 'bg-gray-100'} overflow-hidden`}>
                <div
                  className={`h-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-500 ${coupAbsorbAnim ? 'animate-coupon-bar-pulse-impact' : ''}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
            <div className="px-3 py-2 flex items-center justify-center gap-2">
              <BarIcon className={`w-4 h-4 shrink-0 ${reached ? 'text-white' : isFreeDelivery ? 'text-sky-500' : 'text-orange-500'}`} />
              {reached ? (
                <span className="text-white text-[12px] font-bold">
                  {isFreeDelivery ? 'Frete grátis liberado' : `Desconto de ${descontoLabel} liberado`}
                </span>
              ) : (
                <span className={`text-[12px] font-semibold ${t.text}`}>
                  Faltam <strong className="text-orange-500">R$ {falta.toFixed(2).replace('.', ',')}</strong> pro {isFreeDelivery ? 'frete grátis' : <>desconto de <strong className="text-orange-500">{descontoLabel}</strong></>}
                </span>
              )}
              {visibleCoupons.length > 1 && (
                <span className={`text-[9px] shrink-0 px-1 py-px rounded ${reached ? 'bg-white/20 text-white' : (darkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-gray-200 text-gray-600')}`}>
                  +{visibleCoupons.length - 1}
                </span>
              )}
            </div>
          </button>
        );
      })()}


      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} onLoginSuccess={handleLoginSuccess} logoUrl={companySettings?.logo_url} />

      {showInfoLojaModal && (
        <InfoLojaModal
          onClose={() => setShowInfoLojaModal(false)}
          companySettings={companySettings}
          businessHours={businessHours}
          isOpen={isOpen}
          closingTime={closingTime}
          manuallyClosed={manuallyClosed}
          closeReason={closeReason}
          nextOpenTime={nextOpenTime}
          darkMode={darkMode}
          t={t}
          cardapioStats={cardapioStats}
          onOpenReviews={() => setShowReviewsModal(true)}
          schedulingActive={schedulingConfig.ativo}
        />
      )}

      {showReviewsModal && (
        <PublicReviewsModal
          onClose={() => setShowReviewsModal(false)}
          darkMode={darkMode}
          t={t}
          fantasyName={companySettings?.fantasy_name || companySettings?.company_name}
        />
      )}
      
      {/* Popup de Produto */}
      <ProductPopup
        // key forca remontagem quando troca entre add/edit ou entre produtos diferentes,
        // garantindo que o estado interno (comboStep, selections) seja inicializado de
        // forma consistente em vez de carregar lixo do uso anterior.
        key={`pp-${editingCartItem?.cartItemId || 'new'}-${selectedProduct?.id || 'none'}`}
        product={selectedProduct}
        midias={midiasProdutos[selectedProduct?.id]}
        open={productPopupOpen}
        onClose={() => { setProductPopupOpen(false); setEditingCartItem(null); }}
        onAddToCart={addToCart}
        darkMode={darkMode}
        allProducts={allProductsForPhotos.length > 0 ? allProductsForPhotos : products}
        activePromotions={activePromotions}
        editingItem={editingCartItem}
        cashbackConfig={cashbackConfig}
      />

      {/* Pizza Popup — render do hook usePizzaPopup() */}
      {pizzaPopupNode}

      {/* Modal de Checkout */}
      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        cart={cart}
        cartTotal={cartTotal}
        couponableSubtotal={couponableSubtotal}
        client={loggedClient}
        darkMode={darkMode}
        onOrderComplete={handleOrderComplete}
        stats={cardapioStats}
        visibleCoupons={cuponsDoCheckout}
        selectedCouponCode={selectedCouponCode}
        onSelectCoupon={setSelectedCouponCode}
        allProducts={allProductsForPhotos.length > 0 ? allProductsForPhotos : products}
        storeOpen={getOpenStatus().isOpen}
        schedulingConfig={schedulingConfig}
        cashbackConfig={cashbackConfig}
        cashbackSaldo={cashbackSaldo}
        onCashbackConsumed={() => refetchCashbackSaldo(loggedClient?.id)}
        activePromotions={activePromotions}
        onOfertasDesatualizadas={ofertasDia.recarregar}
        sugestoes={sugestoesCheckout.length > 0 ? (
          <ListaSugestoes lugar="checkout" titulo="Antes de finalizar, que tal?" itens={sugestoesCheckout}
            onEscolher={escolherSugestao} precoDe={precoSugestao} fotoDe={getImageUrl} darkMode={darkMode} compacto />
        ) : null}
      />
      <CombinaCom adicionado={!sacolaLateral && !cartOpen && !checkoutOpen && !productPopupOpen ? ultimoAdicionado : null}
        itens={sugestoesAdicionado} onEscolher={escolherSugestao} onFechar={fecharCombina}
        precoDe={precoSugestao} fotoDe={getImageUrl} darkMode={darkMode} />

      {/* Campanha Inteligente: "Completar cadastro" da faixa abre a edição de conta. */}
      {editarCadastroAberto && loggedClient && (
        <EditAccountModal
          isOpen={editarCadastroAberto}
          onClose={() => setEditarCadastroAberto(false)}
          client={loggedClient}
          onUpdate={handleClientUpdate}
        />
      )}
      
      {/* Tela de Acompanhamento do Pedido */}
      {trackingOpen && currentOrder && (
        <AcompanhamentoPedido
          tempoMedioMin={cardapioStats.avg_order_time_min}
          pedido={currentOrder}
          onClose={() => setTrackingOpen(false)}
          darkMode={darkMode}
          companyPhone={companySettings.phone}
          companySettings={companySettings}
          allProducts={allProductsForPhotos.length > 0 ? allProductsForPhotos : products}
          produtos={products}
        />
      )}

      {/* Avalie seu último pedido: o cliente que não respondeu o link do WhatsApp
          vê a avaliação ao voltar ao cardápio (só com a campanha ligada). Fora da Mesa QR. */}
      <PopupAvaliacao cliente={loggedClient} ativo={!modoMesa && !trackingOpen} />

      {/* Cartão Fidelidade: "Meu cartão" com os prêmios para usar */}
      <MeuCartaoModal aberto={meuCartaoAberto} onFechar={() => setMeuCartaoAberto(false)} estado={cartaoFid.estado}
        onUsar={usarPremio} darkMode={darkMode} />

      {/* Modal com lista completa de cupons visiveis */}
      <Dialog open={couponsModalOpen} onOpenChange={setCouponsModalOpen}>
        <DialogContent hideClose className={`p-0 gap-0 overflow-hidden ${t.bgCard} ${t.text} border-0 sm:max-w-md w-[92vw] max-h-[85vh] rounded-2xl flex flex-col`}>
          <DialogHeader className="sr-only">
            <DialogTitle>Cupons disponíveis</DialogTitle>
          </DialogHeader>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-white" />
              <div>
                <p className="text-white font-bold text-sm">Cupons disponíveis</p>
                <p className="text-white/80 text-[11px]">{visibleCoupons.length} {visibleCoupons.length === 1 ? 'cupom ativo' : 'cupons ativos'}</p>
              </div>
            </div>
            <button onClick={() => setCouponsModalOpen(false)} className="text-white/90 hover:text-white p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="overflow-y-auto p-3 space-y-2 flex-1">
            {visibleCoupons.length === 0 && (
              <p className={`text-center text-xs ${t.textMuted} py-8`}>Nenhum cupom disponível no momento</p>
            )}
            {visibleCoupons.map((c) => {
              const cMin = Number(c.min_order_value || 0);
              const cReached = cMin === 0 || couponableSubtotal >= cMin;
              const cProgress = cMin === 0 ? 100 : Math.min(100, (couponableSubtotal / cMin) * 100);
              const cFalta = Math.max(0, cMin - couponableSubtotal);
              const cIsFreeDelivery = c.discount_type === 'free_delivery';
              const cVal = cIsFreeDelivery
                ? 'FRETE'
                : c.discount_type === 'percentage'
                  ? `${Number(c.discount_value).toFixed(0)}%`
                  : `R$ ${Number(c.discount_value).toFixed(2).replace('.', ',')}`;
              const validUntil = c.valid_until ? (() => {
                try {
                  const d = new Date(c.valid_until);
                  if (isNaN(d.getTime())) return null;
                  return d.toLocaleDateString('pt-BR');
                } catch (e) { return null; }
              })() : null;
              // Estimativa client-side do desconto se este cupom fosse usado (ignora frete)
              const estDiscount = (() => {
                if (!cReached) return null;
                if (cIsFreeDelivery) return null; // sem numero no cardapio — valor depende do bairro
                if (c.discount_type === 'percentage') {
                  const raw = couponableSubtotal * (Number(c.discount_value || 0) / 100);
                  const cap = Number(c.max_discount_value || 0);
                  return cap > 0 ? Math.min(raw, cap) : raw;
                }
                return Number(c.discount_value || 0);
              })();
              const isSelected = selectedCouponCode === c.code;
              const cardGradient = cIsFreeDelivery
                ? 'from-sky-500 to-blue-600'
                : 'from-orange-500 to-orange-600';
              return (
                <div
                  key={c.id}
                  className={`rounded-xl border-2 overflow-hidden ${darkMode ? 'bg-zinc-900' : 'bg-white'} ${isSelected ? 'border-orange-500 ring-2 ring-orange-500/20' : t.border}`}
                >
                  <div className="flex items-stretch">
                    <div className={`bg-gradient-to-br ${cardGradient} px-3 py-3 flex flex-col items-center justify-center min-w-[80px]`}>
                      {cIsFreeDelivery ? (
                        <>
                          <MotoHelmet className="w-7 h-7 text-white" />
                          <span className="text-white text-[10px] font-black mt-0.5">GRÁTIS</span>
                        </>
                      ) : (
                        <>
                          <span className="text-white text-xl font-black leading-none">{cVal}</span>
                          <span className="text-white/90 text-[9px] font-bold mt-0.5">OFF</span>
                        </>
                      )}
                    </div>
                    <div className="flex-1 p-3 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={`font-mono font-black text-[11px] px-1.5 py-0.5 rounded ${darkMode ? 'bg-orange-500/15 text-orange-400' : 'bg-orange-500/10 text-orange-600'}`}>{c.code}</span>
                        {cReached && (
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${darkMode ? 'bg-green-500/15 text-green-400' : 'bg-green-500/10 text-green-700'} flex items-center gap-0.5`}>
                            <Check className="w-2.5 h-2.5" /> Liberado
                          </span>
                        )}
                      </div>
                      {c.description && (
                        <p className={`text-[11px] ${t.textMuted} mb-1.5 line-clamp-2`}>{c.description}</p>
                      )}
                      <div className={`flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] ${t.textMuted}`}>
                        <span>{cMin > 0 ? `Min R$ ${cMin.toFixed(2).replace('.', ',')}` : 'Sem mínimo'}</span>
                        {c.max_discount_value && <span>Max R$ {Number(c.max_discount_value).toFixed(2).replace('.', ',')}</span>}
                        {validUntil && <span>Até {validUntil}</span>}
                        {c.first_order_only ? <span>1º pedido</span> : null}
                      </div>
                      {cMin > 0 && !cReached && (
                        <div className="mt-2">
                          <div className={`h-1 rounded-full ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'} overflow-hidden`}>
                            <div
                              className="h-full transition-all duration-500 bg-gradient-to-r from-orange-400 to-orange-600"
                              style={{ width: `${cProgress}%` }}
                            />
                          </div>
                          <p className={`text-[10px] mt-1 ${t.textMuted}`}>
                            Faltam R$ {cFalta.toFixed(2).replace('.', ',')} para ativar
                          </p>
                        </div>
                      )}
                      {cReached && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setSelectedCouponCode(null);
                              } else {
                                setSelectedCouponCode(c.code);
                                toast.success(`Cupom ${c.code} selecionado`);
                              }
                              setCouponsModalOpen(false);
                            }}
                            className={`w-full h-9 rounded-lg text-xs font-bold transition-colors ${
                              isSelected
                                ? (darkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-zinc-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700')
                                : 'bg-orange-500 hover:bg-orange-600 text-white'
                            }`}
                          >
                            {isSelected
                              ? '✓ Selecionado (tocar p/ remover)'
                              : cIsFreeDelivery
                                ? 'Usar este cupom · frete grátis'
                                : estDiscount !== null
                                  ? `Usar este cupom · economia de R$ ${estDiscount.toFixed(2).replace('.', ',')}`
                                  : 'Usar este cupom'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className={`p-3 border-t ${t.border} flex-shrink-0`}>
            <Button
              onClick={() => setCouponsModalOpen(false)}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-10 text-sm rounded-xl"
            >
              Entendi
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
